import React, { useCallback, useEffect, useMemo, useState } from "react";
import { usePmsTheme } from "../Components/PmsThemeContext";
import {
  createRmsConfigResource,
  deleteRmsConfigResource,
  fetchRmsConfigMeta,
  fetchRmsConfigResource,
  fetchRmsOccupancyPace,
  fetchRmsOrphanGapNearPolicy,
  fetchRmsOrphanGapPolicy,
  fetchRmsOrphanGapStrongPolicy,
  fetchRmsMarketBenchmarkPolicy,
  fetchRmsPriceResponse,
  fetchRmsQualitySettings,
  fetchRmsRateShopConfig,
  saveRmsCategoryPriceLadder,
  saveRmsOccupancyPace,
  saveRmsOrphanGapNearPolicy,
  saveRmsOrphanGapPolicy,
  saveRmsOrphanGapStrongPolicy,
  saveRmsMarketBenchmarkPolicy,
  saveRmsPriceResponse,
  saveRmsQualitySettings,
  updateRmsConfigResource,
} from "./rmsApi";
import { statusTone } from "./rmsShared";
import { createRmsTranslator, getRmsLocale } from "./rmsPmsI18n";
import RmsMarketMappingStudio from "./RmsMarketMappingStudio";
import {
  buildOrphanGapPolicyPayload,
  normalizeOrphanGapPolicyDraft,
  updateOrphanGapPolicyDraftItem,
  validateOrphanGapPolicyDraft,
} from "./rmsOrphanGapPolicy";
import {
  buildOrphanGapNearPolicyPayload,
  normalizeOrphanGapNearPolicyDraft,
  updateOrphanGapNearPolicyDraftItem,
  validateOrphanGapNearPolicyDraft,
} from "./rmsOrphanGapNearPolicy";
import {
  buildOrphanGapStrongPolicyPayload,
  normalizeOrphanGapStrongPolicyDraft,
  updateOrphanGapStrongPolicyDraftItem,
  validateOrphanGapStrongPolicyDraft,
} from "./rmsOrphanGapStrongPolicy";
import {
  buildMarketBenchmarkPolicyPayload,
  normalizeMarketBenchmarkPolicyDraft,
  updateMarketBenchmarkPolicyDraft,
  validateMarketBenchmarkPolicyDraft,
} from "./rmsMarketBenchmarkPolicy";

const QUALITY_FIELD_CONFIG = [
  {
    key: "own_rating",
    label: "Current public review score",
    help:
      "Average score of your hotel in public review channels. It is a quality signal for the RMS, not a price by itself. Example: 4.3.",
    placeholder: "4.3",
    type: "number",
    step: "0.1",
    min: "0",
    max: "5",
    suffix: "/5",
  },
  {
    key: "own_reviews_count",
    label: "Number of reviews",
    help:
      "How many reviews support that score. More reviews means the quality signal is more reliable. Example: 1250.",
    placeholder: "1250",
    type: "number",
    step: "1",
    min: "0",
  },
  {
    key: "rating_sensitivity_pct_per_point",
    label: "Price impact for 1 review point",
    help:
      "How much quality can move price for each full review point. Example: 3 means a one-point rating advantage can justify up to 3% before caps.",
    placeholder: "3",
    type: "number",
    step: "0.1",
    min: "0",
    suffix: "%",
  },
  {
    key: "max_quality_adjustment_pct",
    label: "Maximum quality impact",
    help:
      "Safety cap for the quality effect on price. Example: 8 means quality can move the recommendation by at most 8% up or down.",
    placeholder: "8",
    type: "number",
    step: "0.1",
    min: "0",
    suffix: "%",
  },
];

const SCOPE_TYPE_META = {
  hotel: {
    label: "Whole hotel",
    help: "Use one single pricing posture for the whole hotel.",
  },
  room_category: {
    label: "One room category",
    help: "Use a different posture for one specific room category.",
  },
};

const STRATEGY_MODE_META = {
  occupancy: {
    label: "Fill rooms",
    help: "The optimizer will be more willing to move price to improve occupancy.",
  },
  adr: {
    label: "Protect price",
    help: "The optimizer will defend average daily rate even if it sells a bit slower.",
  },
  balanced: {
    label: "Balanced",
    help: "The optimizer will try to balance occupancy and average price.",
  },
};

const LONG_RANGE_MODE_OPTIONS = [
  {
    value: "season_blocks",
    label: "Use season blocks",
    help: "Build long-range guidance around your seasonality periods when they exist.",
  },
  {
    value: "calendar_range",
    label: "Use calendar blocks",
    help: "Build long-range guidance in regular calendar ranges.",
  },
];

const LONG_RANGE_BLOCK_TYPE_OPTIONS = [
  {
    value: "seasonality_period",
    label: "Seasonality periods",
    help: "Use the named season periods you have configured.",
  },
  {
    value: "calendar_range",
    label: "Calendar ranges",
    help: "Use regular blocks of days instead of season labels.",
  },
];

const LONG_RANGE_MODE_META = Object.fromEntries(
  LONG_RANGE_MODE_OPTIONS.map((option) => [option.value, option])
);

const LONG_RANGE_BLOCK_TYPE_META = Object.fromEntries(
  LONG_RANGE_BLOCK_TYPE_OPTIONS.map((option) => [option.value, option])
);

const SEASONALITY_TEMPLATE_DEMAND_OPTIONS = [
  {
    value: "quiet",
    label: "Quiet season",
    help: "Lower natural demand. The hotel usually needs softer pricing support here.",
  },
  {
    value: "steady",
    label: "Steady season",
    help: "Normal demand pattern without major pressure up or down.",
  },
  {
    value: "busy",
    label: "Busy season",
    help: "Stronger demand. The hotel can usually hold firmer prices.",
  },
  {
    value: "peak",
    label: "Peak season",
    help: "Exceptionally strong demand with the firmest price posture.",
  },
];

const SEASONALITY_TEMPLATE_DEMAND_META = Object.fromEntries(
  SEASONALITY_TEMPLATE_DEMAND_OPTIONS.map((option) => [option.value, option])
);

const SEASONALITY_PRIORITY_OPTIONS = [
  {
    value: "25",
    label: "Background season",
    help: "Use this for broad date ranges that should lose if a more specific season overlaps them.",
  },
  {
    value: "100",
    label: "Normal priority",
    help: "Best default for most seasons. It is the safe middle ground.",
  },
  {
    value: "300",
    label: "Special override",
    help: "Use this when the period must win over normal seasons, such as Easter or a local fair.",
  },
];

const SEASONALITY_PRIORITY_META = Object.fromEntries(
  SEASONALITY_PRIORITY_OPTIONS.map((option) => [option.value, option])
);

const SEASONALITY_DOW_OPTIONS = [
  { value: "", label: "Every day", help: "Use the same refinement for the whole week." },
  { value: "1", label: "Monday", help: "Only for Mondays inside the selected season." },
  { value: "2", label: "Tuesday", help: "Only for Tuesdays inside the selected season." },
  { value: "3", label: "Wednesday", help: "Only for Wednesdays inside the selected season." },
  { value: "4", label: "Thursday", help: "Only for Thursdays inside the selected season." },
  { value: "5", label: "Friday", help: "Only for Fridays inside the selected season." },
  { value: "6", label: "Saturday", help: "Only for Saturdays inside the selected season." },
  { value: "0", label: "Sunday", help: "Only for Sundays inside the selected season." },
];

const SEASONALITY_DOW_META = Object.fromEntries(
  SEASONALITY_DOW_OPTIONS.map((option) => [option.value, option])
);

const MARKET_DISRUPTION_TYPE_OPTIONS = [
  {
    value: "local_event",
    label: "Local event",
    help: "Concerts, fairs, sports matches or festivals that change hotel demand.",
  },
  {
    value: "transport_issue",
    label: "Transport issue",
    help: "Airport, rail, ferry or road disruptions that affect arrivals.",
  },
  {
    value: "weather",
    label: "Weather issue",
    help: "Storms, extreme heat, heavy rain or other weather shocks.",
  },
  {
    value: "works",
    label: "Works nearby",
    help: "Renovation, road works or noise around the destination.",
  },
  {
    value: "restriction",
    label: "Restriction",
    help: "Travel rules, access limits or policy changes that move demand.",
  },
  {
    value: "market_shock",
    label: "Market shock",
    help: "Unexpected changes in the local market that are not a normal season pattern.",
  },
  {
    value: "other",
    label: "Other",
    help: "Any unusual situation that should temporarily make pricing firmer or softer.",
  },
];

const MARKET_DISRUPTION_TYPE_META = Object.fromEntries(
  MARKET_DISRUPTION_TYPE_OPTIONS.map((option) => [option.value, option])
);

const MARKET_DISRUPTION_DIRECTION_OPTIONS = [
  {
    value: "up",
    label: "Push prices up",
    help: "Use this when the disruption makes demand stronger or lets you hold firmer prices.",
  },
  {
    value: "down",
    label: "Push prices down",
    help: "Use this when the disruption weakens demand and prices should become softer.",
  },
];

const MARKET_DISRUPTION_DIRECTION_META = Object.fromEntries(
  MARKET_DISRUPTION_DIRECTION_OPTIONS.map((option) => [option.value, option])
);

const MARKET_DISRUPTION_LEVEL_OPTIONS = [
  {
    value: "low",
    label: "Low effect",
    help: "Small pricing impact. The market changes, but only a little.",
  },
  {
    value: "medium",
    label: "Medium effect",
    help: "Noticeable impact. The RMS should clearly react, but not overreact.",
  },
  {
    value: "high",
    label: "High effect",
    help: "Strong impact. The market is moving enough to justify a firm adjustment.",
  },
  {
    value: "severe",
    label: "Severe effect",
    help: "Exceptionally strong impact. Use only for major anomalies.",
  },
];

const MARKET_DISRUPTION_LEVEL_META = Object.fromEntries(
  MARKET_DISRUPTION_LEVEL_OPTIONS.map((option) => [option.value, option])
);

const CATEGORY_PRICE_LADDER_MODES = new Set([
  "root",
  "enforce",
  "allow_inversion",
  "excluded",
]);

const CATEGORY_PRICE_LADDER_MODE_OPTIONS = [
  {
    value: "root",
    label: "Start an independent ladder",
    help: "Use this for the lowest category in one comparable group.",
  },
  {
    value: "enforce",
    label: "Keep above another category",
    help: "The RMS must keep this category above the selected lower category by at least the configured gap.",
  },
  {
    value: "allow_inversion",
    label: "Allow an explicit inversion",
    help: "Document a deliberate commercial exception that may place this category below the selected category.",
  },
  {
    value: "excluded",
    label: "Exclude from the ladder",
    help: "Use only when this category is not commercially comparable, and explain why.",
  },
];

const OCCUPANCY_PACE_WINDOWS = [
  { code: "0_2", label: "0–2 days before arrival" },
  { code: "3_7", label: "3–7 days before arrival" },
  { code: "8_21", label: "8–21 days before arrival" },
  { code: "22_60", label: "22–60 days before arrival" },
  { code: "61_plus", label: "61+ days before arrival" },
];

const OCCUPANCY_PACE_WINDOW_CODES = new Set(
  OCCUPANCY_PACE_WINDOWS.map((window) => window.code)
);

const PRICE_RESPONSE_WINDOWS = OCCUPANCY_PACE_WINDOWS;
const PRICE_RESPONSE_WINDOW_CODES = new Set(
  PRICE_RESPONSE_WINDOWS.map((window) => window.code)
);
const PRICE_RESPONSE_LOW_CONFIDENCE_ACTIONS = new Set([
  "hold",
  "limit",
  "require_approval",
]);

const PRICE_RESPONSE_DEFAULTS_BY_MODE = {
  occupancy: { behind_gain: 0.4375, ahead_gain: 0.2975 },
  balanced: { behind_gain: 0.35, ahead_gain: 0.35 },
  adr: { behind_gain: 0.2975, ahead_gain: 0.4375 },
};

const PRICE_RESPONSE_DEFAULTS = {
  max_decrease_pct_per_run: 0.18,
  max_increase_pct_per_run: 0.18,
  max_decrease_pct_24h: 0.25,
  max_increase_pct_24h: 0.25,
  hysteresis_enter_pct: 0.02,
  hysteresis_exit_pct: 0.005,
  min_change_interval_minutes: 360,
  low_confidence_threshold: 0.6,
  low_confidence_action: "require_approval",
  low_confidence_max_delta_pct: null,
};

const RESOURCE_CONFIG = [
  {
    key: "rate-product-policies",
    title: "Master room rates",
    description: "The independent room rate optimized by the RMS for each room category.",
    readOnly: true,
    columns: [],
  },
  {
    key: "standard-room-types",
    title: "Standard Room Types",
    description: "Comparable types used only for market matching.",
    readOnly: true,
    columns: ["code", "name_en", "default_max_occupancy", "description_en", "sort_order", "is_active"],
  },
  {
    key: "room-category-mappings",
    title: "Room Category Mappings",
    description: "Internal category to standard type mapping for market comparison.",
    columns: ["room_category.name", "standard_room_type.code", "max_occupancy", "notes", "is_active"],
    template: {
      room_category_id: "",
      standard_room_type_id: "",
      max_occupancy: 2,
      notes: "",
      is_active: true,
    },
  },
  {
    key: "strategy-settings",
    title: "Strategy Settings",
    description: "Hotel or room-category strategy rules.",
    createLabel: "Add strategy",
    editLabel: "Edit strategy",
    columnLabels: {
      scope_type: "Applies to",
      "scope_room_category.name": "Room category",
      strategy_mode: "Main goal",
      detailed_horizon_days: "Daily horizon",
      long_range_mode: "Long-range view",
      is_active: "Status",
    },
    columns: [
      "scope_type",
      "scope_room_category.name",
      "strategy_mode",
      "detailed_horizon_days",
      "long_range_mode",
      "is_active",
    ],
    template: {
      scope_type: "hotel",
      scope_id: "",
      strategy_mode: "balanced",
      target_occupancy_pct: "",
      min_adr_target: "",
      desired_price_index: "",
      floor_price: "",
      ceiling_price: "",
      allow_min_stay_relaxation: false,
      allow_min_stay_hardening: false,
      min_stay_min_allowed: 1,
      min_stay_max_allowed: 7,
      detailed_horizon_days: 90,
      long_range_mode: "season_blocks",
      long_range_block_type: "seasonality_period",
      long_range_min_block_days: 7,
      is_active: true,
    },
  },
  {
    key: "competitors",
    title: "Competitors",
    description: "Active competitor hotels and weights.",
    columns: ["hotel_code", "competitor_name", "competitor_weight", "distance_km", "rating", "is_active"],
    template: {
      hotel_code: "",
      competitor_name: "",
      rating: "",
      review_count: "",
      distance_km: "",
      competitor_weight: 1,
      is_active: true,
    },
  },
  {
    key: "competitor-room-type-mappings",
    title: "Competitor Room Type Mappings",
    description: "Normalization of competitor room names into standard types and comparable guests.",
    columns: ["hotel_code", "website_code", "room_type_raw", "room_type_norm", "max_occupancy", "standard_room_type.code"],
    template: {
      hotel_code: "",
      website_code: "",
      room_type_raw: "",
      room_type_norm: "",
      standard_room_type_id: "",
      max_occupancy: 2,
      notes: "",
    },
  },
  {
    key: "seasonality-templates",
    title: "Seasonality Templates",
    description: "Reusable demand templates.",
    createLabel: "Add season template",
    editLabel: "Edit template",
    columns: ["name", "default_demand_level", "default_price_index", "is_active"],
    template: {
      name: "",
      description: "",
      default_demand_level: "",
      default_price_index: "",
      is_active: true,
    },
  },
  {
    key: "seasonality-periods",
    title: "Seasonality Periods",
    description: "Date ranges assigned to one template.",
    createLabel: "Add season period",
    editLabel: "Edit period",
    columnLabels: {
      "seasonality_template.name": "Season",
      start_date: "Starts",
      end_date: "Ends",
      priority: "Priority",
      is_active: "Status",
    },
    columns: ["seasonality_template.name", "start_date", "end_date", "priority", "is_active"],
    template: {
      seasonality_template_id: "",
      start_date: "",
      end_date: "",
      priority: 100,
      is_active: true,
    },
  },
  {
    key: "seasonality-profiles",
    title: "Seasonality Profiles",
    description: "Expected demand or pricing behavior by room category and day of week.",
    createLabel: "Add season profile",
    editLabel: "Edit profile",
    columnLabels: {
      "seasonality_template.name": "Season",
      "room_category.name": "Room category",
      dow: "Day of week",
      expected_demand_level: "Demand tone",
      expected_price_index: "Price index",
      expected_occupancy_pct: "Expected occupancy",
    },
    columns: [
      "seasonality_template.name",
      "room_category.name",
      "dow",
      "expected_demand_level",
      "expected_price_index",
      "expected_occupancy_pct",
    ],
    template: {
      seasonality_template_id: "",
      room_category_id: "",
      dow: "",
      expected_demand_level: "",
      expected_price_index: "",
      expected_occupancy_pct: "",
      is_active: true,
    },
  },
  {
    key: "market-disruptions",
    title: "Market Disruptions",
    description: "Short-term shocks that should make the RMS firmer or softer than normal.",
    createLabel: "Add disruption",
    editLabel: "Edit disruption",
    columnLabels: {
      name: "Disruption",
      disruption_type: "Type",
      impact_direction: "Effect on price",
      impact_level: "Strength",
      start_date: "Starts",
      end_date: "Ends",
      confidence: "Confidence",
      is_active: "Status",
    },
    columns: [
      "name",
      "disruption_type",
      "impact_direction",
      "impact_level",
      "start_date",
      "end_date",
      "confidence",
      "is_active",
    ],
    template: {
      city_code: "",
      country_code: "",
      name: "",
      disruption_type: "local_event",
      impact_direction: "up",
      impact_level: "medium",
      start_date: "",
      end_date: "",
      confidence: "100",
      source: "",
      notes: "",
      is_active: true,
    },
  },
];

const SETUP_STEP_CONFIG = [
  {
    key: "products",
    number: 1,
    title: "Pricing products",
    shortTitle: "Products",
    description:
      "Choose the independent master rate and verify every active product derives from it.",
    completeWhen:
      "Complete when every category has a valid master, executable derived-rate rules and an explicit category price ladder.",
  },
  {
    key: "strategy",
    number: 2,
    title: "Base pricing strategy",
    shortTitle: "Strategy",
    description:
      "Define the hotel-wide objective, guardrails and planning horizon the optimizer should use first.",
    completeWhen:
      "Complete when one active whole-hotel strategy exists. Add category rules only for real exceptions.",
  },
  {
    key: "competitors",
    number: 3,
    title: "Market scope and competitors",
    shortTitle: "Competitors",
    description:
      "Confirm which extracted hotels should influence the benchmark and how important each one is.",
    completeWhen:
      "Complete when the extraction scope is available and at least one relevant competitor is active.",
  },
  {
    key: "mapping",
    number: 4,
    title: "Comparable room mapping",
    shortTitle: "Room mapping",
    description:
      "Match your room categories and competitor room names to comparable standard room types.",
    completeWhen:
      "Complete when every active hotel room category has a valid standard-room mapping.",
  },
  {
    key: "templates",
    number: 5,
    title: "Season templates",
    shortTitle: "Seasons",
    description:
      "Create a small reusable set of demand seasons such as quiet, steady, busy and peak.",
    completeWhen: "Complete when at least one active season template is ready for calendar dates.",
  },
  {
    key: "periods",
    number: 6,
    title: "Season calendar",
    shortTitle: "Calendar",
    description:
      "Place the season templates on real date ranges and resolve overlaps with clear priorities.",
    completeWhen: "Complete when the operating calendar has at least one active season period.",
  },
  {
    key: "quality",
    number: 7,
    title: "Quality calibration",
    shortTitle: "Quality",
    description:
      "Set the public review signal and the maximum influence quality may have on recommendations.",
    completeWhen: "Complete when the four quality calibration values have been reviewed and saved.",
  },
  {
    key: "profiles",
    number: 8,
    title: "Room and weekday refinements",
    shortTitle: "Refinements",
    description:
      "Optionally refine a season for room categories or weekdays that behave differently.",
    completeWhen: "Optional. Add profiles only when a real category or weekday exception exists.",
    optional: true,
  },
  {
    key: "pace",
    number: 9,
    title: "Expected occupancy & pace",
    shortTitle: "Pace",
    description:
      "Define how much of each profile's expected final occupancy should already be booked at each lead-time window.",
    completeWhen:
      "Complete when every active seasonality profile has five valid lead-time windows and a tolerance band.",
  },
  {
    key: "price-response",
    number: 10,
    title: "Price response safeguards",
    shortTitle: "Safeguards",
    description:
      "Set asymmetric demand response, movement limits and guarded orphan-gap actions for every effective strategy.",
    completeWhen:
      "Complete when price response and T07–T09 orphan-gap safeguards are valid and saved.",
  },
  {
    key: "disruptions",
    number: 11,
    title: "Temporary market disruptions",
    shortTitle: "Disruptions",
    description:
      "Optionally layer exceptional events or shocks on top of the normal season calendar.",
    completeWhen: "Optional. Use only for temporary situations that normal seasonality cannot explain.",
    optional: true,
  },
];

const SETUP_RESOURCE_KEY_BY_STEP = {
  products: "rate-product-policies",
  strategy: "strategy-settings",
  templates: "seasonality-templates",
  periods: "seasonality-periods",
  profiles: "seasonality-profiles",
  disruptions: "market-disruptions",
};

function extractErrorMessage(error, fallbackMessage) {
  const responsePayload = error?.response?.data;
  if (typeof responsePayload?.error === "string" && responsePayload.error.trim()) {
    return responsePayload.error.trim();
  }
  if (Array.isArray(responsePayload?.errors) && responsePayload.errors.length) {
    return responsePayload.errors.map((item) => String(item)).join(" ");
  }
  return error?.message || fallbackMessage;
}

function getValueByPath(item, path) {
  return String(path || "")
    .split(".")
    .filter(Boolean)
    .reduce((current, key) => (current == null ? null : current[key]), item);
}

// eslint-disable-next-line no-unused-vars
function renderStatusPill(value) {
  const label =
    typeof value === "boolean" ? (value ? "Active" : "Inactive") : String(value || "—");
  return <span className={`rmo-pill rmo-pill--${statusTone(value)}`}>{label}</span>;
}

function renderCellContent(value) {
  if (value == null || value === "") return "-";
  if (typeof value === "boolean") return value ? "true" : "false";
  if (Array.isArray(value)) return value.length ? JSON.stringify(value) : "-";
  if (typeof value === "object") return JSON.stringify(value);
  return String(value);
}

function renderLocalizedStatusPill(value, t) {
  const label =
    typeof value === "boolean"
      ? value
        ? t("Active")
        : t("Inactive")
      : String(value || "-");
  return <span className={`rmo-pill rmo-pill--${statusTone(value)}`}>{label}</span>;
}

function getOptionMeta(map, value, fallbackLabel) {
  const hit = map[String(value || "")];
  return hit || { label: fallbackLabel || String(value || "-"), help: "" };
}

function getRateProductDerivationIssueLabel(code, t) {
  const labels = {
    missing_master_policy: "Master rate policy is missing",
    ambiguous_master_policy: "More than one active master policy was found",
    invalid_master: "The selected master rate is not valid",
    master_validity_gap: "The master rate does not cover the operating horizon",
    independent_rate: "This active rate is independent from the master",
    cycle: "The derived-rate chain contains a cycle",
    cross_category: "The derived-rate chain crosses room categories",
    missing_parent: "A parent rate is missing",
    missing_seed_rate: "A synchronized master-group rate is missing",
    derived_rate_cannot_be_seeded:
      "A synchronized master-group rate is derived and cannot be used as a price seed",
    missing_derived_rate: "A downstream derived rate is missing",
    inactive_dependency: "A required parent rate is inactive",
    inactive_seed_rate: "A synchronized master-group rate is inactive",
    inactive_derived_rate: "A downstream derived rate is inactive",
    inactive_group_member: "A synchronized rate in this product group is inactive",
    ambiguous_relationship: "The rate has more than one derivation relationship",
    formula_without_inputs: "The formula has no input rates",
    missing_formula: "The linked formula is missing",
    missing_product_guardrails: "This product needs its own floor and ceiling",
    invalid_product_guardrails: "This product has invalid floor or ceiling values",
    no_integer_price_within_guardrails:
      "This product has no whole price between its floor and ceiling",
    invalid_default_stay_restrictions:
      "This product has inconsistent default min/max stay restrictions for at least one weekday",
    invalid_simple_adjustment: "The simple derived-rate adjustment is invalid",
    invalid_formula: "The derived-rate formula is invalid",
    invalid_formula_definition: "The derived-rate formula is invalid",
    unsupported_custom_formula: "Custom derived formulas are not supported by RMS yet",
    product_validity_gap: "This derived product does not cover the operating horizon",
    group_product_validity_gap:
      "A synchronized rate in this product group does not cover the operating horizon",
    invalid_currency:
      "This product has an invalid currency code; use a three-letter ISO code",
    currency_mismatch: "This product uses a different currency from the master",
    group_currency_mismatch:
      "Synchronized rates in this product group use different currencies",
    group_guardrail_conflict:
      "Synchronized rates in this product group have incompatible price limits",
  };
  return t(labels[String(code || "")] || "The derived-rate relationship is invalid");
}

function normalizeCategoryPriceLadderDraft(ladder, roomCategories) {
  const sourceItems = Array.isArray(ladder?.items) ? ladder.items : [];
  const activePolicyCategoryIds = new Set(
    (Array.isArray(ladder?.active_policy_category_ids)
      ? ladder.active_policy_category_ids
      : []
    )
      .map(Number)
      .filter((roomCategoryId) => Number.isInteger(roomCategoryId) && roomCategoryId > 0)
  );
  const sourceByCategoryId = new Map(
    sourceItems
      .map((item) => [Number(item?.room_category_id), item])
      .filter(
        ([roomCategoryId]) =>
          Number.isInteger(roomCategoryId) && roomCategoryId > 0
      )
  );

  return {
    expected_configuration_hash:
      typeof ladder?.configuration_hash === "string" && ladder.configuration_hash.trim()
        ? ladder.configuration_hash.trim()
        : null,
    items: (roomCategories || [])
      .map((category) => Number(category?.id))
      .filter((roomCategoryId) => Number.isInteger(roomCategoryId) && roomCategoryId > 0)
      .filter((roomCategoryId) => activePolicyCategoryIds.has(roomCategoryId))
      .map((roomCategoryId) => {
        const source = sourceByCategoryId.get(roomCategoryId) || null;
        const mode = CATEGORY_PRICE_LADDER_MODES.has(String(source?.mode || ""))
          ? String(source.mode)
          : "";
        return {
          room_category_id: roomCategoryId,
          mode,
          lower_room_category_id:
            source?.lower_room_category_id == null
              ? ""
              : String(source.lower_room_category_id),
          min_gap_amount:
            source?.min_gap_amount == null
              ? mode
                ? "0"
                : ""
              : String(source.min_gap_amount),
          exception_reason:
            source?.exception_reason == null ? "" : String(source.exception_reason),
        };
      }),
  };
}

function buildCategoryPriceLadderPayload(draft, { isApplicable = true } = {}) {
  return {
    expected_configuration_hash: draft?.expected_configuration_hash || null,
    items: (isApplicable ? draft?.items || [] : []).map((item) => {
      const mode = String(item?.mode || "");
      const lowerRoomCategoryId = Number(item?.lower_room_category_id);
      const reason = String(item?.exception_reason || "").trim();
      return {
        room_category_id: Number(item.room_category_id),
        mode,
        lower_room_category_id:
          mode === "enforce" || mode === "allow_inversion"
            ? lowerRoomCategoryId
            : null,
        min_gap_amount:
          mode === "enforce" ? Number(item.min_gap_amount) : 0,
        exception_reason:
          mode === "allow_inversion" || mode === "excluded" ? reason : null,
      };
    }),
  };
}

function validateCategoryPriceLadderDraft({
  draft,
  roomCategories,
  activePolicyCount,
  t,
}) {
  if (Number(activePolicyCount) <= 1) return [];

  const errors = [];
  const categoryById = new Map(
    (roomCategories || [])
      .map((category) => [Number(category?.id), category])
      .filter(
        ([roomCategoryId]) =>
          Number.isInteger(roomCategoryId) && roomCategoryId > 0
      )
  );
  const itemByCategoryId = new Map();

  for (const item of draft?.items || []) {
    const roomCategoryId = Number(item?.room_category_id);
    if (!categoryById.has(roomCategoryId)) {
      errors.push(t("The ladder contains a room category that is no longer available."));
      continue;
    }
    if (itemByCategoryId.has(roomCategoryId)) {
      errors.push(
        t("Room category {category} appears more than once in the ladder.", {
          category: categoryById.get(roomCategoryId)?.name || roomCategoryId,
        })
      );
      continue;
    }
    itemByCategoryId.set(roomCategoryId, item);
  }

  for (const [roomCategoryId, category] of categoryById) {
    const item = itemByCategoryId.get(roomCategoryId);
    const categoryName = category?.name || String(roomCategoryId);
    if (!item) {
      errors.push(
        t("Choose a ladder rule for {category}.", { category: categoryName })
      );
      continue;
    }

    const mode = String(item.mode || "");
    if (!CATEGORY_PRICE_LADDER_MODES.has(mode)) {
      errors.push(
        t("Choose how {category} belongs in the category ladder.", {
          category: categoryName,
        })
      );
      continue;
    }

    const hasLowerCategory = String(item.lower_room_category_id ?? "").trim() !== "";
    const lowerRoomCategoryId = hasLowerCategory
      ? Number(item.lower_room_category_id)
      : null;
    const reason = String(item.exception_reason || "").trim();
    if (reason.length > 500) {
      errors.push(
        t("Keep the exception reason for {category} to 500 characters or fewer.", {
          category: categoryName,
        })
      );
    }

    if (mode === "root") {
      if (hasLowerCategory || Number(item.min_gap_amount || 0) !== 0 || reason) {
        errors.push(
          t("The root rule for {category} cannot have a lower category, gap or exception reason.", {
            category: categoryName,
          })
        );
      }
      continue;
    }

    if (mode === "excluded") {
      if (hasLowerCategory || Number(item.min_gap_amount || 0) !== 0) {
        errors.push(
          t("The excluded rule for {category} cannot have a lower category or gap.", {
            category: categoryName,
          })
        );
      }
      if (!reason) {
        errors.push(
          t("Explain why {category} is excluded from the category ladder.", {
            category: categoryName,
          })
        );
      }
      continue;
    }

    if (
      !Number.isInteger(lowerRoomCategoryId) ||
      lowerRoomCategoryId <= 0 ||
      !categoryById.has(lowerRoomCategoryId)
    ) {
      errors.push(
        t("Choose a valid lower category for {category}.", {
          category: categoryName,
        })
      );
      continue;
    }
    if (lowerRoomCategoryId === roomCategoryId) {
      errors.push(
        t("A room category cannot be above itself: {category}.", {
          category: categoryName,
        })
      );
    }

    if (mode === "enforce") {
      const rawGap = String(item.min_gap_amount ?? "").trim();
      const gap = Number(rawGap);
      if (
        !/^\d+(?:\.\d{1,2})?$/.test(rawGap) ||
        !Number.isFinite(gap) ||
        gap < 0 ||
        gap > 9999999999.99
      ) {
        errors.push(
          t("Enter a valid minimum gap with up to two decimals for {category}.", {
            category: categoryName,
          })
        );
      }
      if (reason) {
        errors.push(
          t("Remove the exception reason from the enforced rule for {category}.", {
            category: categoryName,
          })
        );
      }
    } else {
      if (Number(item.min_gap_amount || 0) !== 0) {
        errors.push(
          t("An inversion exception for {category} must use a zero gap.", {
            category: categoryName,
          })
        );
      }
      if (!reason) {
        errors.push(
          t("Explain the allowed price inversion for {category}.", {
            category: categoryName,
          })
        );
      }
    }
  }

  const parentByCategoryId = new Map();
  for (const [roomCategoryId, item] of itemByCategoryId) {
    if (item.mode !== "enforce" && item.mode !== "allow_inversion") continue;
    const lowerRoomCategoryId = Number(item.lower_room_category_id);
    if (!categoryById.has(lowerRoomCategoryId) || lowerRoomCategoryId === roomCategoryId) {
      continue;
    }
    const lowerItem = itemByCategoryId.get(lowerRoomCategoryId);
    if (lowerItem?.mode === "excluded") {
      errors.push(
        t("{category} cannot use an excluded category as its lower category.", {
          category: categoryById.get(roomCategoryId)?.name || roomCategoryId,
        })
      );
    }
    parentByCategoryId.set(roomCategoryId, lowerRoomCategoryId);
  }

  for (const startCategoryId of parentByCategoryId.keys()) {
    const path = new Set();
    let currentCategoryId = startCategoryId;
    while (parentByCategoryId.has(currentCategoryId)) {
      if (path.has(currentCategoryId)) {
        errors.push(t("The category ladder contains a cycle. Choose a clear lower category for each rule."));
        return [...new Set(errors)];
      }
      path.add(currentCategoryId);
      currentCategoryId = parentByCategoryId.get(currentCategoryId);
    }
  }

  return [...new Set(errors)];
}

function formatCategoryPriceLadderIssue(issue, roomCategories, t) {
  if (typeof issue === "string" && issue.trim()) return issue.trim();
  if (typeof issue?.message === "string" && issue.message.trim()) {
    return issue.message.trim();
  }

  const categoryById = new Map(
    (roomCategories || []).map((category) => [Number(category?.id), category])
  );
  const category = categoryById.get(Number(issue?.room_category_id));
  const code = String(issue?.code || "").trim();
  if (category?.name && code) return `${category.name}: ${code}`;
  return code || t("The category price ladder needs attention.");
}

function normalizeCanonicalPct(value) {
  if (value == null || value === "") return null;
  const numeric = Number(value);
  return Number.isFinite(numeric) ? numeric : null;
}

function formatCanonicalPctForDraft(value) {
  const numeric = normalizeCanonicalPct(value);
  if (numeric == null) return "";
  return String(Number((numeric * 100).toFixed(4)));
}

function normalizeOccupancyPaceDraft(runtime = {}) {
  const runtimeProfiles = Array.isArray(runtime?.profiles) ? runtime.profiles : [];
  const itemSources = [
    ...(Array.isArray(runtime?.items) ? runtime.items : []),
    ...runtimeProfiles.flatMap((profile) =>
      Array.isArray(profile?.items) ? profile.items : []
    ),
  ];
  const itemByProfileAndWindow = new Map();

  itemSources.forEach((item) => {
    const profileId = String(item?.seasonality_profile_id ?? "").trim();
    const bucket = String(item?.lead_time_bucket ?? "").trim();
    if (!profileId || !OCCUPANCY_PACE_WINDOW_CODES.has(bucket)) return;
    const key = `${profileId}|${bucket}`;
    if (!itemByProfileAndWindow.has(key)) {
      itemByProfileAndWindow.set(key, item);
    }
  });

  return {
    contract_version: Number(runtime?.contract_version || 0),
    is_applicable: runtime?.is_applicable === true,
    expected_configuration_hash:
      typeof runtime?.configuration_hash === "string" &&
      runtime.configuration_hash.trim()
        ? runtime.configuration_hash.trim()
        : null,
    profiles: runtimeProfiles
      .map((profile) => {
        const profileId = String(
          profile?.seasonality_profile_id ?? ""
        ).trim();
        const roomCategoryId = Number(profile?.room_category_id);
        if (!profileId || !Number.isInteger(roomCategoryId) || roomCategoryId <= 0) {
          return null;
        }
        return {
          seasonality_profile_id: profileId,
          room_category_id: roomCategoryId,
          expected_occupancy_pct: profile?.expected_occupancy_pct ?? null,
          items: OCCUPANCY_PACE_WINDOWS.map((window) => {
            const source = itemByProfileAndWindow.get(
              `${profileId}|${window.code}`
            );
            return {
              seasonality_profile_id: profileId,
              room_category_id: roomCategoryId,
              lead_time_bucket: window.code,
              expected_booked_share_pct: formatCanonicalPctForDraft(
                source?.expected_booked_share_pct
              ),
              tolerance_pct: formatCanonicalPctForDraft(source?.tolerance_pct),
              is_active: true,
            };
          }),
        };
      })
      .filter(Boolean),
  };
}

function buildOccupancyPacePayload(draft) {
  return {
    expected_configuration_hash: draft?.expected_configuration_hash || null,
    items: (draft?.profiles || []).flatMap((profile) =>
      (profile.items || []).map((item) => ({
        seasonality_profile_id: String(profile.seasonality_profile_id),
        room_category_id: Number(profile.room_category_id),
        lead_time_bucket: item.lead_time_bucket,
        expected_booked_share_pct: Number(
          (Number(item.expected_booked_share_pct) / 100).toFixed(6)
        ),
        tolerance_pct: Number(
          (Number(item.tolerance_pct) / 100).toFixed(6)
        ),
        is_active: true,
      }))
    ),
  };
}

function validateOccupancyPaceDraft(draft, t) {
  const profiles = Array.isArray(draft?.profiles) ? draft.profiles : [];
  if (!profiles.length) return [];
  const errors = [];
  const profileIds = new Set();

  profiles.forEach((profile) => {
    const profileId = String(profile?.seasonality_profile_id ?? "").trim();
    if (!profileId || profileIds.has(profileId)) {
      errors.push(t("The occupancy pace configuration contains a duplicate or invalid profile."));
      return;
    }
    profileIds.add(profileId);

    const expectedOccupancy = normalizeCanonicalPct(
      profile.expected_occupancy_pct
    );
    if (
      expectedOccupancy == null ||
      expectedOccupancy < 0 ||
      expectedOccupancy > 1
    ) {
      errors.push(
        t("Set a valid expected final occupancy on every active seasonality profile first.")
      );
    }

    const itemByBucket = new Map(
      (profile.items || []).map((item) => [item.lead_time_bucket, item])
    );
    if (
      itemByBucket.size !== OCCUPANCY_PACE_WINDOWS.length ||
      OCCUPANCY_PACE_WINDOWS.some((window) => !itemByBucket.has(window.code))
    ) {
      errors.push(t("Every active profile needs all five occupancy pace windows."));
      return;
    }

    const bookedShares = [];
    OCCUPANCY_PACE_WINDOWS.forEach((window) => {
      const item = itemByBucket.get(window.code);
      const shareText = String(item?.expected_booked_share_pct ?? "").trim();
      const toleranceText = String(item?.tolerance_pct ?? "").trim();
      const share = Number(shareText);
      const tolerance = Number(toleranceText);
      if (
        !shareText ||
        !Number.isFinite(share) ||
        share < 0 ||
        share > 100
      ) {
        errors.push(t("Booked share must stay between 0 and 100 for every window."));
      } else {
        bookedShares.push(share);
      }
      if (
        !toleranceText ||
        !Number.isFinite(tolerance) ||
        tolerance < 0 ||
        tolerance > 100
      ) {
        errors.push(t("Pace tolerance must stay between 0 and 100 for every window."));
      }
    });

    if (
      bookedShares.length === OCCUPANCY_PACE_WINDOWS.length &&
      bookedShares.some(
        (share, index) => index > 0 && bookedShares[index - 1] < share
      )
    ) {
      errors.push(
        t("Booked share cannot rise as the stay date gets farther away.")
      );
    }
  });

  return [...new Set(errors)];
}

function formatOccupancyPaceIssue(issue, t) {
  if (typeof issue === "string" && issue.trim()) return issue.trim();
  if (typeof issue?.message === "string" && issue.message.trim()) {
    return issue.message.trim();
  }
  const code = String(issue?.code || "").trim();
  return code || t("The occupancy pace curve needs attention.");
}

function formatPriceResponsePct(value) {
  const numeric = Number(value);
  return Number.isFinite(numeric) ? String(Number((numeric * 100).toFixed(4))) : "";
}

function priceResponseNumber(value, fallback = null) {
  const numeric = Number(value);
  return Number.isFinite(numeric) ? numeric : fallback;
}

function normalizePriceResponseDraft(runtime = {}, fallbackStrategies = []) {
  const runtimeItems = Array.isArray(runtime?.items)
    ? runtime.items
    : Array.isArray(runtime?.response_windows)
      ? runtime.response_windows
      : Array.isArray(runtime?.windows)
        ? runtime.windows
        : [];
  const sourceStrategies = Array.isArray(runtime?.effective_strategies)
    ? runtime.effective_strategies
    : Array.isArray(runtime?.strategies)
      ? runtime.strategies
      : fallbackStrategies;
  const strategyById = new Map();
  sourceStrategies.forEach((strategy) => {
    const id = Number(strategy?.strategy_setting_id ?? strategy?.strategy_id ?? strategy?.id);
    if (Number.isInteger(id) && id > 0) strategyById.set(id, strategy);
  });
  runtimeItems.forEach((item) => {
    const id = Number(item?.strategy_setting_id ?? item?.strategy_id);
    if (Number.isInteger(id) && id > 0 && !strategyById.has(id)) {
      strategyById.set(id, {
        id,
        strategy_mode: item?.strategy_mode || "balanced",
        scope_type: item?.scope_type,
        scope_id: item?.scope_id,
      });
    }
  });
  const itemByKey = new Map();
  runtimeItems.forEach((item) => {
    const strategyId = Number(item?.strategy_setting_id ?? item?.strategy_id);
    const bucket = String(item?.lead_time_bucket ?? item?.bucket ?? "").trim();
    if (Number.isInteger(strategyId) && strategyId > 0 && PRICE_RESPONSE_WINDOW_CODES.has(bucket)) {
      itemByKey.set(`${strategyId}|${bucket}`, item);
    }
  });

  return {
    contract_version: Number(runtime?.contract_version || runtime?.price_response_contract_version || 0),
    is_applicable: runtime?.is_applicable === true,
    is_ready: runtime?.is_ready === true,
    expected_configuration_hash:
      typeof runtime?.configuration_hash === "string" && runtime.configuration_hash.trim()
        ? runtime.configuration_hash.trim()
        : null,
    strategies: [...strategyById.entries()].map(([strategyId, strategy]) => {
      const mode = String(strategy?.strategy_mode || "balanced");
      const defaults = {
        ...PRICE_RESPONSE_DEFAULTS,
        ...(PRICE_RESPONSE_DEFAULTS_BY_MODE[mode] || PRICE_RESPONSE_DEFAULTS_BY_MODE.balanced),
      };
      return {
        strategy_setting_id: strategyId,
        strategy_mode: mode,
        is_active: strategy?.is_active === true,
        scope_type: String(strategy?.scope_type || "hotel"),
        scope_id: strategy?.scope_id ?? null,
        items: PRICE_RESPONSE_WINDOWS.map((window) => {
          const source = itemByKey.get(`${strategyId}|${window.code}`) || {};
          const lowConfidenceAction = PRICE_RESPONSE_LOW_CONFIDENCE_ACTIONS.has(
            String(source?.low_confidence_action || defaults.low_confidence_action)
          )
            ? String(source?.low_confidence_action || defaults.low_confidence_action)
            : defaults.low_confidence_action;
          return {
            strategy_setting_id: strategyId,
            lead_time_bucket: window.code,
            behind_gain: String(priceResponseNumber(source?.behind_gain, defaults.behind_gain)),
            ahead_gain: String(priceResponseNumber(source?.ahead_gain, defaults.ahead_gain)),
            max_decrease_pct_per_run: formatPriceResponsePct(
              priceResponseNumber(source?.max_decrease_pct_per_run, defaults.max_decrease_pct_per_run)
            ),
            max_increase_pct_per_run: formatPriceResponsePct(
              priceResponseNumber(source?.max_increase_pct_per_run, defaults.max_increase_pct_per_run)
            ),
            max_decrease_pct_24h: formatPriceResponsePct(
              priceResponseNumber(source?.max_decrease_pct_24h, defaults.max_decrease_pct_24h)
            ),
            max_increase_pct_24h: formatPriceResponsePct(
              priceResponseNumber(source?.max_increase_pct_24h, defaults.max_increase_pct_24h)
            ),
            hysteresis_enter_pct: formatPriceResponsePct(
              priceResponseNumber(source?.hysteresis_enter_pct, defaults.hysteresis_enter_pct)
            ),
            hysteresis_exit_pct: formatPriceResponsePct(
              priceResponseNumber(source?.hysteresis_exit_pct, defaults.hysteresis_exit_pct)
            ),
            min_change_interval_minutes: String(
              priceResponseNumber(source?.min_change_interval_minutes, defaults.min_change_interval_minutes)
            ),
            low_confidence_threshold: formatPriceResponsePct(
              priceResponseNumber(source?.low_confidence_threshold, defaults.low_confidence_threshold)
            ),
            low_confidence_action: lowConfidenceAction,
            low_confidence_max_delta_pct:
              lowConfidenceAction === "limit"
                ? formatPriceResponsePct(
                    priceResponseNumber(
                      source?.low_confidence_max_delta_pct,
                      Math.min(
                        defaults.max_decrease_pct_per_run,
                        defaults.max_increase_pct_per_run
                      )
                    )
                  )
                : "",
          };
        }),
      };
    }),
  };
}

function buildPriceResponsePayload(draft) {
  return {
    expected_configuration_hash: draft?.expected_configuration_hash || null,
    items: (draft?.strategies || []).flatMap((strategy) =>
      (strategy.items || []).map((item) => ({
        strategy_setting_id: Number(strategy.strategy_setting_id),
        lead_time_bucket: item.lead_time_bucket,
        behind_gain: Number(item.behind_gain),
        ahead_gain: Number(item.ahead_gain),
        max_decrease_pct_per_run: Number((Number(item.max_decrease_pct_per_run) / 100).toFixed(6)),
        max_increase_pct_per_run: Number((Number(item.max_increase_pct_per_run) / 100).toFixed(6)),
        max_decrease_pct_24h: Number((Number(item.max_decrease_pct_24h) / 100).toFixed(6)),
        max_increase_pct_24h: Number((Number(item.max_increase_pct_24h) / 100).toFixed(6)),
        hysteresis_enter_pct: Number((Number(item.hysteresis_enter_pct) / 100).toFixed(6)),
        hysteresis_exit_pct: Number((Number(item.hysteresis_exit_pct) / 100).toFixed(6)),
        min_change_interval_minutes: Number(item.min_change_interval_minutes),
        low_confidence_threshold: Number((Number(item.low_confidence_threshold) / 100).toFixed(6)),
        low_confidence_action: item.low_confidence_action,
        low_confidence_max_delta_pct:
          item.low_confidence_action === "limit"
            ? Number((Number(item.low_confidence_max_delta_pct) / 100).toFixed(6))
            : null,
      }))
    ),
  };
}

function validatePriceResponseDraft(draft, t) {
  const errors = [];
  const strategies = Array.isArray(draft?.strategies) ? draft.strategies : [];
  if (!strategies.length) return errors;
  strategies.forEach((strategy) => {
    const items = Array.isArray(strategy?.items) ? strategy.items : [];
    const buckets = new Set(items.map((item) => item?.lead_time_bucket));
    if (buckets.size !== PRICE_RESPONSE_WINDOWS.length) {
      errors.push(t("Every effective strategy needs all five price-response windows."));
      return;
    }
    items.forEach((item) => {
      const gains = [Number(item.behind_gain), Number(item.ahead_gain)];
      const [behindGain, aheadGain] = gains;
      const runDown = Number(item.max_decrease_pct_per_run);
      const runUp = Number(item.max_increase_pct_per_run);
      const dayDown = Number(item.max_decrease_pct_24h);
      const dayUp = Number(item.max_increase_pct_24h);
      const enter = Number(item.hysteresis_enter_pct);
      const exit = Number(item.hysteresis_exit_pct);
      const interval = Number(item.min_change_interval_minutes);
      const threshold = Number(item.low_confidence_threshold);
      if (gains.some((value) => !Number.isFinite(value) || value <= 0 || value > 2)) {
        errors.push(t("Response gains must be greater than 0 and no more than 2."));
      } else if (
        (strategy.strategy_mode === "occupancy" && behindGain <= aheadGain) ||
        (strategy.strategy_mode === "adr" && aheadGain <= behindGain) ||
        (strategy.strategy_mode === "balanced" && behindGain !== aheadGain)
      ) {
        errors.push(
          t(
            "Response gains must match the strategy mode: occupancy favors behind-pace response, ADR favors ahead-pace response, and balanced uses equal gains."
          )
        );
      }
      if ([runDown, runUp, dayDown, dayUp].some((value) => !Number.isFinite(value) || value < 0 || value > 100)) {
        errors.push(t("Movement limits must stay between 0 and 100%."));
      }
      if (Number.isFinite(runDown) && Number.isFinite(dayDown) && runDown > dayDown) {
        errors.push(t("The per-run decrease limit cannot exceed the 24-hour decrease limit."));
      }
      if (Number.isFinite(runUp) && Number.isFinite(dayUp) && runUp > dayUp) {
        errors.push(t("The per-run increase limit cannot exceed the 24-hour increase limit."));
      }
      if (!Number.isFinite(exit) || !Number.isFinite(enter) || exit < 0 || enter <= exit || enter > 100) {
        errors.push(t("Hysteresis requires an entry threshold above the exit threshold."));
      }
      if (!Number.isInteger(interval) || interval < 0 || interval > 10080) {
        errors.push(t("The minimum change interval must be between 0 and 10,080 minutes."));
      }
      if (!Number.isFinite(threshold) || threshold < 0 || threshold > 100) {
        errors.push(t("The low-confidence threshold must stay between 0 and 100%."));
      }
      if (!PRICE_RESPONSE_LOW_CONFIDENCE_ACTIONS.has(item.low_confidence_action)) {
        errors.push(t("Choose a valid low-confidence action."));
      }
      if (item.low_confidence_action === "limit") {
        const lowLimit = Number(item.low_confidence_max_delta_pct);
        if (!Number.isFinite(lowLimit) || lowLimit < 0 || lowLimit > Math.min(runDown, runUp)) {
          errors.push(t("The low-confidence movement limit must not exceed either per-run limit."));
        }
      }
    });
  });
  return [...new Set(errors)];
}

function formatPriceResponseIssue(issue, t) {
  if (typeof issue === "string" && issue.trim()) return issue.trim();
  if (typeof issue?.message === "string" && issue.message.trim()) return issue.message.trim();
  return String(issue?.code || "").trim() || t("The price-response safeguards need attention.");
}

function normalizeStrategyDraft(source = {}) {
  const isRoomCategoryScope = source.scope_type === "room_category";
  const storedTargetOccupancy = Number(source.target_occupancy_pct);
  const targetOccupancyDisplay =
    source.target_occupancy_pct == null || source.target_occupancy_pct === ""
      ? ""
      : Number.isFinite(storedTargetOccupancy)
        ? String(
            Number(
              (storedTargetOccupancy <= 1
                ? storedTargetOccupancy * 100
                : storedTargetOccupancy
              ).toFixed(4)
            )
          )
        : String(source.target_occupancy_pct);
  return {
    scope_type: source.scope_type || "hotel",
    scope_id:
      source.scope_id == null || source.scope_id === "" ? "" : String(source.scope_id),
    strategy_mode: source.strategy_mode || "balanced",
    target_occupancy_pct: targetOccupancyDisplay,
    min_adr_target:
      !isRoomCategoryScope || source.min_adr_target == null ? "" : String(source.min_adr_target),
    desired_price_index:
      source.desired_price_index == null ? "" : String(source.desired_price_index),
    floor_price: !isRoomCategoryScope || source.floor_price == null ? "" : String(source.floor_price),
    ceiling_price: source.ceiling_price == null ? "" : String(source.ceiling_price),
    allow_min_stay_relaxation: Boolean(source.allow_min_stay_relaxation),
    allow_min_stay_hardening: Boolean(source.allow_min_stay_hardening),
    min_stay_min_allowed:
      source.min_stay_min_allowed == null ? "1" : String(source.min_stay_min_allowed),
    min_stay_max_allowed:
      source.min_stay_max_allowed == null ? "7" : String(source.min_stay_max_allowed),
    detailed_horizon_days:
      source.detailed_horizon_days == null ? "90" : String(source.detailed_horizon_days),
    long_range_mode: source.long_range_mode || "season_blocks",
    long_range_block_type: source.long_range_block_type || "seasonality_period",
    long_range_min_block_days:
      source.long_range_min_block_days == null ? "7" : String(source.long_range_min_block_days),
    is_active: source.is_active !== false,
  };
}

function buildStrategyPayload(draft) {
  const isRoomCategoryScope = draft.scope_type === "room_category";
  const targetOccupancyText = String(draft.target_occupancy_pct ?? "").trim();
  const targetOccupancyNumeric = Number(targetOccupancyText);
  return {
    scope_type: draft.scope_type || "hotel",
    scope_id:
      draft.scope_type === "hotel"
        ? ""
        : draft.scope_id == null
          ? ""
          : String(draft.scope_id).trim(),
    strategy_mode: draft.strategy_mode || "balanced",
    target_occupancy_pct:
      targetOccupancyText && Number.isFinite(targetOccupancyNumeric)
        ? String(Number((targetOccupancyNumeric / 100).toFixed(6)))
        : "",
    min_adr_target: isRoomCategoryScope ? draft.min_adr_target : "",
    desired_price_index: draft.desired_price_index,
    floor_price: isRoomCategoryScope ? draft.floor_price : "",
    ceiling_price: draft.ceiling_price,
    allow_min_stay_relaxation: Boolean(draft.allow_min_stay_relaxation),
    allow_min_stay_hardening: Boolean(draft.allow_min_stay_hardening),
    min_stay_min_allowed: draft.min_stay_min_allowed,
    min_stay_max_allowed: draft.min_stay_max_allowed,
    detailed_horizon_days: draft.detailed_horizon_days,
    long_range_mode: draft.long_range_mode || "season_blocks",
    long_range_block_type: draft.long_range_block_type || "seasonality_period",
    long_range_min_block_days: draft.long_range_min_block_days,
    is_active: Boolean(draft.is_active),
  };
}

function parseOptionalPriceGuardrail(value) {
  if (value == null || String(value).trim() === "") {
    return { isSet: false, isValid: true, value: null };
  }
  const numeric = Number(value);
  return {
    isSet: true,
    isValid: Number.isFinite(numeric),
    value: Number.isFinite(numeric) ? numeric : null,
  };
}

function findStrategyDraftGuardrailConflict({
  draft,
  editedStrategyId = null,
  strategies,
  roomCategories,
  policyByCategory,
  masterRateById,
}) {
  if (!draft?.is_active) return null;

  const strategyFloor = parseOptionalPriceGuardrail(draft.floor_price);
  const strategyCeiling = parseOptionalPriceGuardrail(draft.ceiling_price);
  const minAdrTarget = parseOptionalPriceGuardrail(draft.min_adr_target);
  const isRoomCategoryScope = draft.scope_type === "room_category";
  if (!strategyFloor.isValid || (strategyFloor.isSet && strategyFloor.value < 0)) {
    return { code: "invalid_strategy_floor" };
  }
  if (
    !strategyCeiling.isValid ||
    (strategyCeiling.isSet && strategyCeiling.value <= 0)
  ) {
    return { code: "invalid_strategy_ceiling" };
  }
  if (
    isRoomCategoryScope &&
    (!minAdrTarget.isValid || (minAdrTarget.isSet && minAdrTarget.value < 0))
  ) {
    return { code: "invalid_strategy_min_adr_target" };
  }
  const strategyFloorCandidates = [
    strategyFloor.isSet ? strategyFloor.value : Number.NEGATIVE_INFINITY,
  ];
  if (isRoomCategoryScope && minAdrTarget.isSet) {
    strategyFloorCandidates.push(minAdrTarget.value);
  }
  const effectiveStrategyFloor = Math.max(...strategyFloorCandidates);
  if (
    strategyCeiling.isSet &&
    effectiveStrategyFloor > strategyCeiling.value
  ) {
    return {
      code: "strategy_guardrail_conflict",
      effective_floor_price: effectiveStrategyFloor,
      effective_ceiling_price: strategyCeiling.value,
    };
  }

  const normalizedEditedStrategyId =
    editedStrategyId == null ? null : String(editedStrategyId);
  const otherActiveCategoryIds = new Set(
    (strategies || [])
      .filter(
        (strategy) =>
          strategy?.is_active === true &&
          strategy.scope_type === "room_category" &&
          (normalizedEditedStrategyId == null ||
            String(strategy.id) !== normalizedEditedStrategyId)
      )
      .map((strategy) => Number(strategy.scope_id))
      .filter((categoryId) => Number.isInteger(categoryId) && categoryId > 0)
  );
  const draftCategoryId = Number(draft.scope_id);
  const affectedCategories =
    draft.scope_type === "room_category"
      ? (roomCategories || []).filter(
          (category) => Number(category?.id) === draftCategoryId
        )
      : (roomCategories || []).filter(
          (category) => !otherActiveCategoryIds.has(Number(category?.id))
        );

  for (const category of affectedCategories) {
    const categoryId = Number(category?.id);
    const policy = policyByCategory.get(categoryId) || null;
    if (!policy?.is_active) continue;
    const masterRoomRateId = Number(policy.master_room_rate_id);
    const masterRate =
      masterRateById.get(masterRoomRateId) || policy.master_room_rate || null;
    const masterFloor = parseOptionalPriceGuardrail(masterRate?.min_price_amount);
    const masterCeiling = parseOptionalPriceGuardrail(masterRate?.max_price_amount);
    if (
      !masterFloor.isSet ||
      !masterFloor.isValid ||
      masterFloor.value < 0 ||
      !masterCeiling.isSet ||
      !masterCeiling.isValid ||
      masterCeiling.value <= 0
    ) {
      continue;
    }

    const effectiveFloor = Math.max(
      masterFloor.value,
      effectiveStrategyFloor
    );
    const effectiveCeiling = Math.min(
      masterCeiling.value,
      strategyCeiling.isSet ? strategyCeiling.value : Number.POSITIVE_INFINITY
    );
    if (effectiveFloor > effectiveCeiling) {
      return {
        code: "effective_strategy_guardrail_conflict",
        room_category_id: categoryId,
        room_category_name: category?.name || null,
        effective_floor_price: effectiveFloor,
        effective_ceiling_price: effectiveCeiling,
      };
    }
  }

  return null;
}

function formatStrategyGuardrailConflict(conflict, roomCategories, t) {
  if (conflict?.code === "invalid_strategy_floor") {
    return t(
      "This strategy cannot be active until its lowest allowed price is a valid number of zero or more."
    );
  }
  if (conflict?.code === "invalid_strategy_ceiling") {
    return t(
      "This strategy cannot be active until its highest allowed price is a valid number greater than zero."
    );
  }
  if (conflict?.code === "invalid_strategy_min_adr_target") {
    return t(
      "This strategy cannot be active until its minimum average daily rate is a valid number of zero or more."
    );
  }

  const category = (roomCategories || []).find(
    (item) => Number(item?.id) === Number(conflict?.room_category_id)
  );
  const categoryName =
    conflict?.room_category_name || category?.name || t("the selected room category");
  const floor =
    conflict?.effective_floor_price == null
      ? null
      : Number(conflict.effective_floor_price);
  const ceiling =
    conflict?.effective_ceiling_price == null
      ? null
      : Number(conflict.effective_ceiling_price);
  if (
    conflict?.room_category_id != null &&
    Number.isFinite(floor) &&
    Number.isFinite(ceiling)
  ) {
    return t(
      "This strategy cannot be active for {category}: the effective floor ({floor}) is above the effective ceiling ({ceiling}). Adjust the strategy or master-rate limits.",
      { category: categoryName, floor, ceiling }
    );
  }
  if (Number.isFinite(floor) && Number.isFinite(ceiling)) {
    return t(
      "This strategy cannot be active: its floor ({floor}) is above its ceiling ({ceiling}).",
      { floor, ceiling }
    );
  }
  if (typeof conflict?.message === "string" && conflict.message.trim()) {
    return conflict.message.trim();
  }
  return t(
    "An active strategy has incompatible floor and ceiling limits. Adjust its strategy or master-rate limits."
  );
}

function normalizeCompetitorDraft(source = {}) {
  return {
    id: source.id ?? null,
    hotel_code: source.hotel_code == null ? "" : String(source.hotel_code),
    source_name: String(source.source_name || source.competitor_name || "").trim(),
    source_address: String(source.source_address || "").trim(),
    competitor_weight:
      source.competitor_weight == null || source.competitor_weight === ""
        ? "1"
        : String(source.competitor_weight),
    distance_km: source.distance_km == null ? "" : String(source.distance_km),
    rating: source.rating == null ? "" : String(source.rating),
    review_count: source.review_count == null ? "" : String(source.review_count),
    is_active: source.is_active !== false,
  };
}

function buildCompetitorPayload(draft) {
  return {
    hotel_code: Number(draft.hotel_code),
    competitor_name: String(draft.source_name || "").trim() || null,
    competitor_weight: draft.competitor_weight,
    distance_km: draft.distance_km,
    rating: draft.rating,
    review_count: draft.review_count,
    is_active: Boolean(draft.is_active),
  };
}

function normalizeSeasonalityTemplateDraft(source = {}) {
  const normalizedLevel = String(source.default_demand_level || "").trim();
  const knownOption = SEASONALITY_TEMPLATE_DEMAND_OPTIONS.find(
    (option) => option.value === normalizedLevel
  );

  return {
    id: source.id ?? null,
    name: String(source.name || "").trim(),
    description: String(source.description || "").trim(),
    demand_level_choice: knownOption ? knownOption.value : normalizedLevel ? "custom" : "",
    demand_level_custom: knownOption ? "" : normalizedLevel,
    default_price_index:
      source.default_price_index == null ? "" : String(source.default_price_index),
    is_active: source.is_active !== false,
  };
}

function buildSeasonalityTemplatePayload(draft) {
  const demandLevel =
    draft.demand_level_choice === "custom"
      ? String(draft.demand_level_custom || "").trim()
      : String(draft.demand_level_choice || "").trim();

  return {
    name: String(draft.name || "").trim(),
    description: String(draft.description || "").trim(),
    default_demand_level: demandLevel || "",
    default_price_index: draft.default_price_index,
    is_active: Boolean(draft.is_active),
  };
}

function normalizeSeasonalityPeriodDraft(source = {}) {
  const priorityValue =
    source.priority == null || source.priority === "" ? "100" : String(source.priority);
  const knownPriority = SEASONALITY_PRIORITY_OPTIONS.find(
    (option) => option.value === priorityValue
  );

  return {
    id: source.id ?? null,
    seasonality_template_id:
      source.seasonality_template_id == null || source.seasonality_template_id === ""
        ? ""
        : String(source.seasonality_template_id),
    start_date: String(source.start_date || "").slice(0, 10),
    end_date: String(source.end_date || "").slice(0, 10),
    priority_choice: knownPriority ? knownPriority.value : "custom",
    priority_value: priorityValue,
    is_active: source.is_active !== false,
  };
}

function buildSeasonalityPeriodPayload(draft) {
  return {
    seasonality_template_id: draft.seasonality_template_id,
    start_date: String(draft.start_date || "").trim(),
    end_date: String(draft.end_date || "").trim(),
    priority: draft.priority_value,
    is_active: Boolean(draft.is_active),
  };
}

function normalizeSeasonalityProfileDraft(source = {}) {
  const normalizedLevel = String(source.expected_demand_level || "").trim();
  const knownOption = SEASONALITY_TEMPLATE_DEMAND_OPTIONS.find(
    (option) => option.value === normalizedLevel
  );
  const rawOccupancy =
    source.expected_occupancy_pct == null || source.expected_occupancy_pct === ""
      ? ""
      : String(source.expected_occupancy_pct);
  const numericOccupancy = Number(rawOccupancy);
  const occupancyDisplay =
    rawOccupancy === ""
      ? ""
      : Number.isFinite(numericOccupancy) && numericOccupancy >= 0 && numericOccupancy <= 1
        ? String(Number((numericOccupancy * 100).toFixed(2)))
        : rawOccupancy;

  return {
    id: source.id ?? null,
    seasonality_template_id:
      source.seasonality_template_id == null || source.seasonality_template_id === ""
        ? ""
        : String(source.seasonality_template_id),
    room_category_id:
      source.room_category_id == null || source.room_category_id === ""
        ? ""
        : String(source.room_category_id),
    dow: source.dow == null || source.dow === "" ? "" : String(source.dow),
    demand_level_choice: knownOption ? knownOption.value : normalizedLevel ? "custom" : "",
    demand_level_custom: knownOption ? "" : normalizedLevel,
    expected_price_index:
      source.expected_price_index == null ? "" : String(source.expected_price_index),
    expected_occupancy_pct: occupancyDisplay,
    is_active: source.is_active !== false,
  };
}

function buildSeasonalityProfilePayload(draft) {
  const demandLevel =
    draft.demand_level_choice === "custom"
      ? String(draft.demand_level_custom || "").trim()
      : String(draft.demand_level_choice || "").trim();
  const occupancyPct = String(draft.expected_occupancy_pct || "").trim();
  const occupancyNumeric = Number(occupancyPct);

  return {
    seasonality_template_id: draft.seasonality_template_id,
    room_category_id: String(draft.room_category_id || "").trim(),
    dow: String(draft.dow || "").trim(),
    expected_demand_level: demandLevel || "",
    expected_price_index: draft.expected_price_index,
    expected_occupancy_pct:
      occupancyPct && Number.isFinite(occupancyNumeric)
        ? String(Number((occupancyNumeric / 100).toFixed(4)))
        : "",
    is_active: Boolean(draft.is_active),
  };
}

function normalizeMarketDisruptionDirectionValue(value) {
  const normalized = String(value || "")
    .trim()
    .toLowerCase();
  if (["up", "upward", "increase", "higher", "positive", "boost"].includes(normalized)) {
    return "up";
  }
  if (
    ["down", "downward", "decrease", "lower", "negative", "drop", "reduce"].includes(
      normalized
    )
  ) {
    return "down";
  }
  return "";
}

function normalizeMarketDisruptionLevelValue(value) {
  const normalized = String(value || "")
    .trim()
    .toLowerCase();
  if (["low", "mild", "minor"].includes(normalized)) return "low";
  if (["medium", "moderate", "mid"].includes(normalized)) return "medium";
  if (["high", "major", "strong"].includes(normalized)) return "high";
  if (["severe", "critical", "extreme", "very_high"].includes(normalized)) return "severe";
  return "";
}

function normalizeMarketDisruptionTypeValue(value) {
  const normalized = String(value || "")
    .trim()
    .toLowerCase();
  return MARKET_DISRUPTION_TYPE_META[normalized] ? normalized : "other";
}

function normalizeMarketDisruptionDraft(source = {}) {
  const rawConfidence =
    source.confidence == null || source.confidence === "" ? "" : String(source.confidence);
  const numericConfidence = Number(rawConfidence);
  const confidenceDisplay =
    rawConfidence === ""
      ? "100"
      : Number.isFinite(numericConfidence) && numericConfidence >= 0 && numericConfidence <= 1
        ? String(Number((numericConfidence * 100).toFixed(2)))
        : rawConfidence;

  return {
    id: source.id ?? null,
    city_code: String(source.city_code || "").trim(),
    country_code: String(source.country_code || "").trim(),
    name: String(source.name || "").trim(),
    disruption_type: normalizeMarketDisruptionTypeValue(source.disruption_type),
    impact_direction: normalizeMarketDisruptionDirectionValue(source.impact_direction) || "up",
    impact_level: normalizeMarketDisruptionLevelValue(source.impact_level) || "medium",
    start_date: String(source.start_date || "").slice(0, 10),
    end_date: String(source.end_date || "").slice(0, 10),
    confidence: confidenceDisplay,
    source: String(source.source || "").trim(),
    notes: String(source.notes || "").trim(),
    is_active: source.is_active !== false,
  };
}

function buildMarketDisruptionPayload(draft) {
  const confidenceText = String(draft.confidence || "").trim();
  const confidenceNumeric = Number(confidenceText);

  return {
    city_code: String(draft.city_code || "").trim(),
    country_code: String(draft.country_code || "").trim(),
    name: String(draft.name || "").trim(),
    disruption_type: String(draft.disruption_type || "").trim(),
    impact_direction: String(draft.impact_direction || "").trim(),
    impact_level: String(draft.impact_level || "").trim(),
    start_date: String(draft.start_date || "").trim(),
    end_date: String(draft.end_date || "").trim(),
    confidence:
      confidenceText && Number.isFinite(confidenceNumeric)
        ? String(Number((confidenceNumeric / 100).toFixed(4)))
        : "",
    source: String(draft.source || "").trim(),
    notes: String(draft.notes || "").trim(),
    is_active: Boolean(draft.is_active),
  };
}

function getRoomCategoryName(roomCategories, scopeId) {
  if (!scopeId) return "";
  const match = (roomCategories || []).find((item) => String(item.id) === String(scopeId));
  return match?.name || "";
}

function getRoomCategoryCapacity(category) {
  const direct = Number(category?.max_occupancy);
  if (Number.isFinite(direct) && direct > 0) return direct;

  const adults = Number(category?.max_adults);
  const children = Number(category?.max_children);
  const total = (Number.isFinite(adults) ? adults : 0) + (Number.isFinite(children) ? children : 0);

  return total > 0 ? total : 1;
}

// eslint-disable-next-line no-unused-vars
function formatRoomCategoryCapacity(category) {
  const total = getRoomCategoryCapacity(category);
  const parts = [];
  const adults = Number(category?.max_adults);
  const children = Number(category?.max_children);

  if (Number.isFinite(adults) && adults > 0) {
    parts.push(`${adults} adult${adults === 1 ? "" : "s"}`);
  }
  if (Number.isFinite(children) && children > 0) {
    parts.push(`${children} child${children === 1 ? "" : "ren"}`);
  }

  return parts.length ? `${total} total · ${parts.join(" + ")}` : `${total} total`;
}

// eslint-disable-next-line no-unused-vars
function formatSeasonDateLabel(value) {
  if (!value) return "Not set";
  const date = new Date(`${String(value).slice(0, 10)}T00:00:00`);
  if (Number.isNaN(date.getTime())) return String(value);
  return new Intl.DateTimeFormat("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  }).format(date);
}

function getDateRangeDays(startDate, endDate) {
  if (!startDate || !endDate) return null;
  const start = new Date(`${String(startDate).slice(0, 10)}T00:00:00`);
  const end = new Date(`${String(endDate).slice(0, 10)}T00:00:00`);
  if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) return null;
  const diff = end.getTime() - start.getTime();
  if (diff < 0) return null;
  return Math.floor(diff / (24 * 60 * 60 * 1000)) + 1;
}

function formatLocalizedSeasonDateLabel(value, locale, t) {
  if (!value) return t("Not set");
  const date = new Date(`${String(value).slice(0, 10)}T00:00:00`);
  if (Number.isNaN(date.getTime())) return String(value);
  return new Intl.DateTimeFormat(locale || "en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  }).format(date);
}

export default function RmsSetupView({ canAccessSettings, onContinue }) {
  const { languageId, language } = usePmsTheme();
  const t = useMemo(() => createRmsTranslator(languageId), [languageId]);
  const locale = getRmsLocale(languageId, language);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [meta, setMeta] = useState(null);
  const [rateShopConfig, setRateShopConfig] = useState(null);
  const [ratePolicyDrafts, setRatePolicyDrafts] = useState({});
  const [savingRatePolicyCategoryId, setSavingRatePolicyCategoryId] = useState(null);
  const [categoryPriceLadderDraft, setCategoryPriceLadderDraft] = useState({
    expected_configuration_hash: null,
    items: [],
  });
  const [categoryPriceLadderDirty, setCategoryPriceLadderDirty] = useState(false);
  const [categoryPriceLadderError, setCategoryPriceLadderError] = useState("");
  const [savingCategoryPriceLadder, setSavingCategoryPriceLadder] = useState(false);
  const [occupancyPace, setOccupancyPace] = useState(null);
  const [occupancyPaceDraft, setOccupancyPaceDraft] = useState({
    contract_version: 0,
    is_applicable: false,
    expected_configuration_hash: null,
    profiles: [],
  });
  const [occupancyPaceDirty, setOccupancyPaceDirty] = useState(false);
  const [occupancyPaceError, setOccupancyPaceError] = useState("");
  const [savingOccupancyPace, setSavingOccupancyPace] = useState(false);
  const [priceResponse, setPriceResponse] = useState(null);
  const [priceResponseDraft, setPriceResponseDraft] = useState({
    expected_configuration_hash: null,
    strategies: [],
  });
  const [priceResponseDirty, setPriceResponseDirty] = useState(false);
  const [priceResponseError, setPriceResponseError] = useState("");
  const [savingPriceResponse, setSavingPriceResponse] = useState(false);
  const [orphanGapPolicy, setOrphanGapPolicy] = useState(null);
  const [orphanGapPolicyDraft, setOrphanGapPolicyDraft] = useState({
    expected_configuration_hash: null,
    items: [],
  });
  const [orphanGapPolicyDirty, setOrphanGapPolicyDirty] = useState(false);
  const [orphanGapPolicyError, setOrphanGapPolicyError] = useState("");
  const [savingOrphanGapPolicy, setSavingOrphanGapPolicy] = useState(false);
  const [orphanGapNearPolicy, setOrphanGapNearPolicy] = useState(null);
  const [orphanGapNearPolicyDraft, setOrphanGapNearPolicyDraft] = useState({
    expected_configuration_hash: null,
    fixed_near_horizon_days: 7,
    items: [],
  });
  const [orphanGapNearPolicyDirty, setOrphanGapNearPolicyDirty] = useState(false);
  const [orphanGapNearPolicyError, setOrphanGapNearPolicyError] = useState("");
  const [savingOrphanGapNearPolicy, setSavingOrphanGapNearPolicy] = useState(false);
  const [orphanGapStrongPolicy, setOrphanGapStrongPolicy] = useState(null);
  const [orphanGapStrongPolicyDraft, setOrphanGapStrongPolicyDraft] = useState({
    expected_configuration_hash: null,
    items: [],
  });
  const [orphanGapStrongPolicyDirty, setOrphanGapStrongPolicyDirty] = useState(false);
  const [orphanGapStrongPolicyError, setOrphanGapStrongPolicyError] = useState("");
  const [savingOrphanGapStrongPolicy, setSavingOrphanGapStrongPolicy] = useState(false);
  const [marketBenchmarkPolicy, setMarketBenchmarkPolicy] = useState(null);
  const [marketBenchmarkPolicyDraft, setMarketBenchmarkPolicyDraft] = useState(
    normalizeMarketBenchmarkPolicyDraft()
  );
  const [marketBenchmarkPolicyDirty, setMarketBenchmarkPolicyDirty] = useState(false);
  const [marketBenchmarkPolicyError, setMarketBenchmarkPolicyError] = useState("");
  const [savingMarketBenchmarkPolicy, setSavingMarketBenchmarkPolicy] = useState(false);
  const [quality, setQuality] = useState({
    own_rating: "",
    own_reviews_count: "",
    rating_sensitivity_pct_per_point: "",
    max_quality_adjustment_pct: "",
  });
  const [qualityDirty, setQualityDirty] = useState(false);
  const [resources, setResources] = useState({});
  const [editor, setEditor] = useState(null);
  const [strategyDraft, setStrategyDraft] = useState(null);
  const [competitorEditor, setCompetitorEditor] = useState(null);
  const [competitorDraft, setCompetitorDraft] = useState(null);
  const [competitorEditorError, setCompetitorEditorError] = useState("");
  const [seasonalityTemplateEditor, setSeasonalityTemplateEditor] = useState(null);
  const [seasonalityTemplateDraft, setSeasonalityTemplateDraft] = useState(null);
  const [seasonalityTemplateEditorError, setSeasonalityTemplateEditorError] =
    useState("");
  const [seasonalityPeriodEditor, setSeasonalityPeriodEditor] = useState(null);
  const [seasonalityPeriodDraft, setSeasonalityPeriodDraft] = useState(null);
  const [seasonalityPeriodEditorError, setSeasonalityPeriodEditorError] = useState("");
  const [seasonalityProfileEditor, setSeasonalityProfileEditor] = useState(null);
  const [seasonalityProfileDraft, setSeasonalityProfileDraft] = useState(null);
  const [seasonalityProfileEditorError, setSeasonalityProfileEditorError] = useState("");
  const [marketDisruptionEditor, setMarketDisruptionEditor] = useState(null);
  const [marketDisruptionDraft, setMarketDisruptionDraft] = useState(null);
  const [marketDisruptionEditorError, setMarketDisruptionEditorError] = useState("");
  const [editorText, setEditorText] = useState("");
  const [editorError, setEditorError] = useState("");
  const [saving, setSaving] = useState(false);
  const [activeSetupStep, setActiveSetupStep] = useState("products");

  const loadAll = useCallback(async () => {
    if (!canAccessSettings) return;
    try {
      setLoading(true);
      setError("");
      const [
        metaPayload,
        qualityPayload,
        rateShopPayload,
        occupancyPacePayload,
        priceResponsePayload,
        orphanGapPolicyPayload,
        orphanGapNearPolicyPayload,
        orphanGapStrongPolicyPayload,
        marketBenchmarkPolicyPayload,
        ...resourcePayloads
      ] = await Promise.all([
        fetchRmsConfigMeta(),
        fetchRmsQualitySettings(),
        fetchRmsRateShopConfig(),
        fetchRmsOccupancyPace(),
        fetchRmsPriceResponse(),
        fetchRmsOrphanGapPolicy(),
        fetchRmsOrphanGapNearPolicy(),
        fetchRmsOrphanGapStrongPolicy(),
        fetchRmsMarketBenchmarkPolicy(),
        ...RESOURCE_CONFIG.map((resource) => fetchRmsConfigResource(resource.key)),
      ]);

      const nextResources = {};
      RESOURCE_CONFIG.forEach((resource, index) => {
        nextResources[resource.key] = resourcePayloads[index];
      });

      setMeta(metaPayload);
      setRateShopConfig(rateShopPayload);
      const loadedRoomCategories = Array.isArray(metaPayload?.room_categories)
        ? metaPayload.room_categories
        : [];
      setCategoryPriceLadderDraft(
        normalizeCategoryPriceLadderDraft(
          metaPayload?.category_price_ladder,
          loadedRoomCategories
        )
      );
      setCategoryPriceLadderDirty(false);
      setCategoryPriceLadderError("");
      const loadedOccupancyPace =
        occupancyPacePayload || metaPayload?.occupancy_pace || null;
      setOccupancyPace(loadedOccupancyPace);
      setOccupancyPaceDraft(
        normalizeOccupancyPaceDraft(loadedOccupancyPace || {})
      );
      setOccupancyPaceDirty(false);
      setOccupancyPaceError("");
      const loadedPriceResponse =
        priceResponsePayload || metaPayload?.price_response || null;
      setPriceResponse(loadedPriceResponse);
      setPriceResponseDraft(
        normalizePriceResponseDraft(
          loadedPriceResponse || {},
          nextResources["strategy-settings"] || []
        )
      );
      setPriceResponseDirty(false);
      setPriceResponseError("");
      const loadedOrphanGapPolicy =
        orphanGapPolicyPayload || metaPayload?.orphan_gap_policy || null;
      setOrphanGapPolicy(loadedOrphanGapPolicy);
      setOrphanGapPolicyDraft(
        normalizeOrphanGapPolicyDraft(
          loadedOrphanGapPolicy || {},
          nextResources["strategy-settings"] || []
        )
      );
      setOrphanGapPolicyDirty(false);
      setOrphanGapPolicyError("");
      const loadedOrphanGapNearPolicy =
        orphanGapNearPolicyPayload || metaPayload?.orphan_gap_near_policy || null;
      setOrphanGapNearPolicy(loadedOrphanGapNearPolicy);
      setOrphanGapNearPolicyDraft(
        normalizeOrphanGapNearPolicyDraft(
          loadedOrphanGapNearPolicy || {},
          nextResources["strategy-settings"] || []
        )
      );
      setOrphanGapNearPolicyDirty(false);
      setOrphanGapNearPolicyError("");
      const loadedOrphanGapStrongPolicy =
        orphanGapStrongPolicyPayload ||
        metaPayload?.orphan_gap_strong_policy ||
        null;
      setOrphanGapStrongPolicy(loadedOrphanGapStrongPolicy);
      setOrphanGapStrongPolicyDraft(
        normalizeOrphanGapStrongPolicyDraft(
          loadedOrphanGapStrongPolicy || {},
          nextResources["strategy-settings"] || []
        )
      );
      setOrphanGapStrongPolicyDirty(false);
      setOrphanGapStrongPolicyError("");
      const loadedMarketBenchmarkPolicy =
        marketBenchmarkPolicyPayload || null;
      setMarketBenchmarkPolicy(loadedMarketBenchmarkPolicy);
      setMarketBenchmarkPolicyDraft(
        normalizeMarketBenchmarkPolicyDraft(
          loadedMarketBenchmarkPolicy || {}
        )
      );
      setMarketBenchmarkPolicyDirty(false);
      setMarketBenchmarkPolicyError("");
      const preferredPolicyByCategory = new Map();
      (nextResources["rate-product-policies"] || []).forEach((item) => {
        const categoryId = Number(item?.room_category_id);
        if (!categoryId) return;
        const current = preferredPolicyByCategory.get(categoryId);
        if (
          !current ||
          (item?.is_active && !current?.is_active) ||
          (Boolean(item?.is_active) === Boolean(current?.is_active) &&
            Number(item?.policy_version || 0) > Number(current?.policy_version || 0))
        ) {
          preferredPolicyByCategory.set(categoryId, item);
        }
      });
      setRatePolicyDrafts(
        Object.fromEntries(
          loadedRoomCategories.map(
            (category) => [
              String(category.id),
              preferredPolicyByCategory.get(Number(category.id))?.master_room_rate_id == null
                ? ""
                : String(
                    preferredPolicyByCategory.get(Number(category.id)).master_room_rate_id
                  ),
            ]
          )
        )
      );
      setQuality((previous) => ({ ...previous, ...(qualityPayload || {}) }));
      setQualityDirty(false);
      setResources(nextResources);
    } catch (nextError) {
      setError(extractErrorMessage(nextError, t("Could not load RMS setup.")));
    } finally {
      setLoading(false);
    }
  }, [canAccessSettings, t]);

  const reloadMarketMappings = useCallback(async () => {
    if (!canAccessSettings) return;
    try {
      const [roomCategoryMappings, competitorRoomTypeMappings] = await Promise.all([
        fetchRmsConfigResource("room-category-mappings"),
        fetchRmsConfigResource("competitor-room-type-mappings"),
      ]);

      setResources((previous) => ({
        ...previous,
        "room-category-mappings": roomCategoryMappings,
        "competitor-room-type-mappings": competitorRoomTypeMappings,
      }));
    } catch (nextError) {
      setError(extractErrorMessage(nextError, t("Could not refresh market mappings.")));
      throw nextError;
    }
  }, [canAccessSettings, t]);

  useEffect(() => {
    loadAll();
  }, [loadAll]);

  const competitorScopeRows = useMemo(() => {
    const rows = Array.isArray(rateShopConfig?.competitor_hotels)
      ? rateShopConfig.competitor_hotels
      : [];
    return rows
      .map((item, index) => ({
        hotel_code: Number(item?.hotel_code),
        source_name: String(item?.name || "").trim(),
        source_address: String(item?.address || "").trim(),
        source_index: index,
      }))
      .filter((item) => item.hotel_code && item.source_name);
  }, [rateShopConfig]);

  const competitorSettingsByHotelCode = useMemo(() => {
    const map = new Map();
    (resources.competitors || []).forEach((item) => {
      const hotelCode = Number(item?.hotel_code);
      if (hotelCode) {
        map.set(hotelCode, item);
      }
    });
    return map;
  }, [resources]);

  const mergedCompetitors = useMemo(
    () =>
      competitorScopeRows.map((sourceItem) => {
        const existing = competitorSettingsByHotelCode.get(sourceItem.hotel_code) || null;
        return {
          id: existing?.id ?? null,
          hotel_code: sourceItem.hotel_code,
          source_name: sourceItem.source_name,
          source_address: sourceItem.source_address,
          competitor_name: existing?.competitor_name || sourceItem.source_name,
          competitor_weight: existing?.competitor_weight ?? 1,
          distance_km: existing?.distance_km ?? "",
          rating: existing?.rating ?? "",
          review_count: existing?.review_count ?? "",
          is_active: existing?.is_active !== false,
          hasCustomSettings: Boolean(existing),
        };
      }),
    [competitorScopeRows, competitorSettingsByHotelCode]
  );

  const competitorSourcesLabel = useMemo(() => {
    const rows = Array.isArray(rateShopConfig?.sources_meta) ? rateShopConfig.sources_meta : [];
    return rows
      .map((item) => String(item?.source_name || "").trim())
      .filter(Boolean)
      .join(", ");
  }, [rateShopConfig]);

  const roomCategoryOptions = useMemo(
    () => (Array.isArray(meta?.room_categories) ? meta.room_categories : []),
    [meta]
  );
  const roomCategoryById = useMemo(
    () =>
      new Map(
        roomCategoryOptions.map((category) => [Number(category?.id), category])
      ),
    [roomCategoryOptions]
  );

  const categoryPriceLadder = useMemo(
    () =>
      meta?.category_price_ladder &&
      typeof meta.category_price_ladder === "object" &&
      !Array.isArray(meta.category_price_ladder)
        ? meta.category_price_ladder
        : null,
    [meta]
  );
  const categoryPriceLadderBackendIssues = useMemo(
    () =>
      Array.isArray(categoryPriceLadder?.issues)
        ? categoryPriceLadder.issues
        : [],
    [categoryPriceLadder]
  );
  const categoryPriceLadderActiveCategoryIds = useMemo(
    () =>
      new Set(
        (Array.isArray(categoryPriceLadder?.active_policy_category_ids)
          ? categoryPriceLadder.active_policy_category_ids
          : []
        )
          .map(Number)
          .filter(
            (roomCategoryId) =>
              Number.isInteger(roomCategoryId) && roomCategoryId > 0
          )
      ),
    [categoryPriceLadder]
  );
  const categoryPriceLadderCategoryOptions = useMemo(
    () =>
      roomCategoryOptions.filter((category) =>
        categoryPriceLadderActiveCategoryIds.has(Number(category?.id))
      ),
    [categoryPriceLadderActiveCategoryIds, roomCategoryOptions]
  );
  const activeRateProductPolicyCount = categoryPriceLadderActiveCategoryIds.size;
  const categoryPriceLadderIsApplicable =
    categoryPriceLadder?.is_applicable === true &&
    activeRateProductPolicyCount > 1;
  const categoryPriceLadderHasStoredRules =
    Array.isArray(categoryPriceLadder?.items) &&
    categoryPriceLadder.items.length > 0;
  const categoryPriceLadderNeedsClear =
    !categoryPriceLadderIsApplicable && categoryPriceLadderHasStoredRules;
  const categoryPriceLadderLocalErrors = useMemo(
    () =>
      validateCategoryPriceLadderDraft({
        draft: categoryPriceLadderDraft,
        roomCategories: categoryPriceLadderCategoryOptions,
        activePolicyCount: activeRateProductPolicyCount,
        t,
      }),
    [
      activeRateProductPolicyCount,
      categoryPriceLadderDraft,
      categoryPriceLadderCategoryOptions,
      t,
    ]
  );
  const categoryPriceLadderDraftByCategory = useMemo(
    () =>
      new Map(
        (categoryPriceLadderDraft.items || []).map((item) => [
          Number(item.room_category_id),
          item,
        ])
      ),
    [categoryPriceLadderDraft.items]
  );

  const masterRoomRates = useMemo(
    () =>
      Array.isArray(meta?.eligible_master_room_rates) ? meta.eligible_master_room_rates : [],
    [meta]
  );

  const rateProductPolicyByCategory = useMemo(() => {
    const out = new Map();
    (resources["rate-product-policies"] || []).forEach((item) => {
      const categoryId = Number(item?.room_category_id);
      if (!categoryId) return;
      const current = out.get(categoryId);
      if (
        !current ||
        (item?.is_active && !current?.is_active) ||
        (Boolean(item?.is_active) === Boolean(current?.is_active) &&
          Number(item?.policy_version || 0) > Number(current?.policy_version || 0))
      ) {
        out.set(categoryId, item);
      }
    });
    return out;
  }, [resources]);

  const masterRoomRatesByCategory = useMemo(() => {
    const out = new Map();
    masterRoomRates.forEach((roomRate) => {
      const categoryId = Number(roomRate?.room_category_id);
      if (!categoryId) return;
      if (!out.has(categoryId)) out.set(categoryId, []);
      out.get(categoryId).push(roomRate);
    });
    return out;
  }, [masterRoomRates]);

  const eligibleMasterRoomRateById = useMemo(
    () =>
      new Map(
        masterRoomRates
          .filter((roomRate) => roomRate?.id != null)
          .map((roomRate) => [Number(roomRate.id), roomRate])
      ),
    [masterRoomRates]
  );

  const strategyGuardrailConflicts = useMemo(
    () =>
      Array.isArray(meta?.strategy_guardrail_conflicts)
        ? meta.strategy_guardrail_conflicts
        : [],
    [meta]
  );

  const rateProductDerivationReadinessByCategory = useMemo(
    () =>
      new Map(
        (Array.isArray(meta?.rate_product_derivation_readiness)
          ? meta.rate_product_derivation_readiness
          : []
        )
          .filter((item) => item?.room_category_id != null)
          .map((item) => [Number(item.room_category_id), item])
      ),
    [meta]
  );
  const hasRateProductDerivationReadiness =
    Array.isArray(meta?.rate_product_derivation_readiness);

  const configuredMasterRateCategoryIds = useMemo(() => {
    const out = new Set();
    roomCategoryOptions.forEach((category) => {
      const categoryId = Number(category?.id);
      const policy = rateProductPolicyByCategory.get(categoryId);
      const masterRate = eligibleMasterRoomRateById.get(Number(policy?.master_room_rate_id));
      const derivationReadiness = rateProductDerivationReadinessByCategory.get(categoryId);
      if (
        policy?.is_active &&
        masterRate &&
        Number(masterRate.room_category_id) === categoryId &&
        (!hasRateProductDerivationReadiness ||
          (derivationReadiness?.is_ready === true &&
            Number(derivationReadiness.master_room_rate_id) ===
              Number(policy.master_room_rate_id)))
      ) {
        out.add(categoryId);
      }
    });
    return out;
  }, [
    eligibleMasterRoomRateById,
    hasRateProductDerivationReadiness,
    rateProductDerivationReadinessByCategory,
    rateProductPolicyByCategory,
    roomCategoryOptions,
  ]);

  const seasonalityTemplateOptions = useMemo(
    () =>
      (resources["seasonality-templates"] || []).map((item) => ({
        id: item.id,
        name: String(item.name || "").trim() || `Template #${item.id}`,
        is_active: item.is_active !== false,
      })),
    [resources]
  );

  const seasonalityTemplateNameById = useMemo(() => {
    const map = new Map();
    seasonalityTemplateOptions.forEach((item) => {
      map.set(String(item.id), item.name);
    });
    return map;
  }, [seasonalityTemplateOptions]);

  const seasonalityProfileById = useMemo(
    () =>
      new Map(
        (resources["seasonality-profiles"] || [])
          .filter((profile) => profile?.id != null)
          .map((profile) => [String(profile.id), profile])
      ),
    [resources]
  );
  const occupancyPaceBackendIssues = useMemo(
    () => (Array.isArray(occupancyPace?.issues) ? occupancyPace.issues : []),
    [occupancyPace]
  );
  const occupancyPaceLocalErrors = useMemo(
    () => validateOccupancyPaceDraft(occupancyPaceDraft, t),
    [occupancyPaceDraft, t]
  );
  const occupancyPaceHasRuntime = Boolean(
    occupancyPace &&
      typeof occupancyPace === "object" &&
      !Array.isArray(occupancyPace)
  );
  const occupancyPaceIsApplicable =
    occupancyPaceHasRuntime && occupancyPace?.is_applicable === true;
  const occupancyPaceHasProfiles = occupancyPaceDraft.profiles.length > 0;
  const occupancyPaceCanConfigure =
    occupancyPaceHasRuntime && occupancyPaceHasProfiles;
  const occupancyPaceIsReady =
    occupancyPaceHasRuntime &&
    occupancyPace?.is_ready === true &&
    !occupancyPaceDirty &&
    occupancyPaceLocalErrors.length === 0 &&
    (!occupancyPaceIsApplicable || occupancyPaceHasProfiles);
  const priceResponseBackendIssues = useMemo(
    () => (Array.isArray(priceResponse?.issues) ? priceResponse.issues : []),
    [priceResponse]
  );
  const priceResponseLocalErrors = useMemo(
    () => validatePriceResponseDraft(priceResponseDraft, t),
    [priceResponseDraft, t]
  );
  const priceResponseHasRuntime = Boolean(
    priceResponse && typeof priceResponse === "object" && !Array.isArray(priceResponse)
  );
  const priceResponseIsApplicable =
    priceResponseHasRuntime && priceResponse?.is_applicable === true;
  const priceResponseCanConfigure =
    priceResponseHasRuntime && priceResponseDraft.strategies.length > 0;
  const priceResponseIsReady =
    priceResponseHasRuntime &&
    priceResponse?.is_ready === true &&
    !priceResponseDirty &&
    priceResponseLocalErrors.length === 0 &&
    (!priceResponseIsApplicable || priceResponseDraft.strategies.length > 0);
  const orphanGapPolicyBackendIssues = useMemo(
    () => (Array.isArray(orphanGapPolicy?.issues) ? orphanGapPolicy.issues : []),
    [orphanGapPolicy]
  );
  const orphanGapPolicyLocalErrors = useMemo(
    () => validateOrphanGapPolicyDraft(orphanGapPolicyDraft),
    [orphanGapPolicyDraft]
  );
  const orphanGapPolicyHasRuntime = Boolean(
    orphanGapPolicy &&
      typeof orphanGapPolicy === "object" &&
      !Array.isArray(orphanGapPolicy) &&
      Number(orphanGapPolicy.contract_version) === 1 &&
      /^[0-9a-f]{64}$/.test(String(orphanGapPolicy.configuration_hash || ""))
  );
  const orphanGapPolicyCanConfigure =
    orphanGapPolicyHasRuntime && orphanGapPolicyDraft.items.length > 0;
  const orphanGapPolicyIsReady =
    orphanGapPolicyHasRuntime &&
    orphanGapPolicy?.is_ready === true &&
    !orphanGapPolicyDirty &&
    orphanGapPolicyLocalErrors.length === 0;
  const orphanGapNearPolicyBackendIssues = useMemo(
    () =>
      Array.isArray(orphanGapNearPolicy?.issues)
        ? orphanGapNearPolicy.issues
        : [],
    [orphanGapNearPolicy]
  );
  const orphanGapNearPolicyLocalErrors = useMemo(
    () =>
      validateOrphanGapNearPolicyDraft(
        orphanGapNearPolicyDraft,
        orphanGapPolicyDraft
      ),
    [orphanGapNearPolicyDraft, orphanGapPolicyDraft]
  );
  const orphanGapNearPolicyHasRuntime = Boolean(
    orphanGapNearPolicy &&
      typeof orphanGapNearPolicy === "object" &&
      !Array.isArray(orphanGapNearPolicy) &&
      Number(orphanGapNearPolicy.contract_version) === 1 &&
      Number(orphanGapNearPolicy.fixed_near_horizon_days) === 7 &&
      /^[0-9a-f]{64}$/.test(
        String(orphanGapNearPolicy.configuration_hash || "")
      )
  );
  const orphanGapNearPolicyCanConfigure =
    orphanGapNearPolicyHasRuntime && orphanGapNearPolicyDraft.items.length > 0;
  const orphanGapNearPolicyIsReady =
    orphanGapNearPolicyHasRuntime &&
    orphanGapNearPolicy?.is_ready === true &&
    !orphanGapNearPolicyDirty &&
    orphanGapNearPolicyLocalErrors.length === 0;
  const orphanGapStrongPolicyBackendIssues = useMemo(
    () =>
      Array.isArray(orphanGapStrongPolicy?.issues)
        ? orphanGapStrongPolicy.issues
        : [],
    [orphanGapStrongPolicy]
  );
  const orphanGapStrongPolicyLocalErrors = useMemo(
    () =>
      validateOrphanGapStrongPolicyDraft(
        orphanGapStrongPolicyDraft,
        orphanGapPolicyDraft,
        orphanGapNearPolicyDraft
      ),
    [
      orphanGapStrongPolicyDraft,
      orphanGapPolicyDraft,
      orphanGapNearPolicyDraft,
    ]
  );
  const orphanGapStrongPolicyHasRuntime = Boolean(
    orphanGapStrongPolicy &&
      typeof orphanGapStrongPolicy === "object" &&
      !Array.isArray(orphanGapStrongPolicy) &&
      Number(orphanGapStrongPolicy.contract_version) === 1 &&
      /^[0-9a-f]{64}$/.test(
        String(orphanGapStrongPolicy.configuration_hash || "")
      )
  );
  const orphanGapStrongPolicyCanConfigure =
    orphanGapStrongPolicyHasRuntime && orphanGapStrongPolicyDraft.items.length > 0;
  const orphanGapStrongPolicyIsReady =
    orphanGapStrongPolicyHasRuntime &&
    orphanGapStrongPolicy?.is_ready === true &&
    !orphanGapStrongPolicyDirty &&
    orphanGapStrongPolicyLocalErrors.length === 0;

  const integrityCards = useMemo(() => {
    const roomCategoriesCount = meta?.room_categories?.length || 0;
    const mappedCount = new Set(
      (resources["room-category-mappings"] || [])
        .filter((item) => item.is_active && item.room_category_id != null)
        .map((item) => String(item.room_category_id))
    ).size;
    return [
      {
        label: t("Product policies ready"),
        value: `${configuredMasterRateCategoryIds.size}/${roomCategoriesCount || 0}`,
        meta:
          roomCategoriesCount > 0
            ? t("{count} still without a ready product policy", {
                count: Math.max(roomCategoriesCount - configuredMasterRateCategoryIds.size, 0),
              })
            : t("No categories found"),
      },
      {
        label: t("Room categories mapped"),
        value: `${mappedCount}/${roomCategoriesCount || 0}`,
        meta: roomCategoriesCount > 0 ? `${Math.max(roomCategoriesCount - mappedCount, 0)} ${t("still without mapping")}` : t("No categories found"),
      },
      {
        label: t("Category price ladder"),
        value:
          categoryPriceLadderNeedsClear
            ? t("Needs attention")
            : !categoryPriceLadderIsApplicable
            ? t("Not applicable")
            : categoryPriceLadder?.is_ready === true && !categoryPriceLadderDirty
            ? t("Ready")
            : t("Needs attention"),
        meta:
          categoryPriceLadderNeedsClear
            ? t("Clear saved ladder rules that no longer apply")
            : !categoryPriceLadderIsApplicable
            ? t("At least two active product policies are needed for a price ladder")
            : categoryPriceLadderDirty
            ? t("Save the category price ladder changes")
            : categoryPriceLadder?.is_ready === true
            ? t("Every category has an explicit commercial relationship")
            : t("Define roots, enforced gaps or documented exceptions"),
      },
      {
        label: t("Active strategies"),
        value: (resources["strategy-settings"] || []).filter((item) => item.is_active).length,
        meta: t("Hotel-level and category-level posture"),
      },
      {
        label: t("Active competitors"),
        value: mergedCompetitors.filter((item) => item.is_active).length,
        meta: competitorScopeRows.length
          ? `${Math.max(competitorScopeRows.length - mergedCompetitors.filter((item) => item.is_active).length, 0)} ${t("currently ignored")}`
          : t("No competitor scope configured"),
      },
      {
        label: t("Seasonality periods"),
        value: (resources["seasonality-periods"] || []).filter((item) => item.is_active).length,
        meta: `${(resources["market-disruptions"] || []).filter((item) => item.is_active).length} ${t("active disruptions")}`,
      },
      {
        label: t("Occupancy pace"),
        value: !occupancyPaceHasRuntime
          ? t("Needs attention")
          : !occupancyPaceIsApplicable
          ? occupancyPaceHasProfiles
            ? t("Not configured")
            : t("Not applicable")
          : occupancyPaceIsReady
            ? t("Ready")
            : t("Needs attention"),
        meta: !occupancyPaceHasRuntime
          ? t("Occupancy pace configuration is unavailable")
          : !occupancyPaceIsApplicable
          ? occupancyPaceHasProfiles
            ? t("Complete all five windows to opt in to occupancy pace")
            : t("No active seasonality profiles require a pace curve")
          : occupancyPaceDirty
            ? t("Save the occupancy pace changes")
            : occupancyPaceIsReady
              ? t("Every active profile has five lead-time windows")
            : t("Complete the expected booked share and tolerance for every window"),
      },
      {
        label: t("Price response safeguards"),
        value:
          !priceResponseHasRuntime ||
          !orphanGapPolicyHasRuntime ||
          !orphanGapNearPolicyHasRuntime ||
          !orphanGapStrongPolicyHasRuntime
          ? t("Needs attention")
          : !priceResponseIsReady ||
              !orphanGapPolicyIsReady ||
              !orphanGapNearPolicyIsReady ||
              !orphanGapStrongPolicyIsReady
            ? t("Needs attention")
          : !priceResponseIsApplicable &&
              orphanGapPolicy?.is_applicable !== true &&
              orphanGapNearPolicy?.is_applicable !== true &&
              orphanGapStrongPolicy?.is_applicable !== true
            ? t("Not applicable")
            : t("Ready"),
        meta: !priceResponseHasRuntime
          ? t("Price-response configuration is unavailable")
          : !orphanGapPolicyHasRuntime
            ? t("Orphan-gap configuration is unavailable.")
          : !orphanGapNearPolicyHasRuntime
            ? t("Near-gap configuration is unavailable.")
          : !orphanGapStrongPolicyHasRuntime
            ? t("Strong-demand gap configuration is unavailable.")
          : priceResponseDirty
            ? t("Save the price-response safeguards")
          : orphanGapPolicyDirty
            ? t("Save orphan-gap safeguards")
          : orphanGapNearPolicyDirty
            ? t("Save near-gap safeguards")
          : orphanGapStrongPolicyDirty
            ? t("Save strong-demand safeguards")
          : !priceResponseIsReady
            ? t("Complete all five windows for every effective strategy")
          : !orphanGapPolicyIsReady ||
              !orphanGapNearPolicyIsReady ||
              !orphanGapStrongPolicyIsReady
            ? t("Resolve the orphan-gap safeguard issues")
          : !priceResponseIsApplicable &&
              orphanGapPolicy?.is_applicable !== true &&
              orphanGapNearPolicy?.is_applicable !== true &&
              orphanGapStrongPolicy?.is_applicable !== true
            ? t("No effective strategy requires response safeguards")
            : t("Response and orphan-gap safeguards are ready"),
      },
    ];
  }, [
    competitorScopeRows.length,
    categoryPriceLadder,
    categoryPriceLadderDirty,
    categoryPriceLadderIsApplicable,
    categoryPriceLadderNeedsClear,
    configuredMasterRateCategoryIds.size,
    mergedCompetitors,
    meta,
    occupancyPaceDirty,
    occupancyPaceHasProfiles,
    occupancyPaceHasRuntime,
    occupancyPaceIsApplicable,
    occupancyPaceIsReady,
    orphanGapNearPolicy,
    orphanGapNearPolicyDirty,
    orphanGapNearPolicyHasRuntime,
    orphanGapNearPolicyIsReady,
    orphanGapPolicy,
    orphanGapPolicyDirty,
    orphanGapPolicyHasRuntime,
    orphanGapPolicyIsReady,
    orphanGapStrongPolicy,
    orphanGapStrongPolicyDirty,
    orphanGapStrongPolicyHasRuntime,
    orphanGapStrongPolicyIsReady,
    priceResponseDirty,
    priceResponseHasRuntime,
    priceResponseIsApplicable,
    priceResponseIsReady,
    resources,
    t,
  ]);

  const setupSteps = useMemo(() => {
    const roomCategoriesCount = meta?.room_categories?.length || 0;
    const mappedRoomCategoriesCount = new Set(
      (resources["room-category-mappings"] || [])
        .filter((item) => item.is_active && item.room_category_id != null)
        .map((item) => String(item.room_category_id))
    ).size;
    const activeCompetitorsCount = mergedCompetitors.filter((item) => item.is_active).length;
    const activeStrategies = (resources["strategy-settings"] || []).filter(
      (item) => item.is_active
    );
    const activeHotelStrategies = activeStrategies.filter(
      (item) => String(item.scope_type || "hotel") === "hotel"
    );
    const hasHotelStrategy = activeHotelStrategies.length === 1;
    const activeTemplatesCount = (resources["seasonality-templates"] || []).filter(
      (item) => item.is_active
    ).length;
    const activePeriodsCount = (resources["seasonality-periods"] || []).filter(
      (item) => item.is_active
    ).length;
    const activeProfilesCount = (resources["seasonality-profiles"] || []).filter(
      (item) => item.is_active
    ).length;
    const activeDisruptionsCount = (resources["market-disruptions"] || []).filter(
      (item) => item.is_active
    ).length;
    const qualityIsComplete =
      !qualityDirty &&
      QUALITY_FIELD_CONFIG.every(
        (field) => String(quality[field.key] ?? "").trim() !== ""
      );
    const productPoliciesAreReady =
      roomCategoriesCount > 0 &&
      configuredMasterRateCategoryIds.size === roomCategoriesCount;
    const categoryPriceLadderIsReady =
      categoryPriceLadder?.is_ready === true && !categoryPriceLadderDirty;

    const statusByKey = {
      products: {
        ready: productPoliciesAreReady && categoryPriceLadderIsReady,
        detail:
          roomCategoriesCount === 0
            ? t("No hotel room categories found")
            : !productPoliciesAreReady
              ? t("{configured}/{total} product policies ready", {
                  configured: configuredMasterRateCategoryIds.size,
                  total: roomCategoriesCount,
                })
              : categoryPriceLadderDirty
                ? t("Save the category price ladder changes")
                : categoryPriceLadderIsReady
                ? categoryPriceLadderIsApplicable
                  ? t("Master products and category price ladder are ready")
                  : t("Master products are ready; the category ladder is not applicable")
                : t("Complete the category price ladder before using this setup"),
      },
      strategy: {
        ready: hasHotelStrategy && strategyGuardrailConflicts.length === 0,
        detail:
          activeHotelStrategies.length > 1
            ? t("Keep only one active whole-hotel strategy")
            : strategyGuardrailConflicts.length > 0
              ? t(
                  strategyGuardrailConflicts.length === 1
                    ? "Fix the active floor and ceiling conflict before using this setup"
                    : "Fix {count} active floor and ceiling conflicts before using this setup",
                  { count: strategyGuardrailConflicts.length }
                )
            : hasHotelStrategy
              ? t("Whole-hotel strategy ready")
              : t("Add one active whole-hotel strategy"),
      },
      competitors: {
        ready: competitorScopeRows.length > 0 && activeCompetitorsCount > 0,
        detail:
          competitorScopeRows.length === 0
            ? t("Market extraction scope is not available yet")
            : activeCompetitorsCount > 0
              ? t(
                  activeCompetitorsCount === 1
                    ? "{count} active competitor"
                    : "{count} active competitors",
                  { count: activeCompetitorsCount }
                )
              : t("Activate at least one relevant competitor"),
      },
      mapping: {
        ready:
          roomCategoriesCount > 0 && mappedRoomCategoriesCount >= roomCategoriesCount,
        detail:
          roomCategoriesCount === 0
            ? t("No hotel room categories found")
            : t("{mapped}/{total} room categories mapped", {
                mapped: mappedRoomCategoriesCount,
                total: roomCategoriesCount,
              }),
      },
      templates: {
        ready: activeTemplatesCount > 0,
        detail:
          activeTemplatesCount > 0
            ? t(
                activeTemplatesCount === 1
                  ? "{count} active season template"
                  : "{count} active season templates",
                { count: activeTemplatesCount }
              )
            : t("Create the first active season template"),
      },
      periods: {
        ready: activePeriodsCount > 0,
        detail:
          activeTemplatesCount === 0
            ? t("Requires an active season template from step 5")
            : activePeriodsCount > 0
              ? t(
                  activePeriodsCount === 1
                    ? "{count} active calendar period"
                    : "{count} active calendar periods",
                  { count: activePeriodsCount }
                )
              : t("Place an active season on the calendar"),
      },
      quality: {
        ready: qualityIsComplete,
        detail: qualityIsComplete
          ? t("Quality calibration reviewed")
          : t("Review and save the four quality values"),
      },
      profiles: {
        ready: activeProfilesCount > 0,
        detail:
          activeTemplatesCount === 0
            ? t("Requires an active season template from step 5")
            : activeProfilesCount > 0
              ? t(
                  activeProfilesCount === 1
                    ? "{count} active refinement"
                    : "{count} active refinements",
                  { count: activeProfilesCount }
                )
              : t("Optional — no refinements configured"),
      },
      pace: {
        ready:
          occupancyPaceHasRuntime &&
          (!occupancyPaceIsApplicable || occupancyPaceIsReady),
        detail: !occupancyPaceHasRuntime
          ? t("Occupancy pace configuration is unavailable")
          : !occupancyPaceIsApplicable
          ? occupancyPaceHasProfiles
            ? t("Not configured — complete all five windows to opt in")
            : t("No active seasonality profiles require a pace curve")
          : occupancyPaceDirty
            ? t("Save the occupancy pace changes")
            : occupancyPaceLocalErrors.length > 0 ||
                occupancyPaceBackendIssues.length > 0
              ? t("Complete all five lead-time windows for every active profile")
              : t(
                  occupancyPaceDraft.profiles.length === 1
                    ? "{count} active profile ready"
                    : "{count} active profiles ready",
                  { count: occupancyPaceDraft.profiles.length }
                ),
      },
      "price-response": {
        ready:
          priceResponseHasRuntime &&
          orphanGapPolicyHasRuntime &&
          orphanGapNearPolicyHasRuntime &&
          orphanGapStrongPolicyHasRuntime &&
          priceResponseIsReady &&
          orphanGapPolicyIsReady &&
          orphanGapNearPolicyIsReady &&
          orphanGapStrongPolicyIsReady,
        detail: !priceResponseHasRuntime
          ? t("Price-response configuration is unavailable")
          : !orphanGapPolicyHasRuntime
            ? t("Orphan-gap configuration is unavailable.")
          : !orphanGapNearPolicyHasRuntime
            ? t("Near-gap configuration is unavailable.")
          : !orphanGapStrongPolicyHasRuntime
            ? t("Strong-demand gap configuration is unavailable.")
          : priceResponseDirty
            ? t("Save the price-response safeguards")
          : orphanGapPolicyDirty
            ? t("Save orphan-gap safeguards")
          : orphanGapNearPolicyDirty
            ? t("Save near-gap safeguards")
          : orphanGapStrongPolicyDirty
            ? t("Save strong-demand safeguards")
          : priceResponseLocalErrors.length > 0 || priceResponseBackendIssues.length > 0
            ? t("Complete all five windows for every effective strategy")
          : orphanGapPolicyLocalErrors.length > 0 ||
              orphanGapPolicyBackendIssues.length > 0 ||
              orphanGapNearPolicyLocalErrors.length > 0 ||
              orphanGapNearPolicyBackendIssues.length > 0 ||
              orphanGapStrongPolicyLocalErrors.length > 0 ||
              orphanGapStrongPolicyBackendIssues.length > 0
            ? t("Resolve the orphan-gap safeguard issues")
          : !priceResponseIsApplicable &&
              orphanGapPolicy?.is_applicable !== true &&
              orphanGapNearPolicy?.is_applicable !== true &&
              orphanGapStrongPolicy?.is_applicable !== true
            ? t("No effective strategy requires response safeguards")
            : t("Response and orphan-gap safeguards are ready"),
      },
      disruptions: {
        ready: activeDisruptionsCount > 0,
        detail:
          activeDisruptionsCount > 0
            ? t(
                activeDisruptionsCount === 1
                  ? "{count} active disruption"
                  : "{count} active disruptions",
                { count: activeDisruptionsCount }
              )
            : t("Optional — no temporary disruptions"),
      },
    };

    return SETUP_STEP_CONFIG.map((step) => ({
      ...step,
      ...(statusByKey[step.key] || { ready: false, detail: "" }),
    }));
  }, [
    competitorScopeRows.length,
    categoryPriceLadder,
    categoryPriceLadderDirty,
    categoryPriceLadderIsApplicable,
    configuredMasterRateCategoryIds.size,
    mergedCompetitors,
    meta,
    occupancyPaceBackendIssues.length,
    occupancyPaceDirty,
    occupancyPaceDraft.profiles.length,
    occupancyPaceHasProfiles,
    occupancyPaceHasRuntime,
    occupancyPaceIsApplicable,
    occupancyPaceIsReady,
    occupancyPaceLocalErrors.length,
    orphanGapNearPolicy,
    orphanGapNearPolicyBackendIssues.length,
    orphanGapNearPolicyDirty,
    orphanGapNearPolicyHasRuntime,
    orphanGapNearPolicyIsReady,
    orphanGapNearPolicyLocalErrors.length,
    orphanGapPolicy,
    orphanGapPolicyBackendIssues.length,
    orphanGapPolicyDirty,
    orphanGapPolicyHasRuntime,
    orphanGapPolicyIsReady,
    orphanGapPolicyLocalErrors.length,
    orphanGapStrongPolicy,
    orphanGapStrongPolicyBackendIssues.length,
    orphanGapStrongPolicyDirty,
    orphanGapStrongPolicyHasRuntime,
    orphanGapStrongPolicyIsReady,
    orphanGapStrongPolicyLocalErrors.length,
    priceResponseBackendIssues.length,
    priceResponseDirty,
    priceResponseHasRuntime,
    priceResponseIsApplicable,
    priceResponseIsReady,
    priceResponseLocalErrors.length,
    quality,
    qualityDirty,
    resources,
    strategyGuardrailConflicts,
    t,
  ]);

  const essentialSetupSteps = setupSteps.filter((step) => !step.optional);
  const completedEssentialSteps = essentialSetupSteps.filter((step) => step.ready).length;
  const nextRecommendedStep = essentialSetupSteps.find((step) => !step.ready) || null;
  const activeSetupStepIndex = Math.max(
    setupSteps.findIndex((step) => step.key === activeSetupStep),
    0
  );
  const activeSetupStepData = setupSteps[activeSetupStepIndex] || setupSteps[0];
  const previousSetupStep = setupSteps[activeSetupStepIndex - 1] || null;
  const nextSetupStep = setupSteps[activeSetupStepIndex + 1] || null;

  const selectSetupStep = (stepKey) => {
    setActiveSetupStep(stepKey);
    window.requestAnimationFrame(() => {
      const reduceMotion = window.matchMedia?.("(prefers-reduced-motion: reduce)")?.matches;
      document
        .querySelector(".rmo-workspace > .rmo-contentPad")
        ?.scrollTo({ top: 0, behavior: reduceMotion ? "auto" : "smooth" });
    });
  };

  const strategyResource = useMemo(
    () => RESOURCE_CONFIG.find((resource) => resource.key === "strategy-settings") || null,
    []
  );
  const scopeTypeOptions = useMemo(
    () =>
      Array.isArray(meta?.scope_type_options) && meta.scope_type_options.length
        ? meta.scope_type_options
        : ["hotel", "room_category"],
    [meta]
  );
  const strategyModeOptions = useMemo(
    () =>
      Array.isArray(meta?.strategy_mode_options) && meta.strategy_mode_options.length
        ? meta.strategy_mode_options
        : ["occupancy", "adr", "balanced"],
    [meta]
  );
  const openEditor = (resource, item = null) => {
    const payload = item ? stripAssociations(item) : resource.template || {};
    setEditor({ resource, item });
    setEditorError("");
    if (resource.key === "strategy-settings") {
      setStrategyDraft(normalizeStrategyDraft(payload));
      setEditorText("");
      return;
    }
    setStrategyDraft(null);
    setEditorText(JSON.stringify(payload, null, 2));
  };

  const closeEditor = () => {
    setEditor(null);
    setStrategyDraft(null);
    setEditorText("");
    setEditorError("");
    setSaving(false);
  };

  const updateStrategyField = (key, value) => {
    setStrategyDraft((previous) => ({
      ...(previous || normalizeStrategyDraft(strategyResource?.template || {})),
      [key]: value,
    }));
  };

  const updateRatePolicyDraft = (roomCategoryId, masterRoomRateId) => {
    setError("");
    setRatePolicyDrafts((previous) => ({
      ...previous,
      [String(roomCategoryId)]: masterRoomRateId,
    }));
  };

  const saveRateProductPolicy = async (roomCategory) => {
    const roomCategoryId = Number(roomCategory?.id);
    const masterRoomRateId = Number(ratePolicyDrafts[String(roomCategoryId)]);
    if (!roomCategoryId || !masterRoomRateId) {
      setError(
        t("Choose a master room rate for {category}.", {
          category: roomCategory?.name || t("this room category"),
        })
      );
      return;
    }

    const selectedMasterRate = eligibleMasterRoomRateById.get(masterRoomRateId);
    if (!selectedMasterRate || Number(selectedMasterRate.room_category_id) !== roomCategoryId) {
      setError(t("The selected master rate is not valid for this room category."));
      return;
    }

    const existingPolicy = rateProductPolicyByCategory.get(roomCategoryId) || null;
    const payload = {
      room_category_id: roomCategoryId,
      master_room_rate_id: masterRoomRateId,
      is_active: true,
    };

    try {
      setSavingRatePolicyCategoryId(roomCategoryId);
      setError("");
      const savedPolicy = existingPolicy
        ? await updateRmsConfigResource(
            "rate-product-policies",
            existingPolicy.id,
            payload
          )
        : await createRmsConfigResource("rate-product-policies", payload);

      setResources((previous) => {
        const rows = previous["rate-product-policies"] || [];
        const nextRows = existingPolicy
          ? rows.map((item) =>
              Number(item.id) === Number(existingPolicy.id) ? savedPolicy : item
            )
          : [...rows, savedPolicy];
        return { ...previous, "rate-product-policies": nextRows };
      });
      await loadAll();
    } catch (nextError) {
      setError(
        extractErrorMessage(nextError, t("Could not save the master room rate policy."))
      );
    } finally {
      setSavingRatePolicyCategoryId(null);
    }
  };

  const updateCategoryPriceLadderItem = (roomCategoryId, field, value) => {
    setCategoryPriceLadderDirty(true);
    setCategoryPriceLadderError("");
    setCategoryPriceLadderDraft((previous) => ({
      ...previous,
      items: (previous.items || []).map((item) => {
        if (Number(item.room_category_id) !== Number(roomCategoryId)) return item;
        if (field !== "mode") return { ...item, [field]: value };

        if (value === "root") {
          return {
            ...item,
            mode: value,
            lower_room_category_id: "",
            min_gap_amount: "0",
            exception_reason: "",
          };
        }
        if (value === "excluded") {
          return {
            ...item,
            mode: value,
            lower_room_category_id: "",
            min_gap_amount: "0",
          };
        }
        if (value === "allow_inversion") {
          return {
            ...item,
            mode: value,
            min_gap_amount: "0",
          };
        }
        if (value === "enforce") {
          return {
            ...item,
            mode: value,
            min_gap_amount:
              String(item.min_gap_amount ?? "").trim() === ""
                ? "0"
                : item.min_gap_amount,
            exception_reason: "",
          };
        }
        return {
          ...item,
          mode: "",
          lower_room_category_id: "",
          min_gap_amount: "",
          exception_reason: "",
        };
      }),
    }));
  };

  const saveCategoryPriceLadder = async () => {
    if (
      categoryPriceLadderIsApplicable &&
      !categoryPriceLadderCategoryOptions.length
    ) {
      setCategoryPriceLadderError(t("No hotel room categories found"));
      return;
    }
    if (categoryPriceLadderLocalErrors.length) {
      setCategoryPriceLadderError(categoryPriceLadderLocalErrors.join(" "));
      return;
    }

    try {
      setSavingCategoryPriceLadder(true);
      setCategoryPriceLadderError("");
      const result = await saveRmsCategoryPriceLadder(
        buildCategoryPriceLadderPayload(categoryPriceLadderDraft, {
          isApplicable: categoryPriceLadderIsApplicable,
        })
      );
      const savedLadder =
        result?.category_price_ladder &&
        typeof result.category_price_ladder === "object" &&
        !Array.isArray(result.category_price_ladder)
          ? result.category_price_ladder
          : Array.isArray(result?.items)
            ? result
            : null;

      if (savedLadder) {
        setMeta((previous) => ({
          ...(previous || {}),
          category_price_ladder: savedLadder,
        }));
        setCategoryPriceLadderDraft(
          normalizeCategoryPriceLadderDraft(savedLadder, roomCategoryOptions)
        );
        setCategoryPriceLadderDirty(false);
      } else {
        await loadAll();
      }
    } catch (nextError) {
      const backendIssues = Array.isArray(nextError?.response?.data?.details?.issues)
        ? nextError.response.data.details.issues.map((issue) =>
            formatCategoryPriceLadderIssue(issue, roomCategoryOptions, t)
          )
        : [];
      const message = extractErrorMessage(
        nextError,
        t("Could not save the category price ladder.")
      );
      setCategoryPriceLadderError(
        [...new Set([message, ...backendIssues].filter(Boolean))].join(" ")
      );
    } finally {
      setSavingCategoryPriceLadder(false);
    }
  };

  const updateOccupancyPaceItem = (profileId, leadTimeBucket, field, value) => {
    setOccupancyPaceDirty(true);
    setOccupancyPaceError("");
    setOccupancyPaceDraft((previous) => ({
      ...previous,
      profiles: (previous.profiles || []).map((profile) =>
        String(profile.seasonality_profile_id) !== String(profileId)
          ? profile
          : {
              ...profile,
              items: (profile.items || []).map((item) =>
                item.lead_time_bucket !== leadTimeBucket
                  ? item
                  : { ...item, [field]: value }
              ),
            }
      ),
    }));
  };

  const saveOccupancyPace = async () => {
    if (!occupancyPaceCanConfigure) {
      setOccupancyPaceError(
        t("There are no active seasonality profiles to configure.")
      );
      return;
    }
    if (occupancyPaceLocalErrors.length) {
      setOccupancyPaceError(occupancyPaceLocalErrors.join(" "));
      return;
    }

    try {
      setSavingOccupancyPace(true);
      setOccupancyPaceError("");
      const savedOccupancyPace = await saveRmsOccupancyPace(
        buildOccupancyPacePayload(occupancyPaceDraft)
      );
      if (savedOccupancyPace) {
        setOccupancyPace(savedOccupancyPace);
        setOccupancyPaceDraft(
          normalizeOccupancyPaceDraft(savedOccupancyPace)
        );
        setOccupancyPaceDirty(false);
        setMeta((previous) => ({
          ...(previous || {}),
          occupancy_pace: savedOccupancyPace,
        }));
      } else {
        await loadAll();
      }
    } catch (nextError) {
      const backendIssues = Array.isArray(nextError?.response?.data?.details?.issues)
        ? nextError.response.data.details.issues.map((issue) =>
            formatOccupancyPaceIssue(issue, t)
          )
        : [];
      const message = extractErrorMessage(
        nextError,
        t("Could not save the occupancy pace curve.")
      );
      setOccupancyPaceError(
        [...new Set([message, ...backendIssues].filter(Boolean))].join(" ")
      );
    } finally {
      setSavingOccupancyPace(false);
    }
  };

  const updatePriceResponseItem = (strategySettingId, leadTimeBucket, field, value) => {
    setPriceResponseDirty(true);
    setPriceResponseError("");
    setPriceResponseDraft((previous) => ({
      ...previous,
      strategies: (previous.strategies || []).map((strategy) =>
        Number(strategy.strategy_setting_id) !== Number(strategySettingId)
          ? strategy
          : {
              ...strategy,
              items: (strategy.items || []).map((item) => {
                if (item.lead_time_bucket !== leadTimeBucket) return item;
                if (field !== "low_confidence_action") return { ...item, [field]: value };
                return {
                  ...item,
                  low_confidence_action: value,
                  low_confidence_max_delta_pct:
                    value === "limit"
                      ? item.low_confidence_max_delta_pct || "1"
                      : "",
                };
              }),
            }
      ),
    }));
  };

  const updateOrphanGapPolicyItem = (strategySettingId, field, value) => {
    setOrphanGapPolicyDirty(true);
    setOrphanGapPolicyError("");
    setOrphanGapPolicyDraft((previous) =>
      updateOrphanGapPolicyDraftItem(previous, strategySettingId, field, value)
    );
  };

  const saveOrphanGapPolicy = async () => {
    if (!orphanGapPolicyCanConfigure) {
      setOrphanGapPolicyError(t("There are no strategies to configure."));
      return;
    }
    if (orphanGapPolicyLocalErrors.length) {
      setOrphanGapPolicyError(
        orphanGapPolicyLocalErrors.map((message) => t(message)).join(" ")
      );
      return;
    }
    try {
      setSavingOrphanGapPolicy(true);
      setOrphanGapPolicyError("");
      const savedPolicy = await saveRmsOrphanGapPolicy(
        buildOrphanGapPolicyPayload(orphanGapPolicyDraft)
      );
      if (savedPolicy) {
        setOrphanGapPolicy(savedPolicy);
        setOrphanGapPolicyDraft(
          normalizeOrphanGapPolicyDraft(
            savedPolicy,
            resources["strategy-settings"] || []
          )
        );
        setOrphanGapPolicyDirty(false);
        setMeta((previous) => ({
          ...(previous || {}),
          orphan_gap_policy: savedPolicy,
        }));
      } else {
        await loadAll();
      }
    } catch (nextError) {
      const backendIssues = Array.isArray(nextError?.response?.data?.details?.issues)
        ? nextError.response.data.details.issues.map((issue) =>
            formatPriceResponseIssue(issue, t)
          )
        : [];
      const message = extractErrorMessage(
        nextError,
        t("Could not save orphan-gap safeguards.")
      );
      setOrphanGapPolicyError(
        [...new Set([message, ...backendIssues].filter(Boolean))].join(" ")
      );
    } finally {
      setSavingOrphanGapPolicy(false);
    }
  };

  const updateOrphanGapNearPolicyItem = (strategySettingId, field, value) => {
    setOrphanGapNearPolicyDirty(true);
    setOrphanGapNearPolicyError("");
    setOrphanGapNearPolicyDraft((previous) =>
      updateOrphanGapNearPolicyDraftItem(
        previous,
        strategySettingId,
        field,
        value
      )
    );
  };

  const saveOrphanGapNearPolicy = async () => {
    if (!orphanGapNearPolicyCanConfigure) {
      setOrphanGapNearPolicyError(t("There are no strategies to configure."));
      return;
    }
    if (orphanGapNearPolicyLocalErrors.length) {
      setOrphanGapNearPolicyError(
        orphanGapNearPolicyLocalErrors.map((message) => t(message)).join(" ")
      );
      return;
    }
    try {
      setSavingOrphanGapNearPolicy(true);
      setOrphanGapNearPolicyError("");
      const savedPolicy = await saveRmsOrphanGapNearPolicy(
        buildOrphanGapNearPolicyPayload(orphanGapNearPolicyDraft)
      );
      if (savedPolicy) {
        setOrphanGapNearPolicy(savedPolicy);
        setOrphanGapNearPolicyDraft(
          normalizeOrphanGapNearPolicyDraft(
            savedPolicy,
            resources["strategy-settings"] || []
          )
        );
        setOrphanGapNearPolicyDirty(false);
        setMeta((previous) => ({
          ...(previous || {}),
          orphan_gap_near_policy: savedPolicy,
        }));
      } else {
        await loadAll();
      }
    } catch (nextError) {
      const backendIssues = Array.isArray(nextError?.response?.data?.details?.issues)
        ? nextError.response.data.details.issues.map((issue) =>
            formatPriceResponseIssue(issue, t)
          )
        : [];
      const message = extractErrorMessage(
        nextError,
        t("Could not save near-gap safeguards.")
      );
      setOrphanGapNearPolicyError(
        [...new Set([message, ...backendIssues].filter(Boolean))].join(" ")
      );
    } finally {
      setSavingOrphanGapNearPolicy(false);
    }
  };

  const updateOrphanGapStrongPolicyItem = (strategySettingId, field, value) => {
    setOrphanGapStrongPolicyDirty(true);
    setOrphanGapStrongPolicyError("");
    setOrphanGapStrongPolicyDraft((previous) =>
      updateOrphanGapStrongPolicyDraftItem(
        previous,
        strategySettingId,
        field,
        value
      )
    );
  };

  const saveOrphanGapStrongPolicy = async () => {
    if (!orphanGapStrongPolicyCanConfigure) {
      setOrphanGapStrongPolicyError(t("There are no strategies to configure."));
      return;
    }
    if (orphanGapStrongPolicyLocalErrors.length) {
      setOrphanGapStrongPolicyError(
        orphanGapStrongPolicyLocalErrors.map((message) => t(message)).join(" ")
      );
      return;
    }
    try {
      setSavingOrphanGapStrongPolicy(true);
      setOrphanGapStrongPolicyError("");
      const savedPolicy = await saveRmsOrphanGapStrongPolicy(
        buildOrphanGapStrongPolicyPayload(orphanGapStrongPolicyDraft)
      );
      if (savedPolicy) {
        setOrphanGapStrongPolicy(savedPolicy);
        setOrphanGapStrongPolicyDraft(
          normalizeOrphanGapStrongPolicyDraft(
            savedPolicy,
            resources["strategy-settings"] || []
          )
        );
        setOrphanGapStrongPolicyDirty(false);
        setMeta((previous) => ({
          ...(previous || {}),
          orphan_gap_strong_policy: savedPolicy,
        }));
      } else {
        await loadAll();
      }
    } catch (nextError) {
      const backendIssues = Array.isArray(nextError?.response?.data?.details?.issues)
        ? nextError.response.data.details.issues.map((issue) =>
            formatPriceResponseIssue(issue, t)
          )
        : [];
      const message = extractErrorMessage(
        nextError,
        t("Could not save strong-demand gap safeguards.")
      );
      setOrphanGapStrongPolicyError(
        [...new Set([message, ...backendIssues].filter(Boolean))].join(" ")
      );
    } finally {
      setSavingOrphanGapStrongPolicy(false);
    }
  };

  const updateMarketBenchmarkPolicy = (field, value) => {
    setMarketBenchmarkPolicyDirty(true);
    setMarketBenchmarkPolicyError("");
    setMarketBenchmarkPolicyDraft((previous) =>
      updateMarketBenchmarkPolicyDraft(previous, field, value)
    );
  };

  const saveMarketBenchmarkPolicy = async () => {
    const issues = validateMarketBenchmarkPolicyDraft(
      marketBenchmarkPolicyDraft
    );
    if (issues.length) {
      setMarketBenchmarkPolicyError(issues.map((issue) => t(issue)).join(" "));
      return;
    }
    try {
      setSavingMarketBenchmarkPolicy(true);
      setMarketBenchmarkPolicyError("");
      const savedPolicy = await saveRmsMarketBenchmarkPolicy(
        buildMarketBenchmarkPolicyPayload(marketBenchmarkPolicyDraft)
      );
      if (savedPolicy) {
        setMarketBenchmarkPolicy(savedPolicy);
        setMarketBenchmarkPolicyDraft(
          normalizeMarketBenchmarkPolicyDraft(savedPolicy)
        );
        setMarketBenchmarkPolicyDirty(false);
      } else {
        await loadAll();
      }
    } catch (nextError) {
      setMarketBenchmarkPolicyError(
        extractErrorMessage(
          nextError,
          t("Could not save market benchmark quality settings.")
        )
      );
    } finally {
      setSavingMarketBenchmarkPolicy(false);
    }
  };

  const savePriceResponse = async () => {
    if (!priceResponseCanConfigure) {
      setPriceResponseError(t("There are no effective strategies to configure."));
      return;
    }
    if (priceResponseLocalErrors.length) {
      setPriceResponseError(priceResponseLocalErrors.join(" "));
      return;
    }
    try {
      setSavingPriceResponse(true);
      setPriceResponseError("");
      const savedPriceResponse = await saveRmsPriceResponse(
        buildPriceResponsePayload(priceResponseDraft)
      );
      if (savedPriceResponse) {
        setPriceResponse(savedPriceResponse);
        setPriceResponseDraft(
          normalizePriceResponseDraft(
            savedPriceResponse,
            resources["strategy-settings"] || []
          )
        );
        setPriceResponseDirty(false);
        setMeta((previous) => ({
          ...(previous || {}),
          price_response: savedPriceResponse,
        }));
      } else {
        await loadAll();
      }
    } catch (nextError) {
      const backendIssues = Array.isArray(nextError?.response?.data?.details?.issues)
        ? nextError.response.data.details.issues.map((issue) =>
            formatPriceResponseIssue(issue, t)
          )
        : [];
      const message = extractErrorMessage(
        nextError,
        t("Could not save the price-response safeguards.")
      );
      setPriceResponseError(
        [...new Set([message, ...backendIssues].filter(Boolean))].join(" ")
      );
    } finally {
      setSavingPriceResponse(false);
    }
  };

  const openCompetitorEditor = (item) => {
    setCompetitorEditor(item);
    setCompetitorDraft(normalizeCompetitorDraft(item));
    setCompetitorEditorError("");
  };

  const closeCompetitorEditor = () => {
    setCompetitorEditor(null);
    setCompetitorDraft(null);
    setCompetitorEditorError("");
  };

  const updateCompetitorField = (key, value) => {
    setCompetitorDraft((previous) => ({
      ...(previous || normalizeCompetitorDraft(competitorEditor || {})),
      [key]: value,
    }));
  };

  const openSeasonalityTemplateEditor = (item = null) => {
    setSeasonalityTemplateEditor(item);
    setSeasonalityTemplateDraft(normalizeSeasonalityTemplateDraft(item || {}));
    setSeasonalityTemplateEditorError("");
  };

  const closeSeasonalityTemplateEditor = () => {
    setSeasonalityTemplateEditor(null);
    setSeasonalityTemplateDraft(null);
    setSeasonalityTemplateEditorError("");
  };

  const openSeasonalityPeriodEditor = (item = null) => {
    const nextDraft = normalizeSeasonalityPeriodDraft(item || {});
    if (!item && !nextDraft.seasonality_template_id && seasonalityTemplateOptions.length) {
      nextDraft.seasonality_template_id = String(seasonalityTemplateOptions[0].id);
    }
    setSeasonalityPeriodEditor(item);
    setSeasonalityPeriodDraft(nextDraft);
    setSeasonalityPeriodEditorError("");
  };

  const closeSeasonalityPeriodEditor = () => {
    setSeasonalityPeriodEditor(null);
    setSeasonalityPeriodDraft(null);
    setSeasonalityPeriodEditorError("");
  };

  const updateSeasonalityTemplateField = (key, value) => {
    setSeasonalityTemplateDraft((previous) => ({
      ...(previous || normalizeSeasonalityTemplateDraft(seasonalityTemplateEditor || {})),
      [key]: value,
    }));
  };

  const updateSeasonalityPeriodField = (key, value) => {
    setSeasonalityPeriodDraft((previous) => ({
      ...(previous || normalizeSeasonalityPeriodDraft(seasonalityPeriodEditor || {})),
      [key]: value,
    }));
  };

  const openSeasonalityProfileEditor = (item = null) => {
    const nextDraft = normalizeSeasonalityProfileDraft(item || {});
    if (!item && !nextDraft.seasonality_template_id && seasonalityTemplateOptions.length) {
      nextDraft.seasonality_template_id = String(seasonalityTemplateOptions[0].id);
    }
    setSeasonalityProfileEditor(item);
    setSeasonalityProfileDraft(nextDraft);
    setSeasonalityProfileEditorError("");
  };

  const closeSeasonalityProfileEditor = () => {
    setSeasonalityProfileEditor(null);
    setSeasonalityProfileDraft(null);
    setSeasonalityProfileEditorError("");
  };

  const updateSeasonalityProfileField = (key, value) => {
    setSeasonalityProfileDraft((previous) => ({
      ...(previous || normalizeSeasonalityProfileDraft(seasonalityProfileEditor || {})),
      [key]: value,
    }));
  };

  const openMarketDisruptionEditor = (item = null) => {
    setMarketDisruptionEditor(item);
    setMarketDisruptionDraft(normalizeMarketDisruptionDraft(item || {}));
    setMarketDisruptionEditorError("");
  };

  const closeMarketDisruptionEditor = () => {
    setMarketDisruptionEditor(null);
    setMarketDisruptionDraft(null);
    setMarketDisruptionEditorError("");
  };

  const updateMarketDisruptionField = (key, value) => {
    setMarketDisruptionDraft((previous) => ({
      ...(previous || normalizeMarketDisruptionDraft(marketDisruptionEditor || {})),
      [key]: value,
    }));
  };

  const saveSeasonalityTemplate = async () => {
    if (!seasonalityTemplateDraft) return;

    const name = String(seasonalityTemplateDraft.name || "").trim();
    const customDemandLevel = String(
      seasonalityTemplateDraft.demand_level_custom || ""
    ).trim();
    const priceIndex =
      seasonalityTemplateDraft.default_price_index === ""
        ? null
        : Number(seasonalityTemplateDraft.default_price_index);

    if (!name) {
      setSeasonalityTemplateEditorError("Template name is required.");
      return;
    }
    if (
      seasonalityTemplateDraft.demand_level_choice === "custom" &&
      !customDemandLevel
    ) {
      setSeasonalityTemplateEditorError(
        "Write a custom demand label or pick one of the suggested demand levels."
      );
      return;
    }
    if (priceIndex != null && (!Number.isFinite(priceIndex) || priceIndex <= 0)) {
      setSeasonalityTemplateEditorError(
        "Default price position must be a number greater than 0."
      );
      return;
    }

    try {
      setSaving(true);
      setSeasonalityTemplateEditorError("");
      const payload = buildSeasonalityTemplatePayload(seasonalityTemplateDraft);
      if (seasonalityTemplateEditor?.id) {
        await updateRmsConfigResource(
          "seasonality-templates",
          seasonalityTemplateEditor.id,
          payload
        );
      } else {
        await createRmsConfigResource("seasonality-templates", payload);
      }
      closeSeasonalityTemplateEditor();
      await loadAll();
    } catch (nextError) {
      setSeasonalityTemplateEditorError(
        extractErrorMessage(nextError, t("Could not save the seasonality template."))
      );
    } finally {
      setSaving(false);
    }
  };

  const saveSeasonalityPeriod = async () => {
    if (!seasonalityPeriodDraft) return;

    const selectedTemplateId = String(
      seasonalityPeriodDraft.seasonality_template_id || ""
    ).trim();
    const startDate = String(seasonalityPeriodDraft.start_date || "").trim();
    const endDate = String(seasonalityPeriodDraft.end_date || "").trim();
    const priorityValue = Number(seasonalityPeriodDraft.priority_value);

    if (!selectedTemplateId) {
      setSeasonalityPeriodEditorError("Pick the season template this date range belongs to.");
      return;
    }
    if (!startDate || !endDate) {
      setSeasonalityPeriodEditorError("Choose both the start date and the end date.");
      return;
    }
    if (startDate > endDate) {
      setSeasonalityPeriodEditorError("The start date cannot be after the end date.");
      return;
    }
    if (!Number.isInteger(priorityValue) || priorityValue <= 0) {
      setSeasonalityPeriodEditorError("Priority must be a whole number greater than 0.");
      return;
    }

    try {
      setSaving(true);
      setSeasonalityPeriodEditorError("");
      const payload = buildSeasonalityPeriodPayload(seasonalityPeriodDraft);
      if (seasonalityPeriodEditor?.id) {
        await updateRmsConfigResource(
          "seasonality-periods",
          seasonalityPeriodEditor.id,
          payload
        );
      } else {
        await createRmsConfigResource("seasonality-periods", payload);
      }
      closeSeasonalityPeriodEditor();
      await loadAll();
    } catch (nextError) {
      setSeasonalityPeriodEditorError(
        extractErrorMessage(nextError, t("Could not save the seasonality period."))
      );
    } finally {
      setSaving(false);
    }
  };

  const saveSeasonalityProfile = async () => {
    if (!seasonalityProfileDraft) return;

    const selectedTemplateId = String(
      seasonalityProfileDraft.seasonality_template_id || ""
    ).trim();
    const customDemandLevel = String(
      seasonalityProfileDraft.demand_level_custom || ""
    ).trim();
    const expectedPriceIndex =
      seasonalityProfileDraft.expected_price_index === ""
        ? null
        : Number(seasonalityProfileDraft.expected_price_index);
    const expectedOccupancyPct =
      seasonalityProfileDraft.expected_occupancy_pct === ""
        ? null
        : Number(seasonalityProfileDraft.expected_occupancy_pct);

    if (!selectedTemplateId) {
      setSeasonalityProfileEditorError("Pick the season this profile should refine.");
      return;
    }
    if (!String(seasonalityProfileDraft.room_category_id || "").trim()) {
      setSeasonalityProfileEditorError(
        "Pick the room category this seasonal refinement belongs to."
      );
      return;
    }
    if (
      seasonalityProfileDraft.demand_level_choice === "custom" &&
      !customDemandLevel
    ) {
      setSeasonalityProfileEditorError(
        "Write a custom demand tone or choose one of the suggested demand labels."
      );
      return;
    }
    if (
      expectedPriceIndex != null &&
      (!Number.isFinite(expectedPriceIndex) || expectedPriceIndex <= 0)
    ) {
      setSeasonalityProfileEditorError(
        "Price index must be a number greater than 0."
      );
      return;
    }
    if (
      expectedOccupancyPct != null &&
      (!Number.isFinite(expectedOccupancyPct) ||
        expectedOccupancyPct < 0 ||
        expectedOccupancyPct > 100)
    ) {
      setSeasonalityProfileEditorError(
        "Expected occupancy must stay between 0 and 100."
      );
      return;
    }

    try {
      setSaving(true);
      setSeasonalityProfileEditorError("");
      const payload = buildSeasonalityProfilePayload(seasonalityProfileDraft);
      if (seasonalityProfileEditor?.id) {
        await updateRmsConfigResource(
          "seasonality-profiles",
          seasonalityProfileEditor.id,
          payload
        );
      } else {
        await createRmsConfigResource("seasonality-profiles", payload);
      }
      closeSeasonalityProfileEditor();
      await loadAll();
    } catch (nextError) {
      setSeasonalityProfileEditorError(
        extractErrorMessage(nextError, t("Could not save the seasonality profile."))
      );
    } finally {
      setSaving(false);
    }
  };

  const saveMarketDisruption = async () => {
    if (!marketDisruptionDraft) return;

    const name = String(marketDisruptionDraft.name || "").trim();
    const startDate = String(marketDisruptionDraft.start_date || "").trim();
    const endDate = String(marketDisruptionDraft.end_date || "").trim();
    const confidenceValue =
      marketDisruptionDraft.confidence === "" ? null : Number(marketDisruptionDraft.confidence);

    if (!name) {
      setMarketDisruptionEditorError("Give this disruption a clear name.");
      return;
    }
    if (!startDate || !endDate) {
      setMarketDisruptionEditorError("Choose both the start date and the end date.");
      return;
    }
    if (startDate > endDate) {
      setMarketDisruptionEditorError("The start date cannot be after the end date.");
      return;
    }
    if (!marketDisruptionDraft.impact_direction) {
      setMarketDisruptionEditorError("Choose whether this disruption pushes price up or down.");
      return;
    }
    if (!marketDisruptionDraft.impact_level) {
      setMarketDisruptionEditorError("Choose how strong the disruption effect should be.");
      return;
    }
    if (
      confidenceValue != null &&
      (!Number.isFinite(confidenceValue) || confidenceValue < 0 || confidenceValue > 100)
    ) {
      setMarketDisruptionEditorError("Confidence must stay between 0 and 100.");
      return;
    }

    try {
      setSaving(true);
      setMarketDisruptionEditorError("");
      const payload = buildMarketDisruptionPayload(marketDisruptionDraft);
      if (marketDisruptionEditor?.id) {
        await updateRmsConfigResource(
          "market-disruptions",
          marketDisruptionEditor.id,
          payload
        );
      } else {
        await createRmsConfigResource("market-disruptions", payload);
      }
      closeMarketDisruptionEditor();
      await loadAll();
    } catch (nextError) {
      setMarketDisruptionEditorError(
        extractErrorMessage(nextError, t("Could not save the market disruption."))
      );
    } finally {
      setSaving(false);
    }
  };

  const saveCompetitor = async () => {
    if (!competitorDraft) return;

    const hotelCode = Number(competitorDraft.hotel_code);
    const competitorWeight = Number(competitorDraft.competitor_weight);
    const rating = competitorDraft.rating === "" ? null : Number(competitorDraft.rating);
    const reviewCount =
      competitorDraft.review_count === "" ? null : Number(competitorDraft.review_count);
    const distanceKm =
      competitorDraft.distance_km === "" ? null : Number(competitorDraft.distance_km);

    if (!hotelCode || Number.isNaN(hotelCode)) {
      setCompetitorEditorError("This competitor is missing a valid hotel code.");
      return;
    }
    if (!Number.isFinite(competitorWeight) || competitorWeight <= 0) {
      setCompetitorEditorError("Importance must be a number greater than 0.");
      return;
    }
    if (rating != null && (!Number.isFinite(rating) || rating < 0 || rating > 5)) {
      setCompetitorEditorError("Public rating must stay between 0 and 5.");
      return;
    }
    if (reviewCount != null && (!Number.isFinite(reviewCount) || reviewCount < 0)) {
      setCompetitorEditorError("Number of reviews cannot be negative.");
      return;
    }
    if (distanceKm != null && (!Number.isFinite(distanceKm) || distanceKm < 0)) {
      setCompetitorEditorError("Distance cannot be negative.");
      return;
    }

    try {
      setSaving(true);
      setCompetitorEditorError("");
      const payload = buildCompetitorPayload(competitorDraft);
      if (competitorDraft.id) {
        await updateRmsConfigResource("competitors", competitorDraft.id, payload);
      } else {
        await createRmsConfigResource("competitors", payload);
      }
      closeCompetitorEditor();
      await loadAll();
    } catch (nextError) {
      setCompetitorEditorError(
        extractErrorMessage(nextError, t("Could not save competitor settings."))
      );
    } finally {
      setSaving(false);
    }
  };

  const saveEditor = async () => {
    if (!editor?.resource) return;
    try {
      setSaving(true);
      setEditorError("");
      let payload;
      if (editor.resource.key === "strategy-settings") {
        if (!strategyDraft) {
          setEditorError("Strategy data is missing.");
          setSaving(false);
          return;
        }
        if (strategyDraft.scope_type === "room_category" && !String(strategyDraft.scope_id || "").trim()) {
          setEditorError("Pick a room category when the rule applies to one room category.");
          setSaving(false);
          return;
        }
        const minStayMin = Number(strategyDraft.min_stay_min_allowed);
        const minStayMax = Number(strategyDraft.min_stay_max_allowed);
        const targetOccupancyText = String(
          strategyDraft.target_occupancy_pct ?? ""
        ).trim();
        const targetOccupancy = Number(targetOccupancyText);
        if (
          targetOccupancyText &&
          (!Number.isFinite(targetOccupancy) ||
            targetOccupancy < 0 ||
            targetOccupancy > 100)
        ) {
          setEditorError("Target occupancy must stay between 0 and 100%.");
          setSaving(false);
          return;
        }
        if (
          Number.isFinite(minStayMin) &&
          Number.isFinite(minStayMax) &&
          minStayMin > minStayMax
        ) {
          setEditorError("Minimum stay floor cannot be higher than minimum stay ceiling.");
          setSaving(false);
          return;
        }
        const strategyGuardrailConflict = findStrategyDraftGuardrailConflict({
          draft: strategyDraft,
          editedStrategyId: editor.item?.id ?? null,
          strategies: resources["strategy-settings"] || [],
          roomCategories: roomCategoryOptions,
          policyByCategory: rateProductPolicyByCategory,
          masterRateById: eligibleMasterRoomRateById,
        });
        if (strategyGuardrailConflict) {
          setEditorError(
            formatStrategyGuardrailConflict(
              strategyGuardrailConflict,
              roomCategoryOptions,
              t
            )
          );
          setSaving(false);
          return;
        }
        payload = buildStrategyPayload(strategyDraft);
      } else {
        payload = JSON.parse(editorText);
      }
      if (editor.item) {
        await updateRmsConfigResource(editor.resource.key, editor.item.id, payload);
      } else {
        await createRmsConfigResource(editor.resource.key, payload);
      }
      closeEditor();
      await loadAll();
    } catch (nextError) {
      setEditorError(extractErrorMessage(nextError, t("Could not save RMS resource.")));
      setSaving(false);
    }
  };

  const saveQuality = async () => {
    try {
      setSaving(true);
      await saveRmsQualitySettings(quality);
      await loadAll();
    } catch (nextError) {
      setError(extractErrorMessage(nextError, t("Could not save RMS quality settings.")));
    } finally {
      setSaving(false);
    }
  };

  const removeItem = async (resource, item) => {
    if (
      !window.confirm(
        t("Delete row #{id} from {title}?", {
          id: item.id,
          title: t(resource.title),
        })
      )
    )
      return;
    try {
      await deleteRmsConfigResource(resource.key, item.id);
      await loadAll();
    } catch (nextError) {
      setError(extractErrorMessage(nextError, t("Could not delete RMS resource.")));
    }
  };

  const renderResourceCell = (resource, column, item) => {
    const value = getValueByPath(item, column);

    if (column.endsWith("is_active") || column === "is_active") {
      return renderLocalizedStatusPill(value, t);
    }

    if (resource.key === "strategy-settings") {
      if (column === "scope_type") {
        return t(getOptionMeta(SCOPE_TYPE_META, value, String(value || "-")).label);
      }
      if (column === "strategy_mode") {
        return t(getOptionMeta(STRATEGY_MODE_META, value, String(value || "-")).label);
      }
      if (column === "detailed_horizon_days") {
        return value ? `${value} ${t("days")}` : "-";
      }
      if (column === "long_range_mode") {
        const match = LONG_RANGE_MODE_OPTIONS.find((option) => option.value === value);
        return match?.label ? t(match.label) : renderCellContent(value);
      }
      if (column === "scope_room_category.name" && item.scope_type === "hotel") {
        return t("Whole hotel");
      }
    }

    if (resource.key === "seasonality-templates") {
      if (column === "default_demand_level") {
        return t(
          getOptionMeta(
          SEASONALITY_TEMPLATE_DEMAND_META,
          value,
          value ? String(value) : "Not set"
          ).label
        );
      }
      if (column === "default_price_index") {
        return value ? `${value} ${t("index")}` : "-";
      }
    }

    if (resource.key === "seasonality-periods") {
      if (column === "start_date" || column === "end_date") {
        return formatLocalizedSeasonDateLabel(value, locale, t);
      }
      if (column === "priority") {
        const priorityMeta = getOptionMeta(
          SEASONALITY_PRIORITY_META,
          String(value || ""),
          value ? `Priority ${value}` : "Not set"
        );
        return value ? `${t(priorityMeta.label)} (${value})` : t(priorityMeta.label);
      }
    }

    if (resource.key === "seasonality-profiles") {
      if (column === "room_category.name") {
        return value ? renderCellContent(value) : t("All room categories");
      }
      if (column === "dow") {
        return t(getOptionMeta(SEASONALITY_DOW_META, String(value ?? ""), "Every day").label);
      }
      if (column === "expected_demand_level") {
        return t(
          getOptionMeta(
          SEASONALITY_TEMPLATE_DEMAND_META,
          value,
          value ? String(value) : "Template default"
          ).label
        );
      }
      if (column === "expected_price_index") {
        return value ? `${value} ${t("index")}` : t("Template default");
      }
      if (column === "expected_occupancy_pct") {
        if (value == null || value === "") return "-";
        const numeric = Number(value);
        return Number.isFinite(numeric) && numeric >= 0 && numeric <= 1
          ? `${Number((numeric * 100).toFixed(2))}%`
          : `${value}%`;
      }
    }

    if (resource.key === "market-disruptions") {
      if (column === "disruption_type") {
        return t(
          getOptionMeta(
          MARKET_DISRUPTION_TYPE_META,
          value,
          value ? String(value) : "Not set"
          ).label
        );
      }
      if (column === "impact_direction") {
        return t(
          getOptionMeta(
          MARKET_DISRUPTION_DIRECTION_META,
          value,
          value ? String(value) : "Not set"
          ).label
        );
      }
      if (column === "impact_level") {
        return t(
          getOptionMeta(
          MARKET_DISRUPTION_LEVEL_META,
          value,
          value ? String(value) : "Not set"
          ).label
        );
      }
      if (column === "start_date" || column === "end_date") {
        return formatLocalizedSeasonDateLabel(value, locale, t);
      }
      if (column === "confidence") {
        if (value == null || value === "") return t("Full confidence");
        const numeric = Number(value);
        return Number.isFinite(numeric) && numeric >= 0 && numeric <= 1
          ? `${Number((numeric * 100).toFixed(0))}%`
          : `${value}%`;
      }
    }

    return renderCellContent(value);
  };

  const strategyModeMeta = getOptionMeta(
    STRATEGY_MODE_META,
    strategyDraft?.strategy_mode,
    t("Balanced")
  );
  const scopeTypeMeta = getOptionMeta(
    SCOPE_TYPE_META,
    strategyDraft?.scope_type,
    t("Whole hotel")
  );
  const longRangeModeMeta = getOptionMeta(
    LONG_RANGE_MODE_META,
    strategyDraft?.long_range_mode,
    t("Use season blocks")
  );
  const longRangeBlockTypeMeta = getOptionMeta(
    LONG_RANGE_BLOCK_TYPE_META,
    strategyDraft?.long_range_block_type,
    t("Seasonality periods")
  );
  const selectedRoomCategoryName = getRoomCategoryName(roomCategoryOptions, strategyDraft?.scope_id);
  const isRoomCategoryStrategy = strategyDraft?.scope_type === "room_category";
  const isStrategyEditor = editor?.resource?.key === "strategy-settings";
  const selectedSeasonalityTemplateName =
    seasonalityTemplateNameById.get(String(seasonalityPeriodDraft?.seasonality_template_id || "")) ||
    t("Choose a season");
  const selectedSeasonalityPriorityMeta = getOptionMeta(
    SEASONALITY_PRIORITY_META,
    seasonalityPeriodDraft?.priority_choice === "custom"
      ? ""
      : seasonalityPeriodDraft?.priority_choice,
    seasonalityPeriodDraft?.priority_value
      ? t("Priority {value}", { value: seasonalityPeriodDraft.priority_value })
      : t("Normal priority")
  );
  const selectedSeasonalityRangeDays = getDateRangeDays(
    seasonalityPeriodDraft?.start_date,
    seasonalityPeriodDraft?.end_date
  );
  const selectedSeasonalityProfileTemplateName =
    seasonalityTemplateNameById.get(String(seasonalityProfileDraft?.seasonality_template_id || "")) ||
    t("Choose a season");
  const selectedSeasonalityProfileRoomCategoryName =
    getRoomCategoryName(roomCategoryOptions, seasonalityProfileDraft?.room_category_id) ||
    t("Choose a room category");
  const selectedSeasonalityProfileDowLabel = getOptionMeta(
    SEASONALITY_DOW_META,
    String(seasonalityProfileDraft?.dow ?? ""),
    t("Every day")
  );
  const selectedSeasonalityProfileDemandLabel =
    seasonalityProfileDraft?.demand_level_choice === "custom"
      ? String(seasonalityProfileDraft?.demand_level_custom || "").trim() || t("Custom demand tone")
      : seasonalityProfileDraft?.demand_level_choice
        ? getOptionMeta(
            SEASONALITY_TEMPLATE_DEMAND_META,
            seasonalityProfileDraft?.demand_level_choice,
            t("Demand tone")
          )
        : t("Template demand tone");
  const selectedMarketDisruptionTypeMeta = getOptionMeta(
    MARKET_DISRUPTION_TYPE_META,
    marketDisruptionDraft?.disruption_type,
    t("Choose a disruption type")
  );
  const selectedMarketDisruptionDirectionMeta = getOptionMeta(
    MARKET_DISRUPTION_DIRECTION_META,
    marketDisruptionDraft?.impact_direction,
    t("Choose direction")
  );
  const selectedMarketDisruptionLevelMeta = getOptionMeta(
    MARKET_DISRUPTION_LEVEL_META,
    marketDisruptionDraft?.impact_level,
    t("Choose strength")
  );
  const selectedMarketDisruptionRangeDays = getDateRangeDays(
    marketDisruptionDraft?.start_date,
    marketDisruptionDraft?.end_date
  );

  if (!canAccessSettings) {
    return <div className="rmo-empty">{t("You do not have permission to edit this setup.")}</div>;
  }

  return (
    <div className="rmo-sectionStack">
      <section className="rmo-setupGuide" id="rmo-setup-guide">
        <div className="rmo-setupGuideHero">
          <div className="rmo-setupGuideCopy">
            <div className="rmo-setupEyebrow">{t("Guided setup")}</div>
            <h3 className="rmo-setupGuideTitle">
              {t("Configure Revenue Optimizer in this order")}
            </h3>
            <p className="rmo-setupGuideText">
              {t(
                "Complete the seven essential steps from left to right. The last two steps are optional refinements for hotels that need them."
              )}
            </p>
          </div>

          <div
            className="rmo-setupProgress"
            role="progressbar"
            aria-label={t("Essential setup progress")}
            aria-valuemin={0}
            aria-valuemax={essentialSetupSteps.length}
            aria-valuenow={completedEssentialSteps}
          >
            <div className="rmo-setupProgressTop">
              <span>{t("Essential setup")}</span>
              <strong>
                {completedEssentialSteps}/{essentialSetupSteps.length}
              </strong>
            </div>
            <div className="rmo-setupProgressTrack" aria-hidden="true">
              <span
                style={{
                  width: `${Math.round(
                    (completedEssentialSteps / Math.max(essentialSetupSteps.length, 1)) * 100
                  )}%`,
                }}
              />
            </div>
            <div className="rmo-setupProgressHint">
              {nextRecommendedStep
                ? t("Next recommended: {step}", {
                    step: t(nextRecommendedStep.shortTitle),
                  })
                : t("The essential setup is complete.")}
            </div>
          </div>
        </div>

        <div className="rmo-setupStepNav" role="tablist" aria-label={t("Setup steps")}>
          {setupSteps.map((step) => {
            const isActive = step.key === activeSetupStepData?.key;
            const isRecommended = nextRecommendedStep?.key === step.key;
            const statusLabel = step.ready
              ? t("Complete")
              : step.optional
                ? t("Optional")
                : t("Needs attention");

            return (
              <button
                key={step.key}
                type="button"
                role="tab"
                id={`rmo-setup-tab-${step.key}`}
                aria-controls="rmo-setup-active-panel"
                aria-selected={isActive}
                className={`rmo-setupStep${isActive ? " isActive" : ""}${
                  step.ready ? " isComplete" : ""
                }${step.optional ? " isOptional" : ""}`}
                onClick={() => selectSetupStep(step.key)}
              >
                <span className="rmo-setupStepNumber" aria-hidden="true">
                  {step.ready ? "✓" : step.number}
                </span>
                <span className="rmo-setupStepCopy">
                  <span className="rmo-setupStepTitle">{t(step.shortTitle)}</span>
                  <span className="rmo-setupStepMeta">{statusLabel}</span>
                </span>
                {isRecommended ? (
                  <span className="rmo-setupStepNext">{t("Next")}</span>
                ) : null}
              </button>
            );
          })}
        </div>

        <div className="rmo-setupSnapshot" aria-label={t("Current setup summary")}>
          {integrityCards.map((card) => (
            <div key={card.label} className="rmo-setupSnapshotItem">
              <span>{card.label}</span>
              <strong>{card.value}</strong>
              <small>{card.meta}</small>
            </div>
          ))}
        </div>

        <div
          className="rmo-setupActiveBrief"
          id="rmo-setup-active-panel"
          role="tabpanel"
          aria-labelledby={`rmo-setup-tab-${activeSetupStepData?.key || "products"}`}
        >
          <div className="rmo-setupActiveNumber">
            {t("Step {number} of {total}", {
              number: activeSetupStepData?.number || 1,
              total: setupSteps.length,
            })}
          </div>
          <div className="rmo-setupActiveCopy">
            <h4>{t(activeSetupStepData?.title || "Setup")}</h4>
            <p>{t(activeSetupStepData?.description || "")}</p>
            <div className="rmo-setupCompleteWhen">
              <strong>{t("Complete when:")}</strong>{" "}
              {t(activeSetupStepData?.completeWhen || "")}
            </div>
          </div>
          <div
            className={`rmo-setupActiveStatus${
              activeSetupStepData?.ready
                ? " isComplete"
                : activeSetupStepData?.optional
                  ? " isOptional"
                  : ""
            }`}
          >
            <span>
              {activeSetupStepData?.ready
                ? t("Complete")
                : activeSetupStepData?.optional
                  ? t("Optional")
                  : t("Needs attention")}
            </span>
            <small>{t(activeSetupStepData?.detail || "")}</small>
          </div>
        </div>
      </section>

      {error ? <div className="rmo-errorBox">{error}</div> : null}

      {activeSetupStep === "quality" ? (
        <div className="rmo-surface rmo-setupStageSurface">
        <div className="rmo-head">
          <div>
            <h3 className="rmo-headTitle">{t("Quality Settings")}</h3>
            <div className="rmo-headSubtitle">
              {t("Tell the RMS how strong your hotel quality signal is when compared with the market.")}
            </div>
          </div>
          <button type="button" className="rmo-btn rmo-btn--accent" onClick={saveQuality} disabled={saving || loading}>
            {saving ? t("Saving...") : t("Save quality settings")}
          </button>
        </div>
        <div className="rmo-contentPad">
          <div className="rmo-banner" style={{ margin: "0 0 14px" }}>
              <div className="rmo-bannerText">
                {t(
                  "These values do not set prices directly. They only tell the RMS how much hotel quality should matter when it compares your hotel with competitors."
                )}
              </div>
          </div>
          <div className="rmo-formGrid">
            {QUALITY_FIELD_CONFIG.map((field) => (
              <div key={field.key} className="rmo-formField">
                    <label htmlFor={`quality-${field.key}`}>{t(field.label)}</label>
                <div className="rmo-inputRow">
                  <input
                    id={`quality-${field.key}`}
                    className="rmo-input rmo-inputGrow"
                    type={field.type || "text"}
                    inputMode={field.type === "number" ? "decimal" : undefined}
                    min={field.min}
                    max={field.max}
                    step={field.step}
                    placeholder={field.placeholder}
                    value={quality[field.key] ?? ""}
                    onChange={(event) => {
                      setQualityDirty(true);
                      setQuality((previous) => ({
                        ...previous,
                        [field.key]: event.target.value,
                      }));
                    }}
                  />
                  {field.suffix ? (
                    <span className="rmo-inputSuffix">{field.suffix}</span>
                  ) : null}
                </div>
                  <div className="rmo-formHelp">{t(field.help)}</div>
              </div>
            ))}
          </div>
        </div>
      </div>
      ) : null}

      {activeSetupStep === "pace" ? (
        <div className="rmo-surface rmo-setupStageSurface">
          <div className="rmo-head">
            <div>
              <h3 className="rmo-headTitle">{t("Expected occupancy & pace")}</h3>
              <div className="rmo-headSubtitle">
                {t(
                  "For each active seasonal profile, define how much of the expected final occupancy should already be on the books in each lead-time window."
                )}
              </div>
            </div>
            <button
              type="button"
              className="rmo-btn rmo-btn--accent"
              onClick={saveOccupancyPace}
              disabled={
                loading ||
                saving ||
                savingOccupancyPace ||
                !occupancyPaceDirty ||
                !occupancyPaceCanConfigure ||
                occupancyPaceLocalErrors.length > 0
              }
            >
              {savingOccupancyPace ? t("Saving...") : t("Save pace curve")}
            </button>
          </div>
          <div className="rmo-contentPad">
            <div className="rmo-banner rmo-banner--tight" style={{ marginBottom: 14 }}>
              <div className="rmo-bannerStack">
                <div className="rmo-bannerText">
                  {t(
                    "The preview multiplies the profile's expected final occupancy by the booked-share curve. Tolerance defines the acceptable band around that commercial target."
                  )}
                </div>
                <div className="rmo-bannerText">
                  {!occupancyPaceHasRuntime
                    ? t("Occupancy pace configuration is unavailable.")
                    : occupancyPaceIsReady
                    ? t("The occupancy pace configuration is ready.")
                    : !occupancyPaceIsApplicable
                      ? occupancyPaceHasProfiles
                        ? t("Occupancy pace is not configured. Complete all five windows to opt in.")
                        : t("No active seasonality profiles currently require a pace curve.")
                      : t("Complete and save all five windows for every active profile.")}
                </div>
              </div>
            </div>

            {occupancyPaceError ? (
              <div className="rmo-errorBox" style={{ marginBottom: 12 }}>
                {occupancyPaceError}
              </div>
            ) : null}

            {occupancyPaceBackendIssues.length > 0 ? (
              <div className="rmo-errorBox" style={{ marginBottom: 12 }}>
                <strong>{t("The saved occupancy pace curve needs attention.")}</strong>
                <ul>
                  {occupancyPaceBackendIssues.map((issue, index) => (
                    <li key={`${String(issue?.code || "issue")}-${index}`}>
                      {formatOccupancyPaceIssue(issue, t)}
                    </li>
                  ))}
                </ul>
              </div>
            ) : null}

            {occupancyPaceDirty && occupancyPaceLocalErrors.length > 0 ? (
              <div className="rmo-errorBox" style={{ marginBottom: 12 }}>
                <ul>
                  {occupancyPaceLocalErrors.map((message) => (
                    <li key={message}>{message}</li>
                  ))}
                </ul>
              </div>
            ) : null}

            {!occupancyPaceDraft.profiles.length ? (
              <div className="rmo-empty">
                {occupancyPaceHasRuntime
                  ? t(
                      "Activate a seasonality profile with an expected final occupancy before configuring pace."
                    )
                  : t("Occupancy pace configuration is unavailable.")}
              </div>
            ) : (
              <div className="rmo-tableWrap">
                <table className="rmo-table">
                  <thead>
                    <tr>
                      <th>{t("Active profile")}</th>
                      <th>{t("Lead-time window")}</th>
                      <th>{t("Expected booked share")}</th>
                      <th>{t("Occupancy target preview")}</th>
                      <th>{t("Tolerance")}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {occupancyPaceDraft.profiles.flatMap((profile) => {
                      const profileId = String(profile.seasonality_profile_id);
                      const sourceProfile = seasonalityProfileById.get(profileId) || null;
                      const roomCategory = roomCategoryById.get(
                        Number(profile.room_category_id)
                      );
                      const templateName = seasonalityTemplateNameById.get(
                        String(sourceProfile?.seasonality_template_id ?? "")
                      );
                      const dowLabel = t(
                        getOptionMeta(
                          SEASONALITY_DOW_META,
                          String(sourceProfile?.dow ?? ""),
                          "Every day"
                        ).label
                      );
                      const profileLabel = [
                        templateName || t("Season profile"),
                        roomCategory?.name || t("Unknown category"),
                        dowLabel,
                      ].join(" · ");
                      const expectedFinalOccupancy = normalizeCanonicalPct(
                        profile.expected_occupancy_pct
                      );

                      return OCCUPANCY_PACE_WINDOWS.map((window, index) => {
                        const item = (profile.items || []).find(
                          (candidate) =>
                            candidate.lead_time_bucket === window.code
                        ) || {
                          expected_booked_share_pct: "",
                          tolerance_pct: "",
                        };
                        const shareDisplay = Number(item.expected_booked_share_pct);
                        const preview =
                          expectedFinalOccupancy != null &&
                          expectedFinalOccupancy >= 0 &&
                          expectedFinalOccupancy <= 1 &&
                          Number.isFinite(shareDisplay) &&
                          shareDisplay >= 0 &&
                          shareDisplay <= 100
                            ? expectedFinalOccupancy * (shareDisplay / 100)
                            : null;

                        return (
                          <tr key={`${profileId}-${window.code}`}>
                            {index === 0 ? (
                              <td rowSpan={OCCUPANCY_PACE_WINDOWS.length}>
                                <strong>{profileLabel}</strong>
                                <div className="rmo-muted">
                                  {t("Expected final occupancy")}: {" "}
                                  {expectedFinalOccupancy == null
                                    ? "-"
                                    : `${(expectedFinalOccupancy * 100).toLocaleString(
                                        locale,
                                        { maximumFractionDigits: 1 }
                                      )}%`}
                                </div>
                              </td>
                            ) : null}
                            <td>
                              <strong>{t(window.label)}</strong>
                              <div className="rmo-muted">{window.code}</div>
                            </td>
                            <td>
                              <div className="rmo-inputRow">
                                <input
                                  className="rmo-input rmo-inputGrow"
                                  type="number"
                                  inputMode="decimal"
                                  min="0"
                                  max="100"
                                  step="0.1"
                                  value={item.expected_booked_share_pct}
                                  aria-label={t("Expected booked share for {window}", {
                                    window: t(window.label),
                                  })}
                                  disabled={savingOccupancyPace || saving}
                                  onChange={(event) =>
                                    updateOccupancyPaceItem(
                                      profileId,
                                      window.code,
                                      "expected_booked_share_pct",
                                      event.target.value
                                    )
                                  }
                                />
                                <span className="rmo-inputSuffix">%</span>
                              </div>
                            </td>
                            <td>
                              <strong>
                                {preview == null
                                  ? "-"
                                  : `${(preview * 100).toLocaleString(locale, {
                                      maximumFractionDigits: 1,
                                    })}%`}
                              </strong>
                            </td>
                            <td>
                              <div className="rmo-inputRow">
                                <input
                                  className="rmo-input rmo-inputGrow"
                                  type="number"
                                  inputMode="decimal"
                                  min="0"
                                  max="100"
                                  step="0.1"
                                  value={item.tolerance_pct}
                                  aria-label={t("Pace tolerance for {window}", {
                                    window: t(window.label),
                                  })}
                                  disabled={savingOccupancyPace || saving}
                                  onChange={(event) =>
                                    updateOccupancyPaceItem(
                                      profileId,
                                      window.code,
                                      "tolerance_pct",
                                      event.target.value
                                    )
                                  }
                                />
                                <span className="rmo-inputSuffix">%</span>
                              </div>
                            </td>
                          </tr>
                        );
                      });
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      ) : null}

      {activeSetupStep === "price-response" ? (
        <div className="rmo-surface rmo-setupStageSurface">
          <div className="rmo-head">
            <div>
              <h3 className="rmo-headTitle">{t("Price response safeguards")}</h3>
              <div className="rmo-headSubtitle">
                {t("The RMS reacts more softly or firmly according to your strategy, but every recommendation stays inside the movement and confidence safeguards below.")}
              </div>
            </div>
            <button
              type="button"
              className="rmo-btn rmo-btn--accent"
              onClick={savePriceResponse}
              disabled={
                loading || saving || savingPriceResponse || !priceResponseDirty ||
                !priceResponseCanConfigure || priceResponseLocalErrors.length > 0
              }
            >
              {savingPriceResponse ? t("Saving...") : t("Save response safeguards")}
            </button>
          </div>
          <div className="rmo-contentPad">
            <div className="rmo-banner rmo-banner--tight" style={{ marginBottom: 14 }}>
              <div className="rmo-bannerStack">
                <div className="rmo-bannerText">
                  {t("Behind pace uses the weak-demand gain; ahead of pace uses the strong-demand gain. The final movement can never exceed the per-run, rolling 24-hour, guardrail or ladder limit.")}
                </div>
                <div className="rmo-bannerText">
                  {t("Inactive strategy drafts are included so their five safeguards can be prepared before activation. A strategy-mode change pauses readiness until its gains are resaved here.")}
                </div>
                <div className="rmo-bannerText">
                  {!priceResponseHasRuntime
                    ? t("Price-response configuration is unavailable.")
                    : priceResponseIsReady
                      ? t("The price-response safeguards are ready.")
                      : !priceResponseIsApplicable
                        ? t("No effective strategy currently requires response safeguards.")
                        : t("Complete and save all five windows for every effective strategy.")}
                </div>
              </div>
            </div>

            {priceResponseError ? (
              <div className="rmo-errorBox" style={{ marginBottom: 12 }}>{priceResponseError}</div>
            ) : null}
            {priceResponseBackendIssues.length > 0 ? (
              <div className="rmo-errorBox" style={{ marginBottom: 12 }}>
                <strong>{t("The saved price-response safeguards need attention.")}</strong>
                <ul>
                  {priceResponseBackendIssues.map((issue, index) => (
                    <li key={`${String(issue?.code || "issue")}-${index}`}>
                      {formatPriceResponseIssue(issue, t)}
                    </li>
                  ))}
                </ul>
              </div>
            ) : null}
            {priceResponseDirty && priceResponseLocalErrors.length > 0 ? (
              <div className="rmo-errorBox" style={{ marginBottom: 12 }}>
                <ul>{priceResponseLocalErrors.map((message) => <li key={message}>{message}</li>)}</ul>
              </div>
            ) : null}

            {!priceResponseDraft.strategies.length ? (
              <div className="rmo-empty">
                {priceResponseHasRuntime
                  ? t("Activate an effective hotel or room-category strategy before configuring response safeguards.")
                  : t("Price-response configuration is unavailable.")}
              </div>
            ) : priceResponseDraft.strategies.map((strategy) => {
              const scope = strategy.scope_type === "room_category"
                ? roomCategoryById.get(Number(strategy.scope_id))?.name || t("Room category")
                : t("Whole hotel");
              return (
                <div key={strategy.strategy_setting_id} className="rmo-strategySection" style={{ marginBottom: 18 }}>
                  <div className="rmo-strategySectionHead">
                    <div>
                      <h4 className="rmo-strategySectionTitle">
                        {t("{mode} strategy · {scope}", {
                          mode: t(getOptionMeta(STRATEGY_MODE_META, strategy.strategy_mode, "Balanced").label),
                          scope,
                        })}
                        {!strategy.is_active ? ` · ${t("Inactive draft")}` : ""}
                      </h4>
                      <div className="rmo-strategySectionHelp">
                        {t("Strategy #{id} · each row is applied for its lead-time window.", { id: strategy.strategy_setting_id })}
                      </div>
                    </div>
                  </div>
                  <div className="rmo-tableWrap">
                    <table className="rmo-table">
                      <thead>
                        <tr>
                          <th>{t("Lead-time window")}</th>
                          <th>{t("Behind gain")}</th>
                          <th>{t("Ahead gain")}</th>
                          <th>{t("Max decrease")}</th>
                          <th>{t("Max increase")}</th>
                          <th>{t("24h decrease")}</th>
                          <th>{t("24h increase")}</th>
                          <th>{t("Hysteresis enter / exit")}</th>
                          <th>{t("Minimum interval")}</th>
                          <th>{t("Low-confidence policy")}</th>
                        </tr>
                      </thead>
                      <tbody>
                        {strategy.items.map((item) => (
                          <tr key={`${strategy.strategy_setting_id}-${item.lead_time_bucket}`}>
                            <td><strong>{t(PRICE_RESPONSE_WINDOWS.find((window) => window.code === item.lead_time_bucket)?.label || item.lead_time_bucket)}</strong></td>
                            {[
                              ["behind_gain", "0.0001", "2"],
                              ["ahead_gain", "0.0001", "2"],
                              ["max_decrease_pct_per_run", "0.1", "100"],
                              ["max_increase_pct_per_run", "0.1", "100"],
                              ["max_decrease_pct_24h", "0.1", "100"],
                              ["max_increase_pct_24h", "0.1", "100"],
                            ].map(([field, step, max]) => (
                              <td key={field}>
                                <div className="rmo-inputRow">
                                  <input className="rmo-input rmo-inputGrow" type="number" min="0" max={max} step={step}
                                    value={item[field]}
                                    disabled={savingPriceResponse || saving}
                                    onChange={(event) => updatePriceResponseItem(strategy.strategy_setting_id, item.lead_time_bucket, field, event.target.value)} />
                                  {field.includes("pct") ? <span className="rmo-inputSuffix">%</span> : <span className="rmo-inputSuffix">×</span>}
                                </div>
                              </td>
                            ))}
                            <td>
                              <div className="rmo-inputRow" style={{ marginBottom: 6 }}>
                                <input className="rmo-input rmo-inputGrow" type="number" min="0" max="100" step="0.1" value={item.hysteresis_enter_pct}
                                  disabled={savingPriceResponse || saving}
                                  onChange={(event) => updatePriceResponseItem(strategy.strategy_setting_id, item.lead_time_bucket, "hysteresis_enter_pct", event.target.value)} />
                                <span className="rmo-inputSuffix">%</span>
                              </div>
                              <div className="rmo-inputRow">
                                <input className="rmo-input rmo-inputGrow" type="number" min="0" max="100" step="0.1" value={item.hysteresis_exit_pct}
                                  disabled={savingPriceResponse || saving}
                                  onChange={(event) => updatePriceResponseItem(strategy.strategy_setting_id, item.lead_time_bucket, "hysteresis_exit_pct", event.target.value)} />
                                <span className="rmo-inputSuffix">%</span>
                              </div>
                            </td>
                            <td>
                              <div className="rmo-inputRow">
                                <input className="rmo-input rmo-inputGrow" type="number" min="0" max="10080" step="1" value={item.min_change_interval_minutes}
                                  disabled={savingPriceResponse || saving}
                                  onChange={(event) => updatePriceResponseItem(strategy.strategy_setting_id, item.lead_time_bucket, "min_change_interval_minutes", event.target.value)} />
                                <span className="rmo-inputSuffix">min</span>
                              </div>
                            </td>
                            <td style={{ minWidth: 190 }}>
                              <select className="rmo-input" value={item.low_confidence_action}
                                disabled={savingPriceResponse || saving}
                                onChange={(event) => updatePriceResponseItem(strategy.strategy_setting_id, item.lead_time_bucket, "low_confidence_action", event.target.value)}>
                                <option value="hold">{t("Hold current price")}</option>
                                <option value="limit">{t("Limit movement")}</option>
                                <option value="require_approval">{t("Require approval")}</option>
                              </select>
                              <div className="rmo-inputRow" style={{ marginTop: 6 }}>
                                <input className="rmo-input rmo-inputGrow" type="number" min="0" max="100" step="0.1"
                                  value={item.low_confidence_threshold}
                                  aria-label={t("Low-confidence threshold")}
                                  disabled={savingPriceResponse || saving}
                                  onChange={(event) => updatePriceResponseItem(strategy.strategy_setting_id, item.lead_time_bucket, "low_confidence_threshold", event.target.value)} />
                                <span className="rmo-inputSuffix">% {t("threshold")}</span>
                              </div>
                              {item.low_confidence_action === "limit" ? (
                                <div className="rmo-inputRow" style={{ marginTop: 6 }}>
                                  <input className="rmo-input rmo-inputGrow" type="number" min="0" max="100" step="0.1"
                                    value={item.low_confidence_max_delta_pct}
                                    aria-label={t("Low-confidence maximum movement")}
                                    disabled={savingPriceResponse || saving}
                                    onChange={(event) => updatePriceResponseItem(strategy.strategy_setting_id, item.lead_time_bucket, "low_confidence_max_delta_pct", event.target.value)} />
                                  <span className="rmo-inputSuffix">% {t("max")}</span>
                                </div>
                              ) : null}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              );
            })}

            <div className="rmo-strategySection" style={{ marginTop: 24 }}>
              <div className="rmo-strategySectionHead">
                <div>
                  <h4 className="rmo-strategySectionTitle">
                    {t("Unavoidable orphan-gap safeguards")}
                  </h4>
                  <div className="rmo-strategySectionHelp">
                    {t(
                      "Opt in by strategy for one- or two-night gaps. A T06 reassignment assessment is always required first, and dates beyond the configured horizon never receive an automatic orphan-gap discount."
                    )}
                  </div>
                </div>
                <button
                  type="button"
                  className="rmo-btn rmo-btn--accent"
                  onClick={saveOrphanGapPolicy}
                  disabled={
                    loading ||
                    saving ||
                    savingOrphanGapPolicy ||
                    !orphanGapPolicyDirty ||
                    !orphanGapPolicyCanConfigure ||
                    orphanGapPolicyLocalErrors.length > 0
                  }
                >
                  {savingOrphanGapPolicy
                    ? t("Saving...")
                    : t("Save orphan-gap safeguards")}
                </button>
              </div>

              {orphanGapPolicyError ? (
                <div className="rmo-errorBox" style={{ marginBottom: 12 }}>
                  {orphanGapPolicyError}
                </div>
              ) : null}
              {orphanGapPolicyBackendIssues.length > 0 ? (
                <div className="rmo-errorBox" style={{ marginBottom: 12 }}>
                  <strong>{t("The saved orphan-gap safeguards need attention.")}</strong>
                  <ul>
                    {orphanGapPolicyBackendIssues.map((issue, index) => (
                      <li key={`${String(issue?.code || "issue")}-${index}`}>
                        {formatPriceResponseIssue(issue, t)}
                      </li>
                    ))}
                  </ul>
                </div>
              ) : null}
              {orphanGapPolicyDirty && orphanGapPolicyLocalErrors.length > 0 ? (
                <div className="rmo-errorBox" style={{ marginBottom: 12 }}>
                  <ul>
                    {orphanGapPolicyLocalErrors.map((message) => (
                      <li key={message}>{t(message)}</li>
                    ))}
                  </ul>
                </div>
              ) : null}

              {!orphanGapPolicyDraft.items.length ? (
                <div className="rmo-empty">
                  {orphanGapPolicyHasRuntime
                    ? t("Create a pricing strategy before configuring orphan-gap safeguards.")
                    : t("Orphan-gap configuration is unavailable.")}
                </div>
              ) : (
                <div className="rmo-tableWrap">
                  <table className="rmo-table">
                    <thead>
                      <tr>
                        <th>{t("Strategy scope")}</th>
                        <th>{t("Active")}</th>
                        <th>{t("Gap lengths")}</th>
                        <th>{t("Automatic-discount horizon")}</th>
                        <th>{t("Reassignment first")}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {orphanGapPolicyDraft.items.map((item) => {
                        const scope =
                          item.scope_type === "room_category"
                            ? roomCategoryById.get(Number(item.scope_id))?.name ||
                              t("Room category")
                            : t("Whole hotel");
                        const disabled = savingOrphanGapPolicy || saving;
                        return (
                          <tr key={`orphan-gap-${item.strategy_setting_id}`}>
                            <td>
                              <strong>{scope}</strong>
                              <div className="rmo-cellMeta">
                                {t("{mode} strategy", {
                                  mode: t(
                                    getOptionMeta(
                                      STRATEGY_MODE_META,
                                      item.strategy_mode,
                                      "Balanced"
                                    ).label
                                  ),
                                })}
                                {!item.strategy_is_active
                                  ? ` · ${t("Inactive draft")}`
                                  : ""}
                              </div>
                            </td>
                            <td>
                              <label className="rmo-orphanGapCheck">
                                <input
                                  type="checkbox"
                                  checked={item.is_enabled}
                                  disabled={disabled}
                                  onChange={(event) =>
                                    updateOrphanGapPolicyItem(
                                      item.strategy_setting_id,
                                      "is_enabled",
                                      event.target.checked
                                    )
                                  }
                                />
                                <span>{t("Use orphan-gap pricing")}</span>
                              </label>
                            </td>
                            <td>
                              <div className="rmo-cellStack">
                                {[
                                  ["gap_1_enabled", "1 night"],
                                  ["gap_2_enabled", "2 nights"],
                                ].map(([field, label]) => (
                                  <label className="rmo-orphanGapCheck" key={field}>
                                    <input
                                      type="checkbox"
                                      checked={item[field]}
                                      disabled={disabled || !item.is_enabled}
                                      onChange={(event) =>
                                        updateOrphanGapPolicyItem(
                                          item.strategy_setting_id,
                                          field,
                                          event.target.checked
                                        )
                                      }
                                    />
                                    <span>{t(label)}</span>
                                  </label>
                                ))}
                              </div>
                            </td>
                            <td style={{ minWidth: 210 }}>
                              <div className="rmo-inputRow">
                                <input
                                  className="rmo-input rmo-inputGrow"
                                  type="number"
                                  min="1"
                                  max="21"
                                  step="1"
                                  value={item.far_horizon_days}
                                  disabled={disabled || !item.is_enabled}
                                  onChange={(event) =>
                                    updateOrphanGapPolicyItem(
                                      item.strategy_setting_id,
                                      "far_horizon_days",
                                      event.target.value
                                    )
                                  }
                                />
                                <span className="rmo-inputSuffix">{t("days")}</span>
                              </div>
                              <div className="rmo-cellMeta">
                                {t("Maximum 21 days; farther dates are protected.")}
                              </div>
                            </td>
                            <td>
                              <label className="rmo-orphanGapCheck">
                                <input type="checkbox" checked readOnly disabled />
                                <span>{t("Mandatory T06 assessment")}</span>
                              </label>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>

            <div className="rmo-strategySection" style={{ marginTop: 24 }}>
              <div className="rmo-strategySectionHead">
                <div>
                  <h4 className="rmo-strategySectionTitle">
                    {t("Near orphan-gap actions")}
                  </h4>
                  <div className="rmo-strategySectionHelp">
                    {t(
                      "Within 0–7 days, an eligible T07 gap may relax MinLOS and propose a capped discount only with certified weak pace and complete economic clearance. Nothing is applied automatically."
                    )}
                  </div>
                </div>
                <button
                  type="button"
                  className="rmo-btn rmo-btn--accent"
                  onClick={saveOrphanGapNearPolicy}
                  disabled={
                    loading ||
                    saving ||
                    savingOrphanGapNearPolicy ||
                    !orphanGapNearPolicyDirty ||
                    !orphanGapNearPolicyCanConfigure ||
                    orphanGapNearPolicyLocalErrors.length > 0
                  }
                >
                  {savingOrphanGapNearPolicy
                    ? t("Saving...")
                    : t("Save near-gap safeguards")}
                </button>
              </div>

              {orphanGapNearPolicyError ? (
                <div className="rmo-errorBox" style={{ marginBottom: 12 }}>
                  {orphanGapNearPolicyError}
                </div>
              ) : null}
              {orphanGapNearPolicyBackendIssues.length > 0 ? (
                <div className="rmo-errorBox" style={{ marginBottom: 12 }}>
                  <strong>{t("The saved near-gap safeguards need attention.")}</strong>
                  <ul>
                    {orphanGapNearPolicyBackendIssues.map((issue, index) => (
                      <li key={`${String(issue?.code || "issue")}-${index}`}>
                        {formatPriceResponseIssue(issue, t)}
                      </li>
                    ))}
                  </ul>
                </div>
              ) : null}
              {orphanGapNearPolicyDirty && orphanGapNearPolicyLocalErrors.length > 0 ? (
                <div className="rmo-errorBox" style={{ marginBottom: 12 }}>
                  <ul>
                    {orphanGapNearPolicyLocalErrors.map((message) => (
                      <li key={message}>{t(message)}</li>
                    ))}
                  </ul>
                </div>
              ) : null}

              {!orphanGapNearPolicyDraft.items.length ? (
                <div className="rmo-empty">
                  {orphanGapNearPolicyHasRuntime
                    ? t("Create a pricing strategy before configuring near-gap safeguards.")
                    : t("Near-gap configuration is unavailable.")}
                </div>
              ) : (
                <div className="rmo-tableWrap">
                  <table className="rmo-table">
                    <thead>
                      <tr>
                        <th>{t("Strategy scope")}</th>
                        <th>{t("Near band")}</th>
                        <th>{t("Active")}</th>
                        <th>{t("MinLOS relaxation")}</th>
                        <th>{t("Discount cap")}</th>
                        <th>{t("Adjacent occupancy")}</th>
                        <th>{t("Economic evidence")}</th>
                        <th>{t("Low confidence")}</th>
                        <th>{t("Safety gates")}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {orphanGapNearPolicyDraft.items.map((item) => {
                        const scope =
                          item.scope_type === "room_category"
                            ? roomCategoryById.get(Number(item.scope_id))?.name ||
                              t("Room category")
                            : t("Whole hotel");
                        const disabled = savingOrphanGapNearPolicy || saving;
                        return (
                          <tr key={`orphan-gap-near-${item.strategy_setting_id}`}>
                            <td>
                              <strong>{scope}</strong>
                              <div className="rmo-cellMeta">
                                {t("{mode} strategy", {
                                  mode: t(
                                    getOptionMeta(
                                      STRATEGY_MODE_META,
                                      item.strategy_mode,
                                      "Balanced"
                                    ).label
                                  ),
                                })}
                                {!item.strategy_is_active
                                  ? ` · ${t("Inactive draft")}`
                                  : ""}
                              </div>
                            </td>
                            <td>
                              <strong>
                                {t("0–{days} days", {
                                  days: orphanGapNearPolicyDraft.fixed_near_horizon_days || 7,
                                })}
                              </strong>
                              <div className="rmo-cellMeta">{t("Fixed horizon")}</div>
                            </td>
                            <td>
                              <label className="rmo-orphanGapCheck">
                                <input
                                  type="checkbox"
                                  checked={item.is_enabled}
                                  disabled={disabled}
                                  onChange={(event) =>
                                    updateOrphanGapNearPolicyItem(
                                      item.strategy_setting_id,
                                      "is_enabled",
                                      event.target.checked
                                    )
                                  }
                                />
                                <span>{t("Use near-gap actions")}</span>
                              </label>
                            </td>
                            <td style={{ minWidth: 180 }}>
                              <label className="rmo-orphanGapCheck">
                                <input
                                  type="checkbox"
                                  checked={item.relax_min_los_enabled}
                                  disabled={disabled || !item.is_enabled}
                                  onChange={(event) =>
                                    updateOrphanGapNearPolicyItem(
                                      item.strategy_setting_id,
                                      "relax_min_los_enabled",
                                      event.target.checked
                                    )
                                  }
                                />
                                <span>{t("Relax MinLOS")}</span>
                              </label>
                              <select
                                className="rmo-input"
                                value={item.min_los_floor_nights}
                                aria-label={t("MinLOS floor")}
                                disabled={disabled || !item.is_enabled}
                                onChange={(event) =>
                                  updateOrphanGapNearPolicyItem(
                                    item.strategy_setting_id,
                                    "min_los_floor_nights",
                                    event.target.value
                                  )
                                }
                              >
                                <option value="1">{t("1 night floor")}</option>
                                <option value="2">{t("2 night floor")}</option>
                              </select>
                            </td>
                            <td>
                              <div className="rmo-inputRow">
                                <input
                                  className="rmo-input rmo-inputGrow"
                                  type="number"
                                  min="0"
                                  max="20"
                                  step="0.1"
                                  value={item.max_discount_pct}
                                  disabled={disabled || !item.is_enabled}
                                  onChange={(event) =>
                                    updateOrphanGapNearPolicyItem(
                                      item.strategy_setting_id,
                                      "max_discount_pct",
                                      event.target.value
                                    )
                                  }
                                />
                                <span className="rmo-inputSuffix">%</span>
                              </div>
                            </td>
                            <td>
                              <div className="rmo-inputRow">
                                <input
                                  className="rmo-input rmo-inputGrow"
                                  type="number"
                                  min="0"
                                  max="100"
                                  step="0.1"
                                  value={item.min_adjacent_occupancy_pct}
                                  disabled={disabled || !item.is_enabled}
                                  onChange={(event) =>
                                    updateOrphanGapNearPolicyItem(
                                      item.strategy_setting_id,
                                      "min_adjacent_occupancy_pct",
                                      event.target.value
                                    )
                                  }
                                />
                                <span className="rmo-inputSuffix">%</span>
                              </div>
                            </td>
                            <td style={{ minWidth: 210 }}>
                              <div className="rmo-cellStack">
                                <input
                                  className="rmo-input"
                                  type="text"
                                  maxLength="3"
                                  placeholder={t("Currency")}
                                  value={item.currency_code}
                                  disabled={disabled || !item.is_enabled}
                                  onChange={(event) =>
                                    updateOrphanGapNearPolicyItem(
                                      item.strategy_setting_id,
                                      "currency_code",
                                      event.target.value
                                    )
                                  }
                                />
                                {item.expected_currency_code ? (
                                  <div className="rmo-cellMeta">
                                    {t("Master-rate currency")}: {item.expected_currency_code}
                                  </div>
                                ) : null}
                                <div className="rmo-inputRow">
                                  <input
                                    className="rmo-input rmo-inputGrow"
                                    type="number"
                                    min="0"
                                    step="0.01"
                                    placeholder={t("Variable cost")}
                                    value={item.variable_cost_per_occupied_room}
                                    disabled={disabled || !item.is_enabled}
                                    onChange={(event) =>
                                      updateOrphanGapNearPolicyItem(
                                        item.strategy_setting_id,
                                        "variable_cost_per_occupied_room",
                                        event.target.value
                                      )
                                    }
                                  />
                                  <span className="rmo-inputSuffix">
                                    {item.currency_code || "—"}
                                  </span>
                                </div>
                                <div className="rmo-inputRow">
                                  <input
                                    className="rmo-input rmo-inputGrow"
                                    type="number"
                                    min="0"
                                    max="95"
                                    step="0.1"
                                    placeholder={t("Distribution cost")}
                                    value={item.distribution_cost_pct}
                                    disabled={disabled || !item.is_enabled}
                                    onChange={(event) =>
                                      updateOrphanGapNearPolicyItem(
                                        item.strategy_setting_id,
                                        "distribution_cost_pct",
                                        event.target.value
                                      )
                                    }
                                  />
                                  <span className="rmo-inputSuffix">%</span>
                                </div>
                              </div>
                            </td>
                            <td>
                              <select
                                className="rmo-input"
                                value={item.low_confidence_action}
                                disabled={disabled || !item.is_enabled}
                                onChange={(event) =>
                                  updateOrphanGapNearPolicyItem(
                                    item.strategy_setting_id,
                                    "low_confidence_action",
                                    event.target.value
                                  )
                                }
                              >
                                <option value="hold">{t("Hold current state")}</option>
                                <option value="require_approval">
                                  {t("Require approval")}
                                </option>
                              </select>
                            </td>
                            <td style={{ minWidth: 190 }}>
                              <div className="rmo-cellStack">
                                <label className="rmo-orphanGapCheck">
                                  <input type="checkbox" checked readOnly disabled />
                                  <span>{t("Certified weak pace")}</span>
                                </label>
                                <label className="rmo-orphanGapCheck">
                                  <input type="checkbox" checked readOnly disabled />
                                  <span>{t("Economic clearance")}</span>
                                </label>
                                <div className="rmo-cellMeta">
                                  {t("T07 eligibility is required first.")}
                                </div>
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>

            <div className="rmo-strategySection" style={{ marginTop: 24 }}>
              <div className="rmo-strategySectionHead">
                <div>
                  <h4 className="rmo-strategySectionTitle">
                    {t("Strong-demand orphan-gap protection")}
                  </h4>
                  <div className="rmo-strategySectionHelp">
                    {t(
                      "For a certified last-room orphan gap with strong pace, protect inventory and optionally propose a capped premium. Discounts and restriction changes remain forbidden, and nothing is applied automatically."
                    )}
                  </div>
                </div>
                <button
                  type="button"
                  className="rmo-btn rmo-btn--accent"
                  onClick={saveOrphanGapStrongPolicy}
                  disabled={
                    loading ||
                    saving ||
                    savingOrphanGapStrongPolicy ||
                    !orphanGapStrongPolicyDirty ||
                    !orphanGapStrongPolicyCanConfigure ||
                    orphanGapStrongPolicyLocalErrors.length > 0
                  }
                >
                  {savingOrphanGapStrongPolicy
                    ? t("Saving...")
                    : t("Save strong-demand safeguards")}
                </button>
              </div>

              {orphanGapStrongPolicyError ? (
                <div className="rmo-errorBox" style={{ marginBottom: 12 }}>
                  {orphanGapStrongPolicyError}
                </div>
              ) : null}
              {orphanGapStrongPolicyBackendIssues.length > 0 ? (
                <div className="rmo-errorBox" style={{ marginBottom: 12 }}>
                  <strong>
                    {t("The saved strong-demand safeguards need attention.")}
                  </strong>
                  <ul>
                    {orphanGapStrongPolicyBackendIssues.map((issue, index) => (
                      <li key={`${String(issue?.code || "issue")}-${index}`}>
                        {formatPriceResponseIssue(issue, t)}
                      </li>
                    ))}
                  </ul>
                </div>
              ) : null}
              {orphanGapStrongPolicyDirty &&
              orphanGapStrongPolicyLocalErrors.length > 0 ? (
                <div className="rmo-errorBox" style={{ marginBottom: 12 }}>
                  <ul>
                    {orphanGapStrongPolicyLocalErrors.map((message) => (
                      <li key={message}>{t(message)}</li>
                    ))}
                  </ul>
                </div>
              ) : null}

              {!orphanGapStrongPolicyDraft.items.length ? (
                <div className="rmo-empty">
                  {orphanGapStrongPolicyHasRuntime
                    ? t(
                        "Create a pricing strategy before configuring strong-demand safeguards."
                      )
                    : t("Strong-demand gap configuration is unavailable.")}
                </div>
              ) : (
                <div className="rmo-tableWrap">
                  <table className="rmo-table">
                    <thead>
                      <tr>
                        <th>{t("Strategy scope")}</th>
                        <th>{t("Active")}</th>
                        <th>{t("Premium cap")}</th>
                        <th>{t("Low confidence")}</th>
                        <th>{t("Immutable safety gates")}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {orphanGapStrongPolicyDraft.items.map((item) => {
                        const scope =
                          item.scope_type === "room_category"
                            ? roomCategoryById.get(Number(item.scope_id))?.name ||
                              t("Room category")
                            : t("Whole hotel");
                        const disabled = savingOrphanGapStrongPolicy || saving;
                        const dependencyMaskLabels = (
                          item.dependency_mask_codes || []
                        ).map((code) =>
                          code === "t07_disabled"
                            ? t("T07 disabled")
                            : code === "t08_disabled"
                              ? t("T08 disabled")
                              : String(code)
                        );
                        return (
                          <tr key={`orphan-gap-strong-${item.strategy_setting_id}`}>
                            <td>
                              <strong>{scope}</strong>
                              <div className="rmo-cellMeta">
                                {t("{mode} strategy", {
                                  mode: t(
                                    getOptionMeta(
                                      STRATEGY_MODE_META,
                                      item.strategy_mode,
                                      "Balanced"
                                    ).label
                                  ),
                                })}
                                {!item.strategy_is_active
                                  ? ` · ${t("Inactive draft")}`
                                  : ""}
                                {item.scope_type === "room_category" &&
                                item.policy_is_configured === false
                                  ? ` · ${t("Inherits whole hotel")}`
                                  : ""}
                              </div>
                            </td>
                            <td>
                              <label className="rmo-orphanGapCheck">
                                <input
                                  type="checkbox"
                                  checked={item.is_enabled}
                                  disabled={disabled}
                                  onChange={(event) =>
                                    updateOrphanGapStrongPolicyItem(
                                      item.strategy_setting_id,
                                      "is_enabled",
                                      event.target.checked
                                    )
                                  }
                                />
                                <span>
                                  {item.dependency_masked
                                    ? t("Configured; dependency-masked")
                                    : t("Protect strong-demand gap")}
                                </span>
                              </label>
                              {item.scope_type === "room_category" &&
                              item.effective_state_available ? (
                                <div className="rmo-cellMeta">
                                  {t("Saved effective state")}: {item.effective_is_enabled
                                    ? t("Enabled")
                                    : item.dependency_masked
                                      ? t("Disabled by dependencies")
                                      : t("Disabled")}
                                  {dependencyMaskLabels.length
                                    ? ` · ${dependencyMaskLabels.join(", ")}`
                                    : ""}
                                </div>
                              ) : null}
                            </td>
                            <td style={{ minWidth: 170 }}>
                              <div className="rmo-inputRow">
                                <input
                                  className="rmo-input rmo-inputGrow"
                                  type="number"
                                  min="0"
                                  max="20"
                                  step="0.1"
                                  value={item.max_premium_pct}
                                  disabled={disabled || !item.is_enabled}
                                  onChange={(event) =>
                                    updateOrphanGapStrongPolicyItem(
                                      item.strategy_setting_id,
                                      "max_premium_pct",
                                      event.target.value
                                    )
                                  }
                                />
                                <span className="rmo-inputSuffix">%</span>
                              </div>
                              <div className="rmo-cellMeta">
                                {t("Zero keeps protection without a premium.")}
                              </div>
                            </td>
                            <td>
                              <select
                                className="rmo-input"
                                value={item.low_confidence_action}
                                disabled={disabled || !item.is_enabled}
                                onChange={(event) =>
                                  updateOrphanGapStrongPolicyItem(
                                    item.strategy_setting_id,
                                    "low_confidence_action",
                                    event.target.value
                                  )
                                }
                              >
                                <option value="hold">{t("Hold current state")}</option>
                                <option value="require_approval">
                                  {t("Require approval")}
                                </option>
                              </select>
                            </td>
                            <td style={{ minWidth: 250 }}>
                              <div className="rmo-cellStack">
                                {[
                                  "Last room only",
                                  "Certified strong pace",
                                  "No longer-stay displacement",
                                  "Restrictions unchanged",
                                  "Zero discount",
                                  "Proposal only",
                                ].map((label) => (
                                  <label className="rmo-orphanGapCheck" key={label}>
                                    <input type="checkbox" checked readOnly disabled />
                                    <span>{t(label)}</span>
                                  </label>
                                ))}
                                <div className="rmo-cellMeta">
                                  {t("Effective T07 and T08 policies are required first.")}
                                </div>
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        </div>
      ) : null}

      {activeSetupStep === "mapping" ? (
        <div className="rmo-setupStage">
          <RmsMarketMappingStudio
            meta={meta}
            resources={resources}
            loading={loading}
            reloadMappings={reloadMarketMappings}
            onError={setError}
          />
        </div>
      ) : null}

      {activeSetupStep === "competitors" ? (
      <div className="rmo-surface rmo-setupStageSurface">
        <div className="rmo-head">
          <div>
            <h3 className="rmo-headTitle">{t("Competitors")}</h3>
            <div className="rmo-headSubtitle">
              {t(
                "Review which hotels are in your market scope and decide how strongly each one should influence the RMS."
              )}
            </div>
          </div>
        </div>
        <div className="rmo-contentPad">
          <div className="rmo-card" style={{ marginBottom: "16px" }}>
            <div className="rmo-head rmo-head--compact">
              <div>
                <h4 className="rmo-headTitle">{t("Market benchmark quality gate")}</h4>
                <div className="rmo-headSubtitle">
                  {t("Only fresh, comparable and sufficiently covered competitor prices may influence RMS recommendations.")}
                </div>
              </div>
              <button
                type="button"
                className="rmo-btn rmo-btn--primary"
                onClick={saveMarketBenchmarkPolicy}
                disabled={savingMarketBenchmarkPolicy || !marketBenchmarkPolicyDirty}
              >
                {savingMarketBenchmarkPolicy ? t("Saving...") : t("Save quality gate")}
              </button>
            </div>
            <div className="rmo-contentPad">
              <div className="rmo-formGrid">
                <label className="rmo-field">
                  <span>{t("Use market benchmark")}</span>
                  <input
                    type="checkbox"
                    checked={marketBenchmarkPolicyDraft.item.is_enabled === true}
                    onChange={(event) =>
                      updateMarketBenchmarkPolicy("is_enabled", event.target.checked)
                    }
                  />
                </label>
                <label className="rmo-field">
                  <span>{t("Maximum data age (minutes)")}</span>
                  <input
                    type="number"
                    min="5"
                    max="10080"
                    value={marketBenchmarkPolicyDraft.item.max_data_age_minutes}
                    onChange={(event) =>
                      updateMarketBenchmarkPolicy(
                        "max_data_age_minutes",
                        event.target.value
                      )
                    }
                  />
                </label>
                <label className="rmo-field">
                  <span>{t("Minimum comparable competitors")}</span>
                  <input
                    type="number"
                    min="1"
                    max="20"
                    value={marketBenchmarkPolicyDraft.item.min_competitors}
                    onChange={(event) =>
                      updateMarketBenchmarkPolicy(
                        "min_competitors",
                        event.target.value
                      )
                    }
                  />
                </label>
                {[
                  ["min_weighted_coverage_pct", "Minimum weighted coverage", 0, 100],
                  ["max_outlier_deviation_pct", "Maximum outlier deviation", 1, 200],
                  ["max_market_adjustment_pct", "Maximum market influence", 0, 20],
                ].map(([field, label, min, max]) => (
                  <label className="rmo-field" key={field}>
                    <span>{t(label)} (%)</span>
                    <input
                      type="number"
                      min={min}
                      max={max}
                      step="0.1"
                      value={Number(
                        marketBenchmarkPolicyDraft.item[field] * 100
                      ).toFixed(1)}
                      onChange={(event) =>
                        updateMarketBenchmarkPolicy(
                          field,
                          Number(event.target.value) / 100
                        )
                      }
                    />
                  </label>
                ))}
              </div>
              <div className="rmo-cellMeta" style={{ marginTop: "10px" }}>
                {t("Fallback is fail-safe: stale or low-quality market data is ignored and the recommendation explains why.")}
              </div>
              {marketBenchmarkPolicyError ? (
                <div className="rmo-inlineError" style={{ marginTop: "10px" }}>
                  {marketBenchmarkPolicyError}
                </div>
              ) : null}
              {marketBenchmarkPolicy?.snapshot_hash ? (
                <div className="rmo-cellMeta" style={{ marginTop: "8px" }}>
                  {t("Certified configuration")}: {marketBenchmarkPolicy.snapshot_hash.slice(0, 12)}…
                </div>
              ) : null}
            </div>
          </div>
          <div className="rmo-banner rmo-banner--tight" style={{ marginBottom: "14px" }}>
            <div className="rmo-bannerStack">
              <div className="rmo-bannerText">
                {t(
                  "This list is controlled by your market data extraction scope. It is read-only here, so this screen does not add or remove competitor hotels."
                )}
              </div>
              <div className="rmo-bannerText">
                {t(
                  "What you can control here is how much each competitor matters in the RMS benchmark, and whether a competitor should influence the optimizer right now."
                )}
              </div>
            </div>
          </div>

          <div className="rmo-competitorScopeGrid">
            <div className="rmo-competitorScopeCard">
              <div className="rmo-competitorScopeLabel">{t("Extraction scope")}</div>
              <div className="rmo-competitorScopeValue">
                {rateShopConfig?.rateshopname || t("Not configured yet")}
              </div>
              <div className="rmo-competitorScopeMeta">
                {rateShopConfig?.currency
                  ? t("Currency: {currency}", { currency: rateShopConfig.currency })
                  : t("No market scope has been loaded yet.")}
              </div>
            </div>

            <div className="rmo-competitorScopeCard">
              <div className="rmo-competitorScopeLabel">{t("Sources")}</div>
              <div className="rmo-competitorScopeValue">
                {competitorSourcesLabel || t("No sources detected")}
              </div>
              <div className="rmo-competitorScopeMeta">
                {rateShopConfig?.sources?.length
                  ? t(
                      rateShopConfig.sources.length === 1
                        ? "{count} source feeding this benchmark"
                        : "{count} sources feeding this benchmark",
                      { count: rateShopConfig.sources.length }
                    )
                  : t("Sources are missing from the extraction scope.")}
              </div>
            </div>

            <div className="rmo-competitorScopeCard">
              <div className="rmo-competitorScopeLabel">{t("Competitors in scope")}</div>
              <div className="rmo-competitorScopeValue">{competitorScopeRows.length}</div>
              <div className="rmo-competitorScopeMeta">
                {t("{count} currently influence the RMS", {
                  count: mergedCompetitors.filter((item) => item.is_active).length,
                })}
              </div>
            </div>
          </div>

          {loading ? (
            <div className="rmo-empty">{t("Loading competitors...")}</div>
          ) : mergedCompetitors.length === 0 ? (
            <div className="rmo-empty">
              {t("No competitor hotels are available yet in the market extraction scope.")}
            </div>
          ) : (
            <div className="rmo-competitorGrid">
              {mergedCompetitors.map((item) => (
                <div
                  key={`competitor-${item.hotel_code}`}
                  className={`rmo-competitorCard${item.is_active ? "" : " isMuted"}`}
                >
                  <div className="rmo-competitorCardHead">
                    <div>
                      <div className="rmo-competitorCardTitle">{item.source_name}</div>
                      <div className="rmo-competitorCardMeta">
                        {t("Hotel code {code}", { code: item.hotel_code })}
                      </div>
                    </div>
                    <div className="rmo-competitorCardPills">
                      <span
                        className={`rmo-pill ${
                          item.is_active ? "rmo-pill--success" : "rmo-pill--soft"
                        }`}
                      >
                        {item.is_active ? t("Influences RMS") : t("Ignored by RMS")}
                      </span>
                      <span className="rmo-pill rmo-pill--soft">
                        {item.hasCustomSettings ? t("Custom settings") : t("Default settings")}
                      </span>
                    </div>
                  </div>

                  <div className="rmo-competitorCardAddress">
                    {item.source_address ||
                      t("Address stored in the extraction scope is not available.")}
                  </div>

                  <div className="rmo-competitorStatGrid">
                    <div className="rmo-competitorStat">
                      <div className="rmo-competitorStatLabel">{t("Importance")}</div>
                      <div className="rmo-competitorStatValue">
                        {item.competitor_weight || 1}x
                      </div>
                    </div>
                    <div className="rmo-competitorStat">
                      <div className="rmo-competitorStatLabel">{t("Distance")}</div>
                      <div className="rmo-competitorStatValue">
                        {item.distance_km === "" || item.distance_km == null
                          ? t("Not set")
                          : `${item.distance_km} km`}
                      </div>
                    </div>
                    <div className="rmo-competitorStat">
                      <div className="rmo-competitorStatLabel">{t("Public rating")}</div>
                      <div className="rmo-competitorStatValue">
                        {item.rating === "" || item.rating == null ? t("Not set") : `${item.rating}/5`}
                      </div>
                    </div>
                    <div className="rmo-competitorStat">
                      <div className="rmo-competitorStatLabel">{t("Reviews")}</div>
                      <div className="rmo-competitorStatValue">
                        {item.review_count === "" || item.review_count == null
                          ? t("Not set")
                          : item.review_count}
                      </div>
                    </div>
                  </div>

                  <div className="rmo-competitorCardFoot">
                    <div className="rmo-competitorCardFootText">
                      {item.is_active
                        ? t("This hotel currently contributes to your market benchmark.")
                        : t(
                            "This hotel stays in the scope, but the RMS ignores it until you activate it again."
                          )}
                    </div>
                    <button
                      type="button"
                      className="rmo-btn rmo-btn--accent"
                      onClick={() => openCompetitorEditor(item)}
                      disabled={saving}
                    >
                      {t("Adjust competitor")}
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
      ) : null}

      {activeSetupStep === "products" ? (
        <div className="rmo-surface rmo-setupStageSurface">
          <div className="rmo-head">
            <div>
              <h3 className="rmo-headTitle">{t("Master room rates")}</h3>
              <div className="rmo-headSubtitle">
                {t(
                  "Choose the one independent rate the RMS may optimize in each room category."
                )}
              </div>
            </div>
            <span
              className={`rmo-pill ${
                roomCategoryOptions.length > 0 &&
                configuredMasterRateCategoryIds.size === roomCategoryOptions.length
                  ? "rmo-pill--success"
                  : "rmo-pill--warn"
              }`}
            >
              {t("{configured}/{total} configured", {
                configured: configuredMasterRateCategoryIds.size,
                total: roomCategoryOptions.length,
              })}
            </span>
          </div>

          <div className="rmo-contentPad">
            <div className="rmo-banner rmo-banner--tight">
              <div className="rmo-bannerStack">
                <div className="rmo-bannerText">
                  <strong>{t("What the RMS changes:")}</strong>{" "}
                  {t(
                    "Recommendations target only the selected master rate. Derived non-refundable, meal, occupancy and channel products keep following their existing PMS rules."
                  )}
                </div>
                <div className="rmo-bannerText">
                  <strong>{t("Safe selection:")}</strong>{" "}
                  {t(
                    "Only active independent rates from the same room category are available here."
                  )}
                </div>
              </div>
            </div>
          </div>

          {loading ? (
            <div className="rmo-empty">{t("Loading master room rates...")}</div>
          ) : roomCategoryOptions.length === 0 ? (
            <div className="rmo-empty">{t("No hotel room categories found")}</div>
          ) : (
            <div className="rmo-tableWrap">
              <table className="rmo-table">
                <thead>
                  <tr>
                    <th>{t("Room category")}</th>
                    <th>{t("Master room rate")}</th>
                    <th>{t("Rate guardrails")}</th>
                    <th>{t("Derived products")}</th>
                    <th>{t("Policy")}</th>
                    <th>{t("Actions")}</th>
                  </tr>
                </thead>
                <tbody>
                  {roomCategoryOptions.map((category) => {
                    const categoryId = Number(category.id);
                    const currentPolicy = rateProductPolicyByCategory.get(categoryId) || null;
                    const derivationReadiness =
                      rateProductDerivationReadinessByCategory.get(categoryId) || null;
                    const derivationIssues = Array.isArray(derivationReadiness?.issues)
                      ? derivationReadiness.issues
                      : [];
                    const candidates = masterRoomRatesByCategory.get(categoryId) || [];
                    const draftValue = String(ratePolicyDrafts[String(categoryId)] || "");
                    const selectedMasterRate = eligibleMasterRoomRateById.get(
                      Number(draftValue)
                    );
                    const currentMasterIsUnavailable =
                      draftValue &&
                      !candidates.some((roomRate) => String(roomRate.id) === draftValue);
                    const isReady = configuredMasterRateCategoryIds.has(categoryId);
                    const isUnchanged =
                      currentPolicy?.is_active &&
                      Number(currentPolicy.master_room_rate_id) === Number(draftValue);
                    const isSaving = savingRatePolicyCategoryId === categoryId;
                    const currency = selectedMasterRate?.currency || "";
                    const floor = selectedMasterRate?.min_price_amount;
                    const ceiling = selectedMasterRate?.max_price_amount;

                    return (
                      <tr key={`master-rate-${categoryId}`}>
                        <td>
                          <strong>{category.name}</strong>
                        </td>
                        <td>
                          <select
                            className="rmo-select"
                            value={draftValue}
                            aria-label={t("Master room rate for {category}", {
                              category: category.name,
                            })}
                            disabled={isSaving || candidates.length === 0}
                            onChange={(event) =>
                              updateRatePolicyDraft(categoryId, event.target.value)
                            }
                          >
                            <option value="">
                              {candidates.length
                                ? t("Choose a master room rate")
                                : t("No independent active rates available")}
                            </option>
                            {currentMasterIsUnavailable ? (
                              <option value={draftValue} disabled>
                                {t("Current selection is no longer available")}
                              </option>
                            ) : null}
                            {candidates.map((roomRate) => (
                              <option key={roomRate.id} value={roomRate.id}>
                                {roomRate.name} ({roomRate.rate_code}) · {roomRate.currency}
                              </option>
                            ))}
                          </select>
                          {candidates.length === 0 ? (
                            <div className="rmo-formHelp">
                              {t(
                                "Create or activate an independent room rate with floor and ceiling for this category first."
                              )}
                            </div>
                          ) : null}
                        </td>
                        <td>
                          {selectedMasterRate ? (
                            <span>
                              {floor == null && ceiling == null
                                ? t("Not set")
                                : `${floor == null ? "-" : floor} - ${
                                    ceiling == null ? "-" : ceiling
                                  } ${currency}`}
                            </span>
                          ) : (
                            "-"
                          )}
                        </td>
                        <td>
                          <div className="rmo-cellStack" style={{ minWidth: 210 }}>
                            <div className="rmo-cellMain">
                              {derivationReadiness
                                ? t("{traced}/{total} linked to master", {
                                    traced: derivationReadiness.traced_to_master_count || 0,
                                    total:
                                      derivationReadiness.active_non_master_rates_count || 0,
                                  })
                                : t("Derivation check unavailable")}
                            </div>
                            {derivationReadiness?.coverage_date_from &&
                            derivationReadiness?.coverage_date_to ? (
                              <div className="rmo-cellMeta">
                                {t("Coverage: {from} to {to}", {
                                  from: derivationReadiness.coverage_date_from,
                                  to: derivationReadiness.coverage_date_to,
                                })}
                              </div>
                            ) : null}
                            {derivationIssues.slice(0, 2).map((issue, issueIndex) => (
                              <div
                                className="rmo-cellMeta rmo-deltaDown"
                                key={`${issue.room_rate_id || "policy"}-${issue.code}-${issueIndex}`}
                              >
                                {issue.name || issue.rate_code
                                  ? `${issue.name || issue.rate_code}: `
                                  : ""}
                                {getRateProductDerivationIssueLabel(issue.code, t)}
                              </div>
                            ))}
                            {derivationIssues.length > 2 ? (
                              <div className="rmo-cellMeta rmo-deltaDown">
                                {t("+{count} more issues", {
                                  count: derivationIssues.length - 2,
                                })}
                              </div>
                            ) : null}
                          </div>
                        </td>
                        <td>
                          <div className="rmo-inlineActions">
                            <span
                              className={`rmo-pill ${
                                isReady ? "rmo-pill--success" : "rmo-pill--warn"
                              }`}
                            >
                              {isReady ? t("Active") : t("Needs attention")}
                            </span>
                            {currentPolicy?.policy_version != null ? (
                              <span className="rmo-pill rmo-pill--soft">
                                {t("Version {version}", {
                                  version: currentPolicy.policy_version,
                                })}
                              </span>
                            ) : null}
                          </div>
                        </td>
                        <td>
                          <button
                            type="button"
                            className="rmo-btn rmo-btn--accent"
                            disabled={
                              isSaving ||
                              saving ||
                              !draftValue ||
                              !selectedMasterRate ||
                              isUnchanged
                            }
                            onClick={() => saveRateProductPolicy(category)}
                          >
                            {isSaving
                              ? t("Saving...")
                              : isUnchanged
                                ? t("Saved")
                                : currentPolicy
                                  ? t("Save change")
                                  : t("Save master rate")}
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}

          <div className="rmo-contentPad">
            <div className="rmo-strategySection">
              <div className="rmo-strategySectionHead">
                <div>
                  <h4 className="rmo-strategySectionTitle">
                    {t("Category price ladder")}
                  </h4>
                  <div className="rmo-strategySectionHelp">
                    {t(
                      "Define every commercial relationship explicitly. The RMS does not infer category order from names, IDs or current prices."
                    )}
                  </div>
                </div>
                <div className="rmo-inlineActions">
                  <span
                    className={`rmo-pill ${
                      categoryPriceLadderDirty
                        ? "rmo-pill--warn"
                        : categoryPriceLadder?.is_ready === true
                          ? "rmo-pill--success"
                          : "rmo-pill--warn"
                    }`}
                  >
                    {categoryPriceLadderNeedsClear
                      ? t("Needs attention")
                      : !categoryPriceLadderIsApplicable
                      ? t("Not applicable")
                      : categoryPriceLadderDirty
                      ? t("Unsaved changes")
                      : categoryPriceLadder?.is_ready === true
                        ? t("Ready")
                        : t("Needs attention")}
                  </span>
                  <button
                    type="button"
                    className="rmo-btn rmo-btn--accent"
                    onClick={saveCategoryPriceLadder}
                    disabled={
                      loading ||
                      saving ||
                      savingCategoryPriceLadder ||
                      (!categoryPriceLadderDirty &&
                        !categoryPriceLadderNeedsClear) ||
                      (categoryPriceLadderIsApplicable &&
                        categoryPriceLadderCategoryOptions.length === 0) ||
                      categoryPriceLadderLocalErrors.length > 0
                    }
                  >
                    {savingCategoryPriceLadder
                      ? t("Saving...")
                      : categoryPriceLadderNeedsClear &&
                          !categoryPriceLadderIsApplicable
                        ? t("Clear ladder")
                      : categoryPriceLadderDirty
                        ? t("Save category ladder")
                        : t("Saved")}
                  </button>
                </div>
              </div>

              <div className="rmo-banner rmo-banner--tight">
                <div className="rmo-bannerStack">
                  <div className="rmo-bannerText">
                    <strong>{t("How to read the graph:")}</strong>{" "}
                    {categoryPriceLadderIsApplicable
                      ? t(
                          "Each arrow runs from the lower-priced category to the category whose rule you are editing. Multiple independent ladders may each start at a root."
                        )
                      : t(
                          "The ladder becomes applicable when at least two product policies are active. No category order is inferred in the meantime."
                        )}
                  </div>
                  <div className="rmo-inlineActions">
                    {!categoryPriceLadderIsApplicable ? (
                      <span className="rmo-pill rmo-pill--soft">
                        {t("No active price ladder required")}
                      </span>
                    ) : (categoryPriceLadderDraft.items || []).some((item) =>
                      CATEGORY_PRICE_LADDER_MODES.has(String(item?.mode || ""))
                    ) ? (
                      (categoryPriceLadderDraft.items || [])
                        .filter((item) =>
                          CATEGORY_PRICE_LADDER_MODES.has(String(item?.mode || ""))
                        )
                        .map((item) => {
                          const roomCategoryId = Number(item.room_category_id);
                          const categoryName =
                            roomCategoryById.get(roomCategoryId)?.name || roomCategoryId;
                          const lowerCategoryName =
                            roomCategoryById.get(Number(item.lower_room_category_id))?.name ||
                            t("Choose lower category");
                          const gap = Number(item.min_gap_amount);
                          let relationshipLabel = `${categoryName} · ${t("root")}`;

                          if (item.mode === "enforce") {
                            relationshipLabel = `${lowerCategoryName} → ${categoryName} (+${
                              Number.isFinite(gap) ? gap : "?"
                            })`;
                          } else if (item.mode === "allow_inversion") {
                            relationshipLabel = `${lowerCategoryName} ⇢ ${categoryName} · ${t(
                              "exception"
                            )}`;
                          } else if (item.mode === "excluded") {
                            relationshipLabel = `${categoryName} · ${t("excluded")}`;
                          }

                          return (
                            <span
                              className="rmo-pill rmo-pill--soft"
                              key={`category-ladder-edge-${roomCategoryId}`}
                            >
                              {relationshipLabel}
                            </span>
                          );
                        })
                    ) : (
                      <span className="rmo-formHelp">
                        {t("No category relationships have been defined yet.")}
                      </span>
                    )}
                  </div>
                </div>
              </div>

              {categoryPriceLadderError ? (
                <div className="rmo-errorBox">{categoryPriceLadderError}</div>
              ) : null}

              {categoryPriceLadderBackendIssues.length > 0 ? (
                <div className="rmo-errorBox">
                  <strong>{t("Saved ladder issues")}</strong>
                  <ul>
                    {categoryPriceLadderBackendIssues.map((issue, issueIndex) => (
                      <li key={`category-ladder-backend-issue-${issueIndex}`}>
                        {formatCategoryPriceLadderIssue(
                          issue,
                          roomCategoryOptions,
                          t
                        )}
                      </li>
                    ))}
                  </ul>
                </div>
              ) : null}

              {categoryPriceLadderLocalErrors.length > 0 ? (
                <div className="rmo-errorBox">
                  <strong>{t("Complete these rules before saving")}</strong>
                  <ul>
                    {categoryPriceLadderLocalErrors.map((validationError) => (
                      <li key={validationError}>{validationError}</li>
                    ))}
                  </ul>
                </div>
              ) : null}

              {!categoryPriceLadderIsApplicable ? (
                <div className="rmo-empty">
                  {t(
                    "Activate a valid master product policy for a second category before defining commercial relationships."
                  )}
                </div>
              ) : categoryPriceLadderCategoryOptions.length === 0 ? (
                <div className="rmo-empty">{t("No hotel room categories found")}</div>
              ) : (
                <div className="rmo-tableWrap">
                  <table className="rmo-table">
                    <thead>
                      <tr>
                        <th>{t("Room category")}</th>
                        <th>{t("Relationship")}</th>
                        <th>{t("Lower category")}</th>
                        <th>{t("Minimum gap")}</th>
                        <th>{t("Exception reason")}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {categoryPriceLadderCategoryOptions.map((category) => {
                        const categoryId = Number(category.id);
                        const item = categoryPriceLadderDraftByCategory.get(categoryId) || {
                          room_category_id: categoryId,
                          mode: "",
                          lower_room_category_id: "",
                          min_gap_amount: "",
                          exception_reason: "",
                        };
                        const needsLowerCategory =
                          item.mode === "enforce" || item.mode === "allow_inversion";
                        const needsReason =
                          item.mode === "allow_inversion" || item.mode === "excluded";
                        const selectedMode = CATEGORY_PRICE_LADDER_MODE_OPTIONS.find(
                          (option) => option.value === item.mode
                        );
                        const draftMasterRateId = Number(
                          ratePolicyDrafts[String(categoryId)]
                        );
                        const masterCurrency =
                          eligibleMasterRoomRateById.get(draftMasterRateId)?.currency || "";

                        return (
                          <tr key={`category-ladder-rule-${categoryId}`}>
                            <td>
                              <strong>{category.name}</strong>
                            </td>
                            <td>
                              <select
                                className="rmo-select"
                                value={item.mode}
                                aria-label={t("Price relationship for {category}", {
                                  category: category.name,
                                })}
                                disabled={savingCategoryPriceLadder || saving}
                                onChange={(event) =>
                                  updateCategoryPriceLadderItem(
                                    categoryId,
                                    "mode",
                                    event.target.value
                                  )
                                }
                              >
                                <option value="">{t("Choose a relationship")}</option>
                                {CATEGORY_PRICE_LADDER_MODE_OPTIONS.map((option) => (
                                  <option key={option.value} value={option.value}>
                                    {t(option.label)}
                                  </option>
                                ))}
                              </select>
                              {selectedMode ? (
                                <div className="rmo-formHelp">{t(selectedMode.help)}</div>
                              ) : null}
                            </td>
                            <td>
                              {needsLowerCategory ? (
                                <select
                                  className="rmo-select"
                                  value={String(item.lower_room_category_id || "")}
                                  aria-label={t("Lower category for {category}", {
                                    category: category.name,
                                  })}
                                  disabled={savingCategoryPriceLadder || saving}
                                  onChange={(event) =>
                                    updateCategoryPriceLadderItem(
                                      categoryId,
                                      "lower_room_category_id",
                                      event.target.value
                                    )
                                  }
                                >
                                  <option value="">{t("Choose lower category")}</option>
                                  {categoryPriceLadderCategoryOptions
                                    .filter(
                                      (candidate) => Number(candidate.id) !== categoryId
                                    )
                                    .map((candidate) => {
                                      const candidateRule =
                                        categoryPriceLadderDraftByCategory.get(
                                          Number(candidate.id)
                                        );
                                      return (
                                        <option
                                          key={candidate.id}
                                          value={candidate.id}
                                          disabled={candidateRule?.mode === "excluded"}
                                        >
                                          {candidate.name}
                                          {candidateRule?.mode === "excluded"
                                            ? ` · ${t("excluded")}`
                                            : ""}
                                        </option>
                                      );
                                    })}
                                </select>
                              ) : (
                                <span className="rmo-formHelp">{t("Not applicable")}</span>
                              )}
                            </td>
                            <td>
                              {item.mode === "enforce" ? (
                                <div className="rmo-inputRow">
                                  <input
                                    className="rmo-input rmo-inputGrow"
                                    type="number"
                                    min="0"
                                    step="0.01"
                                    value={item.min_gap_amount}
                                    aria-label={t("Minimum price gap for {category}", {
                                      category: category.name,
                                    })}
                                    disabled={savingCategoryPriceLadder || saving}
                                    onChange={(event) =>
                                      updateCategoryPriceLadderItem(
                                        categoryId,
                                        "min_gap_amount",
                                        event.target.value
                                      )
                                    }
                                  />
                                  {masterCurrency ? (
                                    <span className="rmo-inputSuffix">{masterCurrency}</span>
                                  ) : null}
                                </div>
                              ) : (
                                <span className="rmo-formHelp">{t("Not applicable")}</span>
                              )}
                            </td>
                            <td>
                              {needsReason ? (
                                  <input
                                    className="rmo-input"
                                    type="text"
                                    maxLength={500}
                                    value={item.exception_reason}
                                  placeholder={
                                    item.mode === "excluded"
                                      ? t("Why is this category not comparable?")
                                      : t("Why may this inversion happen?")
                                  }
                                  aria-label={t("Exception reason for {category}", {
                                    category: category.name,
                                  })}
                                  disabled={savingCategoryPriceLadder || saving}
                                  onChange={(event) =>
                                    updateCategoryPriceLadderItem(
                                      categoryId,
                                      "exception_reason",
                                      event.target.value
                                    )
                                  }
                                />
                              ) : (
                                <span className="rmo-formHelp">{t("Not applicable")}</span>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        </div>
      ) : null}

      {RESOURCE_CONFIG.filter(
        (resource) =>
          resource.key !== "rate-product-policies" &&
          resource.key !== "standard-room-types" &&
          resource.key !== "room-category-mappings" &&
          resource.key !== "competitors" &&
          resource.key !== "competitor-room-type-mappings" &&
          resource.key === SETUP_RESOURCE_KEY_BY_STEP[activeSetupStep]
      ).map((resource) => {
        const rows = resources[resource.key] || [];
        return (
          <div key={resource.key} className="rmo-surface rmo-setupStageSurface">
            <div className="rmo-head">
              <div>
                <h3 className="rmo-headTitle">{t(resource.title)}</h3>
                <div className="rmo-headSubtitle">{t(resource.description)}</div>
              </div>
              {!resource.readOnly ? (
                <button
                  type="button"
                  className="rmo-btn rmo-btn--accent"
                  onClick={() =>
                    resource.key === "seasonality-templates"
                      ? openSeasonalityTemplateEditor()
                      : resource.key === "seasonality-periods"
                        ? openSeasonalityPeriodEditor()
                        : resource.key === "seasonality-profiles"
                          ? openSeasonalityProfileEditor()
                          : resource.key === "market-disruptions"
                            ? openMarketDisruptionEditor()
                        : openEditor(resource)
                  }
                  disabled={
                    loading ||
                    ((resource.key === "seasonality-periods" ||
                      resource.key === "seasonality-profiles") &&
                      (seasonalityTemplateOptions.length === 0 ||
                        (resource.key === "seasonality-profiles" &&
                          roomCategoryOptions.length === 0)))
                  }
                >
                  {t(resource.createLabel || "Add row")}
                </button>
              ) : null}
            </div>
            {resource.key === "strategy-settings" ? (
              <div className="rmo-contentPad">
                {strategyGuardrailConflicts.length > 0 ? (
                  <div className="rmo-errorBox" style={{ marginBottom: 12 }}>
                    <strong>{t("Strategy activation is blocked.")}</strong>{" "}
                    {formatStrategyGuardrailConflict(
                      strategyGuardrailConflicts[0],
                      roomCategoryOptions,
                      t
                    )}
                    {strategyGuardrailConflicts.length > 1
                      ? ` ${t("Review all {count} active conflicts before continuing.", {
                          count: strategyGuardrailConflicts.length,
                        })}`
                      : ""}
                  </div>
                ) : null}
                <div className="rmo-banner rmo-banner--tight">
                  <div className="rmo-bannerStack">
                    <div className="rmo-bannerText">
                      <strong>{t("Recommended setup:")}</strong>{" "}
                      {t(
                        "Create one active rule for the whole hotel as your base strategy, then add extra active rules only for room categories that need different behavior."
                      )}
                    </div>
                    <div className="rmo-bannerText">
                      <strong>{t("How decisions are made:")}</strong>{" "}
                      {t(
                        "The RMS uses the room-category rule first. If there is no active category rule, it uses the whole-hotel rule instead."
                      )}
                    </div>
                    <div className="rmo-bannerText">
                      {t(
                        "Keep just one active rule per room category. The selected strategy influences the goal, price limits, minimum-stay freedom and planning horizon used by the RMS recommendation."
                      )}
                    </div>
                  </div>
                </div>
              </div>
            ) : null}
            {resource.key === "seasonality-templates" ? (
              <div className="rmo-contentPad">
                <div className="rmo-banner rmo-banner--tight">
                  <div className="rmo-bannerStack">
                    <div className="rmo-bannerText">
                      <strong>{t("What a template is:")}</strong>{" "}
                      {t(
                        "One reusable season label such as low season, shoulder season or peak season."
                      )}
                    </div>
                    <div className="rmo-bannerText">
                      <strong>{t("How it is used later:")}</strong>{" "}
                      {t(
                        "In the next sections you assign date ranges to that template and, if needed, refine behavior by room type and day of week."
                      )}
                    </div>
                    <div className="rmo-bannerText">
                      <strong>{t("Tip:")}</strong>{" "}
                      {t(
                        "Keep the templates broad and simple. A child should be able to understand the season names at first glance."
                      )}
                    </div>
                  </div>
                </div>
              </div>
            ) : null}
            {resource.key === "seasonality-periods" ? (
              <div className="rmo-contentPad">
                <div className="rmo-banner rmo-banner--tight">
                  <div className="rmo-bannerStack">
                    <div className="rmo-bannerText">
                      <strong>{t("What this section does:")}</strong>{" "}
                      {t("Here you place each season template on the real calendar.")}
                    </div>
                    <div className="rmo-bannerText">
                      <strong>{t("How priority works:")}</strong>{" "}
                      {t("If two periods overlap, the higher priority wins on those dates.")}
                    </div>
                    <div className="rmo-bannerText">
                      <strong>{t("Tip:")}</strong>{" "}
                      {t(
                        "Use one normal-priority base season for most dates and only raise priority for special overrides such as fairs, Easter or local events."
                      )}
                    </div>
                    {seasonalityTemplateOptions.length === 0 ? (
                      <div className="rmo-bannerText">
                        <strong>{t("First create a season template")}</strong>{" "}
                        {t("before adding date ranges here.")}
                      </div>
                    ) : null}
                  </div>
                </div>
              </div>
            ) : null}
            {resource.key === "seasonality-profiles" ? (
              <div className="rmo-contentPad">
                <div className="rmo-banner rmo-banner--tight">
                  <div className="rmo-bannerStack">
                    <div className="rmo-bannerText">
                      <strong>{t("What a profile is:")}</strong>{" "}
                      {t("A refinement inside one season template.")}
                    </div>
                    <div className="rmo-bannerText">
                      <strong>{t("How it works:")}</strong>{" "}
                      {t(
                        "First the RMS finds the season by date. Then it can refine that season for one of your room categories and, if needed, one day of the week."
                      )}
                    </div>
                    <div className="rmo-bannerText">
                      <strong>{t("Tip:")}</strong>{" "}
                      {t(
                        "Create a profile only when one room category, or one weekday inside that room category, behaves differently from the main season."
                      )}
                    </div>
                    {seasonalityTemplateOptions.length === 0 ? (
                      <div className="rmo-bannerText">
                        <strong>{t("First create a season template")}</strong>{" "}
                        {t("before defining profile refinements.")}
                      </div>
                    ) : null}
                  </div>
                </div>
              </div>
            ) : null}
            {resource.key === "market-disruptions" ? (
              <div className="rmo-contentPad">
                <div className="rmo-banner rmo-banner--tight">
                  <div className="rmo-bannerStack">
                    <div className="rmo-bannerText">
                      <strong>{t("What this is for:")}</strong>{" "}
                      {t(
                        "Use disruptions for unusual situations that sit on top of normal seasonality, not instead of it."
                      )}
                    </div>
                    <div className="rmo-bannerText">
                      <strong>{t("How it works:")}</strong>{" "}
                      {t(
                        "Each active disruption adds a temporary factor that makes the RMS firmer or softer during the chosen date range."
                      )}
                    </div>
                    <div className="rmo-bannerText">
                      <strong>{t("Good examples:")}</strong>{" "}
                      {t(
                        "A big concert week, a transport strike, major road works nearby or a sudden weather shock."
                      )}
                    </div>
                  </div>
                </div>
              </div>
            ) : null}
            {loading ? (
              <div className="rmo-empty">
                {t("Loading {title}...", { title: t(resource.title).toLowerCase() })}
              </div>
            ) : rows.length === 0 ? (
              <div className="rmo-empty">
                {t("No rows yet for {title}.", { title: t(resource.title).toLowerCase() })}
              </div>
            ) : (
              <div className="rmo-tableWrap">
                <table className="rmo-table">
                  <thead>
                    <tr>
                      {resource.columns.map((column) => (
                        <th key={`${resource.key}-${column}`}>
                          {t(resource.columnLabels?.[column] || column)}
                        </th>
                      ))}
                      {!resource.readOnly ? <th>{t("Actions")}</th> : null}
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((item) => (
                      <tr key={`${resource.key}-${item.id}`}>
                        {resource.columns.map((column) => {
                          return (
                            <td key={`${resource.key}-${item.id}-${column}`}>
                              {renderResourceCell(resource, column, item)}
                            </td>
                          );
                        })}
                        {!resource.readOnly ? (
                          <td>
                            <div className="rmo-inlineActions">
                              <button
                                type="button"
                                className="rmo-btn"
                                onClick={() =>
                                  resource.key === "seasonality-templates"
                                      ? openSeasonalityTemplateEditor(item)
                                      : resource.key === "seasonality-periods"
                                        ? openSeasonalityPeriodEditor(item)
                                        : resource.key === "seasonality-profiles"
                                          ? openSeasonalityProfileEditor(item)
                                          : resource.key === "market-disruptions"
                                            ? openMarketDisruptionEditor(item)
                                       : openEditor(resource, item)
                                  }
                                >
                                  {resource.key === "seasonality-templates" ||
                                  resource.key === "seasonality-periods" ||
                                  resource.key === "seasonality-profiles" ||
                                  resource.key === "market-disruptions"
                                    ? t(resource.editLabel || "Edit row")
                                    : t(resource.editLabel || "Advanced edit")}
                              </button>
                              <button type="button" className="rmo-btn rmo-btn--danger" onClick={() => removeItem(resource, item)}>
                                {t("Delete")}
                              </button>
                            </div>
                          </td>
                        ) : null}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        );
      })}

      <div className="rmo-setupFooter">
        <div className="rmo-setupFooterCopy">
          <strong>{t(activeSetupStepData?.title || "Setup")}</strong>
          <span>{t(activeSetupStepData?.detail || "")}</span>
        </div>
        <div className="rmo-setupFooterActions">
          {previousSetupStep ? (
            <button
              type="button"
              className="rmo-btn"
              onClick={() => selectSetupStep(previousSetupStep.key)}
            >
              {t("Previous: {step}", { step: t(previousSetupStep.shortTitle) })}
            </button>
          ) : null}
          {nextSetupStep ? (
            <button
              type="button"
              className="rmo-btn rmo-btn--accent"
              onClick={() => selectSetupStep(nextSetupStep.key)}
            >
              {t("Next: {step}", { step: t(nextSetupStep.shortTitle) })}
            </button>
          ) : (
            <button
              type="button"
              className="rmo-btn rmo-btn--accent"
              onClick={onContinue}
              disabled={typeof onContinue !== "function"}
            >
              {t("Continue to Signals & Forecast")}
            </button>
          )}
        </div>
      </div>

      {competitorEditor ? (
        <div className="rmo-modalBackdrop" onClick={closeCompetitorEditor}>
          <div className="rmo-modal rmo-modal--narrow" onClick={(event) => event.stopPropagation()}>
            <div className="rmo-modalHead">
              <div>
                <h3 className="rmo-headTitle" style={{ marginBottom: 0 }}>
                  {t("Adjust competitor")}
                </h3>
                <div className="rmo-headSubtitle">
                  {t("Decide how much this hotel should influence your market benchmark.")}
                </div>
              </div>
              <button type="button" className="rmo-btn" onClick={closeCompetitorEditor}>
                {t("Close")}
              </button>
            </div>
            {competitorEditorError ? (
              <div className="rmo-errorBox">{competitorEditorError}</div>
            ) : null}
            <div className="rmo-modalBody">
              <div className="rmo-competitorEditorHero">
                <div className="rmo-competitorEditorEyebrow">
                  {t("Competitor in extraction scope")}
                </div>
                <div className="rmo-competitorEditorTitle">
                  {competitorDraft?.source_name || competitorEditor?.source_name || t("Competitor")}
                </div>
                <div className="rmo-competitorEditorText">
                  {t(
                    "This hotel already belongs to your market scope. Here you only decide its RMS importance, optional reference details and whether it should influence the optimizer now."
                  )}
                </div>
                <div className="rmo-strategySummaryPills">
                  <span className="rmo-pill rmo-pill--soft">
                    {t("Hotel code {code}", {
                      code: competitorDraft?.hotel_code || competitorEditor?.hotel_code,
                    })}
                  </span>
                  <span className="rmo-pill rmo-pill--soft">
                    {competitorDraft?.source_address ||
                      competitorEditor?.source_address ||
                      t("Address not available")}
                  </span>
                </div>
              </div>

              <div className="rmo-strategySection">
                <div className="rmo-strategySectionHead">
                  <h4 className="rmo-strategySectionTitle">
                    {t("1. How important should this competitor be?")}
                  </h4>
                  <div className="rmo-strategySectionHelp">
                    {t(
                      "Use a bigger number when this hotel is one of your closest real references. Keep it smaller when it is only a secondary reference."
                    )}
                  </div>
                </div>
                <div className="rmo-formGrid">
                  <div className="rmo-formField">
                    <label htmlFor="competitor-weight">
                      {t("Importance in market benchmark")}
                    </label>
                    <div className="rmo-inputRow">
                      <input
                        id="competitor-weight"
                        className="rmo-input rmo-inputGrow"
                        type="number"
                        inputMode="decimal"
                        min="0.1"
                        step="0.1"
                        placeholder="1"
                        value={competitorDraft?.competitor_weight ?? ""}
                        onChange={(event) =>
                          updateCompetitorField("competitor_weight", event.target.value)
                        }
                      />
                      <span className="rmo-inputSuffix">x</span>
                    </div>
                    <div className="rmo-formHelp">
                      {t(
                        "Example: 1 means normal importance, 2 means this hotel counts about twice as much, and 0.5 means it counts about half as much."
                      )}
                    </div>
                  </div>
                </div>
              </div>

              <div className="rmo-strategySection">
                <div className="rmo-strategySectionHead">
                  <h4 className="rmo-strategySectionTitle">
                    {t("2. Optional reference details")}
                  </h4>
                  <div className="rmo-strategySectionHelp">
                    {t(
                      "These details help you review the competitor set with more context. Leave them empty if you do not need them."
                    )}
                  </div>
                </div>
                <div className="rmo-formGrid">
                  <div className="rmo-formField">
                    <label htmlFor="competitor-distance">{t("Distance from your hotel")}</label>
                    <div className="rmo-inputRow">
                      <input
                        id="competitor-distance"
                        className="rmo-input rmo-inputGrow"
                        type="number"
                        inputMode="decimal"
                        min="0"
                        step="0.1"
                        placeholder="1.5"
                        value={competitorDraft?.distance_km ?? ""}
                        onChange={(event) =>
                          updateCompetitorField("distance_km", event.target.value)
                        }
                      />
                      <span className="rmo-inputSuffix">km</span>
                    </div>
                    <div className="rmo-formHelp">
                      {t(
                        "Optional. A nearby hotel is often a stronger reference than one that is much farther away."
                      )}
                    </div>
                  </div>

                  <div className="rmo-formField">
                    <label htmlFor="competitor-rating">{t("Public review score")}</label>
                    <div className="rmo-inputRow">
                      <input
                        id="competitor-rating"
                        className="rmo-input rmo-inputGrow"
                        type="number"
                        inputMode="decimal"
                        min="0"
                        max="5"
                        step="0.1"
                        placeholder="4.3"
                        value={competitorDraft?.rating ?? ""}
                        onChange={(event) => updateCompetitorField("rating", event.target.value)}
                      />
                      <span className="rmo-inputSuffix">/5</span>
                    </div>
                    <div className="rmo-formHelp">
                      {t(
                        "Optional. This helps you remember whether the competitor is positioned above, below or close to your hotel in public perception."
                      )}
                    </div>
                  </div>

                  <div className="rmo-formField">
                    <label htmlFor="competitor-reviews">{t("Number of reviews")}</label>
                    <input
                      id="competitor-reviews"
                      className="rmo-input"
                      type="number"
                      inputMode="numeric"
                      min="0"
                      step="1"
                      placeholder="1250"
                      value={competitorDraft?.review_count ?? ""}
                      onChange={(event) =>
                        updateCompetitorField("review_count", event.target.value)
                      }
                    />
                    <div className="rmo-formHelp">
                      {t(
                        "Optional. More reviews usually make that public rating more trustworthy."
                      )}
                    </div>
                  </div>
                </div>
              </div>

              <div className="rmo-strategySection">
                <div className="rmo-strategySectionHead">
                  <h4 className="rmo-strategySectionTitle">
                    {t("3. Should this competitor influence the RMS now?")}
                  </h4>
                  <div className="rmo-strategySectionHelp">
                    {t(
                      "Turn the competitor off when you want to keep it in scope but temporarily exclude it from the market benchmark."
                    )}
                  </div>
                </div>
                <div className="rmo-toggleGrid rmo-toggleGrid--single">
                  <button
                    type="button"
                    className={`rmo-toggleCard${competitorDraft?.is_active ? " isActive" : ""}`}
                    aria-pressed={competitorDraft?.is_active ? "true" : "false"}
                    onClick={() =>
                      updateCompetitorField("is_active", !competitorDraft?.is_active)
                    }
                  >
                    <div className="rmo-toggleCardHead">
                        <div className="rmo-toggleCardTitle">
                          {competitorDraft?.is_active
                            ? t("This competitor currently influences the RMS")
                            : t("This competitor is currently ignored by the RMS")}
                        </div>
                      <span
                        className={`rmo-pill ${
                          competitorDraft?.is_active
                            ? "rmo-pill--success"
                            : "rmo-pill--soft"
                        }`}
                      >
                        {competitorDraft?.is_active ? t("Active") : t("Inactive")}
                      </span>
                    </div>
                      <div className="rmo-choiceCardText">
                        {competitorDraft?.is_active
                          ? t("Keep this on when the hotel is a valid benchmark reference.")
                          : t("Turn this back on when you want its prices to count again.")}
                      </div>
                  </button>
                </div>
              </div>
            </div>
            <div className="rmo-modalFoot">
              <button type="button" className="rmo-btn" onClick={closeCompetitorEditor}>
                {t("Cancel")}
              </button>
              <button
                type="button"
                className="rmo-btn rmo-btn--accent"
                onClick={saveCompetitor}
                disabled={saving}
              >
                {saving ? t("Saving...") : t("Save competitor settings")}
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {seasonalityTemplateDraft ? (
        <div className="rmo-modalBackdrop" onClick={closeSeasonalityTemplateEditor}>
          <div className="rmo-modal rmo-modal--narrow" onClick={(event) => event.stopPropagation()}>
            <div className="rmo-modalHead">
              <div>
                <h3 className="rmo-headTitle" style={{ marginBottom: 0 }}>
                  {seasonalityTemplateEditor ? t("Edit seasonality template") : t("Create seasonality template")}
                </h3>
                <div className="rmo-headSubtitle">
                  Give the RMS a simple season name, the expected demand tone and a default price posture.
                </div>
              </div>
              <button type="button" className="rmo-btn" onClick={closeSeasonalityTemplateEditor}>
                {t("Close")}
              </button>
            </div>
            {seasonalityTemplateEditorError ? (
              <div className="rmo-errorBox">{seasonalityTemplateEditorError}</div>
            ) : null}
            <div className="rmo-modalBody">
              <div className="rmo-strategySummary">
                <div className="rmo-strategySummaryEyebrow">{t("Reusable season template")}</div>
                <div className="rmo-strategySummaryTitle">
                  {seasonalityTemplateDraft?.name || "Name this season clearly"}
                </div>
                <div className="rmo-strategySummaryText">
                  A template is the label the RMS will reuse later when you define real date ranges.
                  Think in simple commercial seasons such as low season, city-event season or summer peak.
                </div>
                <div className="rmo-strategySummaryPills">
                  <span className="rmo-pill rmo-pill--soft">
                    {seasonalityTemplateDraft?.is_active ? "Active template" : "Inactive template"}
                  </span>
                  <span className="rmo-pill rmo-pill--soft">
                    {seasonalityTemplateDraft?.default_price_index
                      ? `Price index ${seasonalityTemplateDraft.default_price_index}`
                      : "No default price index"}
                  </span>
                </div>
              </div>

              <div className="rmo-strategySection">
                <div className="rmo-strategySectionHead">
                  <h4 className="rmo-strategySectionTitle">1. Name this season</h4>
                  <div className="rmo-strategySectionHelp">
                    Use a short label that any hotel owner would understand at a glance.
                  </div>
                </div>
                <div className="rmo-formGrid">
                  <div className="rmo-formField">
                    <label htmlFor="seasonality-template-name">{t("Season name")}</label>
                    <input
                      id="seasonality-template-name"
                      className="rmo-input"
                      type="text"
                      placeholder="High season"
                      value={seasonalityTemplateDraft?.name ?? ""}
                      onChange={(event) =>
                        updateSeasonalityTemplateField("name", event.target.value)
                      }
                    />
                    <div className="rmo-formHelp">
                      Examples: Low season, Shoulder season, Summer peak, Trade fair season.
                    </div>
                  </div>

                  <div className="rmo-formField">
                    <label htmlFor="seasonality-template-description">{t("Short note")}</label>
                    <textarea
                      id="seasonality-template-description"
                      className="rmo-textarea"
                      rows={3}
                      placeholder="Why this season matters, which kind of dates it covers, or any short business note."
                      value={seasonalityTemplateDraft?.description ?? ""}
                      onChange={(event) =>
                        updateSeasonalityTemplateField("description", event.target.value)
                      }
                    />
                    <div className="rmo-formHelp">
                      Optional. Use this for your internal explanation, not for a technical formula.
                    </div>
                  </div>
                </div>
              </div>

              <div className="rmo-strategySection">
                <div className="rmo-strategySectionHead">
                  <h4 className="rmo-strategySectionTitle">2. How strong is demand in this season?</h4>
                  <div className="rmo-strategySectionHelp">
                    Pick the demand tone that best describes this season. If your hotel uses a different business term, you can write your own label.
                  </div>
                </div>
                <div className="rmo-choiceGrid">
                  {SEASONALITY_TEMPLATE_DEMAND_OPTIONS.map((option) => {
                    const isActive =
                      seasonalityTemplateDraft?.demand_level_choice === option.value;
                    return (
                      <button
                        key={option.value}
                        type="button"
                        className={`rmo-choiceCard${isActive ? " isActive" : ""}`}
                        onClick={() =>
                          setSeasonalityTemplateDraft((previous) => ({
                            ...(previous || normalizeSeasonalityTemplateDraft({})),
                            demand_level_choice: option.value,
                            demand_level_custom: "",
                          }))
                        }
                      >
                        <div className="rmo-choiceCardTitle">{t(option.label)}</div>
                        <div className="rmo-choiceCardText">{t(option.help)}</div>
                      </button>
                    );
                  })}

                  <button
                    type="button"
                    className={`rmo-choiceCard${
                      seasonalityTemplateDraft?.demand_level_choice === "custom"
                        ? " isActive"
                        : ""
                    }`}
                    onClick={() =>
                      updateSeasonalityTemplateField("demand_level_choice", "custom")
                    }
                  >
                    <div className="rmo-choiceCardTitle">{t("Custom label")}</div>
                    <div className="rmo-choiceCardText">
                      Use your own business wording if the suggested labels do not fit.
                    </div>
                  </button>
                </div>

                {seasonalityTemplateDraft?.demand_level_choice === "custom" ? (
                  <div className="rmo-formGrid">
                    <div className="rmo-formField">
                      <label htmlFor="seasonality-template-demand-custom">
                        Your custom demand label
                      </label>
                      <input
                        id="seasonality-template-demand-custom"
                        className="rmo-input"
                        type="text"
                        placeholder="Conference peak"
                        value={seasonalityTemplateDraft?.demand_level_custom ?? ""}
                        onChange={(event) =>
                          updateSeasonalityTemplateField(
                            "demand_level_custom",
                            event.target.value
                          )
                        }
                      />
                      <div className="rmo-formHelp">
                        Optional custom wording used as the default demand label for this season.
                      </div>
                    </div>
                  </div>
                ) : null}
              </div>

              <div className="rmo-strategySection">
                <div className="rmo-strategySectionHead">
                  <h4 className="rmo-strategySectionTitle">3. What is the normal price posture here?</h4>
                  <div className="rmo-strategySectionHelp">
                    This is the default price position for the season before day-by-day details refine it later.
                  </div>
                </div>
                <div className="rmo-formGrid">
                  <div className="rmo-formField">
                    <label htmlFor="seasonality-template-price-index">{t("Default price index")}</label>
                    <div className="rmo-inputRow">
                      <input
                        id="seasonality-template-price-index"
                        className="rmo-input rmo-inputGrow"
                        type="number"
                        inputMode="decimal"
                        min="0"
                        step="0.1"
                        placeholder="100"
                        value={seasonalityTemplateDraft?.default_price_index ?? ""}
                        onChange={(event) =>
                          updateSeasonalityTemplateField(
                            "default_price_index",
                            event.target.value
                          )
                        }
                      />
                      <span className="rmo-inputSuffix">{t("index")}</span>
                    </div>
                    <div className="rmo-formHelp">
                      Example: 100 means normal price posture, 110 means firmer than normal, 90 means softer than normal.
                    </div>
                  </div>
                </div>
              </div>

              <div className="rmo-strategySection">
                <div className="rmo-strategySectionHead">
                  <h4 className="rmo-strategySectionTitle">4. Should this template be available now?</h4>
                  <div className="rmo-strategySectionHelp">
                    Inactive templates stay saved, but they will not appear as active season options for the rest of the setup.
                  </div>
                </div>
                <div className="rmo-toggleGrid rmo-toggleGrid--single">
                  <button
                    type="button"
                    className={`rmo-toggleCard${
                      seasonalityTemplateDraft?.is_active ? " isActive" : ""
                    }`}
                    aria-pressed={seasonalityTemplateDraft?.is_active ? "true" : "false"}
                    onClick={() =>
                      updateSeasonalityTemplateField(
                        "is_active",
                        !seasonalityTemplateDraft?.is_active
                      )
                    }
                  >
                    <div className="rmo-toggleCardHead">
                      <div className="rmo-toggleCardTitle">
                        {seasonalityTemplateDraft?.is_active
                          ? "This template is active"
                          : "This template is inactive"}
                      </div>
                      <span
                        className={`rmo-pill ${
                          seasonalityTemplateDraft?.is_active
                            ? "rmo-pill--success"
                            : "rmo-pill--soft"
                        }`}
                      >
                        {seasonalityTemplateDraft?.is_active ? "Active" : "Inactive"}
                      </span>
                    </div>
                    <div className="rmo-choiceCardText">
                      {seasonalityTemplateDraft?.is_active
                        ? "Keep it active when this season should be available for live RMS setup."
                        : "Use inactive when you want to prepare the season without using it yet."}
                    </div>
                  </button>
                </div>
              </div>
            </div>
            <div className="rmo-modalFoot">
              <button type="button" className="rmo-btn" onClick={closeSeasonalityTemplateEditor}>
                {t("Cancel")}
              </button>
              <button
                type="button"
                className="rmo-btn rmo-btn--accent"
                onClick={saveSeasonalityTemplate}
                disabled={saving}
              >
                {saving ? t("Saving...") : t("Save season template")}
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {seasonalityPeriodDraft ? (
        <div className="rmo-modalBackdrop" onClick={closeSeasonalityPeriodEditor}>
          <div className="rmo-modal rmo-modal--narrow" onClick={(event) => event.stopPropagation()}>
            <div className="rmo-modalHead">
              <div>
                <h3 className="rmo-headTitle" style={{ marginBottom: 0 }}>
                  {seasonalityPeriodEditor ? t("Edit seasonality period") : t("Create seasonality period")}
                </h3>
                <div className="rmo-headSubtitle">
                  Assign one season template to a real date range and decide which period should win if dates overlap.
                </div>
              </div>
              <button type="button" className="rmo-btn" onClick={closeSeasonalityPeriodEditor}>
                {t("Close")}
              </button>
            </div>
            {seasonalityPeriodEditorError ? (
              <div className="rmo-errorBox">{seasonalityPeriodEditorError}</div>
            ) : null}
            <div className="rmo-modalBody">
              <div className="rmo-strategySummary">
                <div className="rmo-strategySummaryEyebrow">{t("Calendar season rule")}</div>
                <div className="rmo-strategySummaryTitle">{selectedSeasonalityTemplateName}</div>
                <div className="rmo-strategySummaryText">
                  This period tells the RMS exactly when a season starts and ends. If another period overlaps it, the higher priority wins.
                </div>
                <div className="rmo-strategySummaryPills">
                  <span className="rmo-pill rmo-pill--soft">
                    {selectedSeasonalityRangeDays
                      ? `${selectedSeasonalityRangeDays} day${
                          selectedSeasonalityRangeDays === 1 ? "" : "s"
                        }`
                      : "Choose dates"}
                  </span>
                  <span className="rmo-pill rmo-pill--soft">
                    {seasonalityPeriodDraft?.is_active ? "Active period" : "Inactive period"}
                  </span>
                  <span className="rmo-pill rmo-pill--soft">
                    {t(selectedSeasonalityPriorityMeta.label)}
                  </span>
                </div>
              </div>

              <div className="rmo-strategySection">
                <div className="rmo-strategySectionHead">
                  <h4 className="rmo-strategySectionTitle">1. Which season is this?</h4>
                  <div className="rmo-strategySectionHelp">
                    Pick the season template that best describes this date range.
                  </div>
                </div>
                <div className="rmo-formGrid">
                  <div className="rmo-formField">
                    <label htmlFor="seasonality-period-template">{t("Season template")}</label>
                    <select
                      id="seasonality-period-template"
                      className="rmo-select"
                      value={seasonalityPeriodDraft?.seasonality_template_id ?? ""}
                      onChange={(event) =>
                        updateSeasonalityPeriodField("seasonality_template_id", event.target.value)
                      }
                    >
                      <option value="">{t("Choose a season template")}</option>
                      {seasonalityTemplateOptions.map((item) => (
                        <option key={item.id} value={item.id}>
                          {item.name}
                          {item.is_active ? "" : " (inactive)"}
                        </option>
                      ))}
                    </select>
                    <div className="rmo-formHelp">
                      Example: Low season, Shoulder season, Summer peak or Fair week.
                    </div>
                  </div>
                </div>
              </div>

              <div className="rmo-strategySection">
                <div className="rmo-strategySectionHead">
                  <h4 className="rmo-strategySectionTitle">2. When does it apply?</h4>
                  <div className="rmo-strategySectionHelp">
                    Choose the exact dates covered by this season period.
                  </div>
                </div>
                <div className="rmo-formGrid">
                  <div className="rmo-formField">
                    <label htmlFor="seasonality-period-start">{t("Start date")}</label>
                    <input
                      id="seasonality-period-start"
                      className="rmo-input"
                      type="date"
                      value={seasonalityPeriodDraft?.start_date ?? ""}
                      onChange={(event) =>
                        updateSeasonalityPeriodField("start_date", event.target.value)
                      }
                    />
                    <div className="rmo-formHelp">
                      The first date that should behave like this season.
                    </div>
                  </div>

                  <div className="rmo-formField">
                    <label htmlFor="seasonality-period-end">{t("End date")}</label>
                    <input
                      id="seasonality-period-end"
                      className="rmo-input"
                      type="date"
                      value={seasonalityPeriodDraft?.end_date ?? ""}
                      onChange={(event) =>
                        updateSeasonalityPeriodField("end_date", event.target.value)
                      }
                    />
                    <div className="rmo-formHelp">
                      The last date included in the season. The range is inclusive.
                    </div>
                  </div>
                </div>
                {seasonalityPeriodDraft?.start_date && seasonalityPeriodDraft?.end_date ? (
                  <div className="rmo-banner rmo-banner--tight">
                    <div className="rmo-bannerText">
                      {t("This period runs from {from} to {to}.", {
                        from: formatLocalizedSeasonDateLabel(
                          seasonalityPeriodDraft.start_date,
                          locale,
                          t
                        ),
                        to: formatLocalizedSeasonDateLabel(
                          seasonalityPeriodDraft.end_date,
                          locale,
                          t
                        ),
                      })}
                    </div>
                  </div>
                ) : null}
              </div>

              <div className="rmo-strategySection">
                <div className="rmo-strategySectionHead">
                  <h4 className="rmo-strategySectionTitle">3. What happens if periods overlap?</h4>
                  <div className="rmo-strategySectionHelp">
                    Priority decides which season wins when two date ranges cover the same date.
                  </div>
                </div>
                <div className="rmo-choiceGrid">
                  {SEASONALITY_PRIORITY_OPTIONS.map((option) => {
                    const isActive = seasonalityPeriodDraft?.priority_choice === option.value;
                    return (
                      <button
                        key={option.value}
                        type="button"
                        className={`rmo-choiceCard${isActive ? " isActive" : ""}`}
                        onClick={() =>
                          setSeasonalityPeriodDraft((previous) => ({
                            ...(previous || normalizeSeasonalityPeriodDraft({})),
                            priority_choice: option.value,
                            priority_value: option.value,
                          }))
                        }
                      >
                        <div className="rmo-choiceCardTitle">{t(option.label)}</div>
                        <div className="rmo-choiceCardText">{t(option.help)}</div>
                      </button>
                    );
                  })}

                  <button
                    type="button"
                    className={`rmo-choiceCard${
                      seasonalityPeriodDraft?.priority_choice === "custom" ? " isActive" : ""
                    }`}
                    onClick={() => updateSeasonalityPeriodField("priority_choice", "custom")}
                  >
                    <div className="rmo-choiceCardTitle">{t("Custom priority")}</div>
                    <div className="rmo-choiceCardText">
                      Use your own whole number if your business needs a different override level.
                    </div>
                  </button>
                </div>

                <div className="rmo-formGrid">
                  <div className="rmo-formField">
                    <label htmlFor="seasonality-period-priority">{t("Priority number")}</label>
                    <input
                      id="seasonality-period-priority"
                      className="rmo-input"
                      type="number"
                      inputMode="numeric"
                      min="1"
                      step="1"
                      placeholder="100"
                      value={seasonalityPeriodDraft?.priority_value ?? ""}
                      onChange={(event) => {
                        const nextValue = event.target.value;
                        const matchingPriority = SEASONALITY_PRIORITY_OPTIONS.find(
                          (option) => option.value === nextValue
                        );
                        setSeasonalityPeriodDraft((previous) => ({
                          ...(previous || normalizeSeasonalityPeriodDraft({})),
                          priority_value: nextValue,
                          priority_choice: matchingPriority ? matchingPriority.value : "custom",
                        }));
                      }}
                    />
                    <div className="rmo-formHelp">
                      Bigger number = stronger override. Example: 300 beats 100, and 100 beats 25.
                    </div>
                  </div>
                </div>
              </div>

              <div className="rmo-strategySection">
                <div className="rmo-strategySectionHead">
                  <h4 className="rmo-strategySectionTitle">4. Should this period be live now?</h4>
                  <div className="rmo-strategySectionHelp">
                    Keep it active if the RMS should use it immediately. Save inactive if you are still preparing future seasons.
                  </div>
                </div>
                <div className="rmo-toggleGrid rmo-toggleGrid--single">
                  <button
                    type="button"
                    className={`rmo-toggleCard${seasonalityPeriodDraft?.is_active ? " isActive" : ""}`}
                    aria-pressed={seasonalityPeriodDraft?.is_active ? "true" : "false"}
                    onClick={() =>
                      updateSeasonalityPeriodField(
                        "is_active",
                        !seasonalityPeriodDraft?.is_active
                      )
                    }
                  >
                    <div className="rmo-toggleCardHead">
                      <div className="rmo-toggleCardTitle">
                        {seasonalityPeriodDraft?.is_active
                          ? "This date range is active"
                          : "This date range is inactive"}
                      </div>
                      <span
                        className={`rmo-pill ${
                          seasonalityPeriodDraft?.is_active
                            ? "rmo-pill--success"
                            : "rmo-pill--soft"
                        }`}
                      >
                        {seasonalityPeriodDraft?.is_active ? "Active" : "Inactive"}
                      </span>
                    </div>
                    <div className="rmo-choiceCardText">
                      {seasonalityPeriodDraft?.is_active
                        ? "The RMS will consider this period when it resolves seasonality."
                        : "Keep it saved without letting it affect live RMS calculations yet."}
                    </div>
                  </button>
                </div>
              </div>
            </div>
            <div className="rmo-modalFoot">
              <button type="button" className="rmo-btn" onClick={closeSeasonalityPeriodEditor}>
                {t("Cancel")}
              </button>
              <button
                type="button"
                className="rmo-btn rmo-btn--accent"
                onClick={saveSeasonalityPeriod}
                disabled={saving}
              >
                {saving ? t("Saving...") : t("Save season period")}
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {seasonalityProfileDraft ? (
        <div className="rmo-modalBackdrop" onClick={closeSeasonalityProfileEditor}>
          <div className="rmo-modal rmo-modal--narrow" onClick={(event) => event.stopPropagation()}>
            <div className="rmo-modalHead">
              <div>
                <h3 className="rmo-headTitle" style={{ marginBottom: 0 }}>
                  {seasonalityProfileEditor ? t("Edit seasonality profile") : t("Create seasonality profile")}
                </h3>
                <div className="rmo-headSubtitle">
                  Refine one season for one room category and, if needed, one day of the week.
                </div>
              </div>
              <button type="button" className="rmo-btn" onClick={closeSeasonalityProfileEditor}>
                {t("Close")}
              </button>
            </div>
            {seasonalityProfileEditorError ? (
              <div className="rmo-errorBox">{seasonalityProfileEditorError}</div>
            ) : null}
            <div className="rmo-modalBody">
              <div className="rmo-strategySummary">
                <div className="rmo-strategySummaryEyebrow">{t("Season refinement rule")}</div>
                <div className="rmo-strategySummaryTitle">
                  {selectedSeasonalityProfileTemplateName}
                </div>
                <div className="rmo-strategySummaryText">
                  Think of a profile as a smaller rule inside one season. It can say that one of your
                  room categories, or one weekday inside that category, behaves differently from the
                  main season.
                </div>
                <div className="rmo-strategySummaryPills">
                  <span className="rmo-pill rmo-pill--soft">
                    {selectedSeasonalityProfileRoomCategoryName}
                  </span>
                  <span className="rmo-pill rmo-pill--soft">
                    {t(selectedSeasonalityProfileDowLabel.label)}
                  </span>
                  <span className="rmo-pill rmo-pill--soft">
                    {typeof selectedSeasonalityProfileDemandLabel === "string"
                      ? selectedSeasonalityProfileDemandLabel
                      : t(selectedSeasonalityProfileDemandLabel.label)}
                  </span>
                  <span className="rmo-pill rmo-pill--soft">
                    {seasonalityProfileDraft?.is_active ? "Active profile" : "Inactive profile"}
                  </span>
                </div>
              </div>

              <div className="rmo-banner rmo-banner--tight">
                <div className="rmo-bannerStack">
                  <div className="rmo-bannerText">
                    <strong>{t("Decision order:")}</strong>{" "}
                    {t(
                      "The RMS first identifies the season from `Seasonality Periods`, then looks here for a more specific refinement."
                    )}
                  </div>
                  <div className="rmo-bannerText">
                    A profile only matters inside the selected season template. If you leave a field
                    empty, the RMS keeps the template default for that part.
                  </div>
                </div>
              </div>

              <div className="rmo-strategySection">
                <div className="rmo-strategySectionHead">
                  <h4 className="rmo-strategySectionTitle">1. Which season are you refining?</h4>
                  <div className="rmo-strategySectionHelp">
                    Start by choosing the season template this rule belongs to.
                  </div>
                </div>
                <div className="rmo-formGrid">
                  <div className="rmo-formField">
                    <label htmlFor="seasonality-profile-template">{t("Season template")}</label>
                    <select
                      id="seasonality-profile-template"
                      className="rmo-select"
                      value={seasonalityProfileDraft?.seasonality_template_id ?? ""}
                      onChange={(event) =>
                        updateSeasonalityProfileField("seasonality_template_id", event.target.value)
                      }
                    >
                      <option value="">{t("Choose a season template")}</option>
                      {seasonalityTemplateOptions.map((item) => (
                        <option key={item.id} value={item.id}>
                          {item.name}
                          {item.is_active ? "" : " (inactive)"}
                        </option>
                      ))}
                    </select>
                    <div className="rmo-formHelp">
                      Example: High season, Shoulder season, City fair season or Holiday peak.
                    </div>
                  </div>
                </div>
              </div>

              <div className="rmo-strategySection">
                <div className="rmo-strategySectionHead">
                  <h4 className="rmo-strategySectionTitle">2. Who does this refinement apply to?</h4>
                  <div className="rmo-strategySectionHelp">
                    Choose which of your room categories this season rule belongs to. Then decide if
                    it applies every day or only on one weekday.
                  </div>
                </div>
                <div className="rmo-formGrid">
                  <div className="rmo-formField">
                    <label htmlFor="seasonality-profile-room-category">{t("Room category")}</label>
                    <select
                      id="seasonality-profile-room-category"
                      className="rmo-select"
                      value={seasonalityProfileDraft?.room_category_id ?? ""}
                      onChange={(event) =>
                        updateSeasonalityProfileField("room_category_id", event.target.value)
                      }
                    >
                      <option value="">{t("Choose a room category")}</option>
                      {roomCategoryOptions.map((item) => (
                        <option key={item.id} value={item.id}>
                          {item.name}
                        </option>
                      ))}
                    </select>
                    <div className="rmo-formHelp">
                      Pick the exact room category whose seasonal behaviour you want to refine.
                    </div>
                  </div>

                  <div className="rmo-formField">
                    <label htmlFor="seasonality-profile-dow">{t("Day of week")}</label>
                    <select
                      id="seasonality-profile-dow"
                      className="rmo-select"
                      value={seasonalityProfileDraft?.dow ?? ""}
                      onChange={(event) =>
                        updateSeasonalityProfileField("dow", event.target.value)
                      }
                    >
                      {SEASONALITY_DOW_OPTIONS.map((option) => (
                        <option key={option.value || "all"} value={option.value}>
                          {t(option.label)}
                        </option>
                      ))}
                    </select>
                    <div className="rmo-formHelp">
                      Leave this on every day unless one weekday behaves differently inside the same season.
                    </div>
                  </div>
                </div>
              </div>

              <div className="rmo-strategySection">
                <div className="rmo-strategySectionHead">
                  <h4 className="rmo-strategySectionTitle">3. What changes for this slice?</h4>
                  <div className="rmo-strategySectionHelp">
                    Only fill the parts that should differ from the template default.
                  </div>
                </div>

                <div className="rmo-choiceGrid">
                  {SEASONALITY_TEMPLATE_DEMAND_OPTIONS.map((option) => {
                    const isActive = seasonalityProfileDraft?.demand_level_choice === option.value;
                    return (
                      <button
                        key={option.value}
                        type="button"
                        className={`rmo-choiceCard${isActive ? " isActive" : ""}`}
                        onClick={() =>
                          setSeasonalityProfileDraft((previous) => ({
                            ...(previous || normalizeSeasonalityProfileDraft({})),
                            demand_level_choice: option.value,
                            demand_level_custom: "",
                          }))
                        }
                      >
                        <div className="rmo-choiceCardTitle">{t(option.label)}</div>
                        <div className="rmo-choiceCardText">{t(option.help)}</div>
                      </button>
                    );
                  })}

                  <button
                    type="button"
                    className={`rmo-choiceCard${
                      seasonalityProfileDraft?.demand_level_choice === "custom"
                        ? " isActive"
                        : ""
                    }`}
                    onClick={() => updateSeasonalityProfileField("demand_level_choice", "custom")}
                  >
                    <div className="rmo-choiceCardTitle">{t("Custom demand tone")}</div>
                    <div className="rmo-choiceCardText">
                      Use your own wording if the normal labels do not fit this specific slice.
                    </div>
                  </button>

                  <button
                    type="button"
                    className={`rmo-choiceCard${
                      !seasonalityProfileDraft?.demand_level_choice ? " isActive" : ""
                    }`}
                    onClick={() =>
                      setSeasonalityProfileDraft((previous) => ({
                        ...(previous || normalizeSeasonalityProfileDraft({})),
                        demand_level_choice: "",
                        demand_level_custom: "",
                      }))
                    }
                  >
                    <div className="rmo-choiceCardTitle">{t("Keep template demand tone")}</div>
                    <div className="rmo-choiceCardText">
                      Use no override when the template already describes demand correctly.
                    </div>
                  </button>
                </div>

                {seasonalityProfileDraft?.demand_level_choice === "custom" ? (
                  <div className="rmo-formGrid">
                    <div className="rmo-formField">
                      <label htmlFor="seasonality-profile-demand-custom">{t("Custom demand tone")}</label>
                      <input
                        id="seasonality-profile-demand-custom"
                        className="rmo-input"
                        type="text"
                        placeholder="Conference-heavy weekdays"
                        value={seasonalityProfileDraft?.demand_level_custom ?? ""}
                        onChange={(event) =>
                          updateSeasonalityProfileField("demand_level_custom", event.target.value)
                        }
                      />
                      <div className="rmo-formHelp">
                        Use short business wording that explains what is special here.
                      </div>
                    </div>
                  </div>
                ) : null}

                <div className="rmo-formGrid">
                  <div className="rmo-formField">
                    <label htmlFor="seasonality-profile-price-index">{t("Price index override")}</label>
                    <div className="rmo-inputRow">
                      <input
                        id="seasonality-profile-price-index"
                        className="rmo-input rmo-inputGrow"
                        type="number"
                        inputMode="decimal"
                        min="0"
                        step="0.1"
                        placeholder="Leave blank to keep template default"
                        value={seasonalityProfileDraft?.expected_price_index ?? ""}
                        onChange={(event) =>
                          updateSeasonalityProfileField("expected_price_index", event.target.value)
                        }
                      />
                      <span className="rmo-inputSuffix">{t("index")}</span>
                    </div>
                    <div className="rmo-formHelp">
                      Example: 110 means this slice is normally firmer than the base season. Leave blank to inherit the template index.
                    </div>
                  </div>

                  <div className="rmo-formField">
                    <label htmlFor="seasonality-profile-occupancy">{t("Expected occupancy")}</label>
                    <div className="rmo-inputRow">
                      <input
                        id="seasonality-profile-occupancy"
                        className="rmo-input rmo-inputGrow"
                        type="number"
                        inputMode="decimal"
                        min="0"
                        max="100"
                        step="0.1"
                        placeholder="Leave blank if not needed"
                        value={seasonalityProfileDraft?.expected_occupancy_pct ?? ""}
                        onChange={(event) =>
                          updateSeasonalityProfileField("expected_occupancy_pct", event.target.value)
                        }
                      />
                      <span className="rmo-inputSuffix">%</span>
                    </div>
                    <div className="rmo-formHelp">
                      Final occupancy expectation for this profile. When pace is configured, a 100% booked share points to this value; example: 84 means an 84% final target.
                    </div>
                  </div>
                </div>
              </div>

              <div className="rmo-strategySection">
                <div className="rmo-strategySectionHead">
                  <h4 className="rmo-strategySectionTitle">4. Should this profile be active now?</h4>
                  <div className="rmo-strategySectionHelp">
                    Save it inactive if you want to prepare the refinement without using it yet.
                  </div>
                </div>
                <div className="rmo-toggleGrid rmo-toggleGrid--single">
                  <button
                    type="button"
                    className={`rmo-toggleCard${seasonalityProfileDraft?.is_active ? " isActive" : ""}`}
                    aria-pressed={seasonalityProfileDraft?.is_active ? "true" : "false"}
                    onClick={() =>
                      updateSeasonalityProfileField(
                        "is_active",
                        !seasonalityProfileDraft?.is_active
                      )
                    }
                  >
                    <div className="rmo-toggleCardHead">
                      <div className="rmo-toggleCardTitle">
                        {seasonalityProfileDraft?.is_active
                          ? "This profile is active"
                          : "This profile is inactive"}
                      </div>
                      <span
                        className={`rmo-pill ${
                          seasonalityProfileDraft?.is_active
                            ? "rmo-pill--success"
                            : "rmo-pill--soft"
                        }`}
                      >
                        {seasonalityProfileDraft?.is_active ? "Active" : "Inactive"}
                      </span>
                    </div>
                    <div className="rmo-choiceCardText">
                      {seasonalityProfileDraft?.is_active
                        ? "The RMS can use this refinement when the selected season, type and weekday match."
                        : "Keep it saved without letting it affect live RMS calculations yet."}
                    </div>
                  </button>
                </div>
              </div>
            </div>
            <div className="rmo-modalFoot">
              <button type="button" className="rmo-btn" onClick={closeSeasonalityProfileEditor}>
                {t("Cancel")}
              </button>
              <button
                type="button"
                className="rmo-btn rmo-btn--accent"
                onClick={saveSeasonalityProfile}
                disabled={saving}
              >
                {saving ? t("Saving...") : t("Save season profile")}
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {marketDisruptionDraft ? (
        <div className="rmo-modalBackdrop" onClick={closeMarketDisruptionEditor}>
          <div className="rmo-modal rmo-modal--narrow" onClick={(event) => event.stopPropagation()}>
            <div className="rmo-modalHead">
              <div>
                <h3 className="rmo-headTitle" style={{ marginBottom: 0 }}>
                  {marketDisruptionEditor ? t("Edit market disruption") : t("Create market disruption")}
                </h3>
                <div className="rmo-headSubtitle">
                  Tell the RMS when an unusual market situation should make pricing firmer or softer.
                </div>
              </div>
              <button type="button" className="rmo-btn" onClick={closeMarketDisruptionEditor}>
                {t("Close")}
              </button>
            </div>
            {marketDisruptionEditorError ? (
              <div className="rmo-errorBox">{marketDisruptionEditorError}</div>
            ) : null}
            <div className="rmo-modalBody">
              <div className="rmo-strategySummary">
                <div className="rmo-strategySummaryEyebrow">{t("Temporary market shock")}</div>
                <div className="rmo-strategySummaryTitle">
                  {marketDisruptionDraft?.name || "Name this disruption clearly"}
                </div>
                <div className="rmo-strategySummaryText">
                  Use this only for unusual situations that sit on top of your normal seasonality, such
                  as a major event week, a transport strike or disruptive works nearby.
                </div>
                <div className="rmo-strategySummaryPills">
                  <span className="rmo-pill rmo-pill--soft">
                    {t(selectedMarketDisruptionTypeMeta.label)}
                  </span>
                  <span className="rmo-pill rmo-pill--soft">
                    {t(selectedMarketDisruptionDirectionMeta.label)}
                  </span>
                  <span className="rmo-pill rmo-pill--soft">
                    {t(selectedMarketDisruptionLevelMeta.label)}
                  </span>
                  <span className="rmo-pill rmo-pill--soft">
                    {selectedMarketDisruptionRangeDays
                      ? `${selectedMarketDisruptionRangeDays} day${selectedMarketDisruptionRangeDays === 1 ? "" : "s"}`
                      : "Choose dates"}
                  </span>
                </div>
              </div>

              <div className="rmo-banner rmo-banner--tight">
                <div className="rmo-bannerStack">
                  <div className="rmo-bannerText">
                    <strong>{t("What the RMS does:")}</strong>{" "}
                    {t(
                      "It turns each active disruption into a temporary price factor around 1.0 during the selected dates."
                    )}
                  </div>
                  <div className="rmo-bannerText">
                    Upward disruptions make the optimizer firmer. Downward disruptions make it softer.
                  </div>
                </div>
              </div>

              <div className="rmo-strategySection">
                <div className="rmo-strategySectionHead">
                  <h4 className="rmo-strategySectionTitle">1. What is happening?</h4>
                  <div className="rmo-strategySectionHelp">
                    Use a plain business description that a hotel owner can recognize immediately.
                  </div>
                </div>
                <div className="rmo-formGrid">
                  <div className="rmo-formField">
                    <label htmlFor="market-disruption-name">{t("Disruption name")}</label>
                    <input
                      id="market-disruption-name"
                      className="rmo-input"
                      type="text"
                      placeholder="City music festival"
                      value={marketDisruptionDraft?.name ?? ""}
                      onChange={(event) =>
                        updateMarketDisruptionField("name", event.target.value)
                      }
                    />
                    <div className="rmo-formHelp">
                      Example: Airport strike, Easter procession week, promenade works, trade fair.
                    </div>
                  </div>
                </div>

                <div className="rmo-choiceGrid">
                  {MARKET_DISRUPTION_TYPE_OPTIONS.map((option) => {
                    const isActive = marketDisruptionDraft?.disruption_type === option.value;
                    return (
                      <button
                        key={option.value}
                        type="button"
                        className={`rmo-choiceCard${isActive ? " isActive" : ""}`}
                        onClick={() => updateMarketDisruptionField("disruption_type", option.value)}
                      >
                        <div className="rmo-choiceCardTitle">{t(option.label)}</div>
                        <div className="rmo-choiceCardText">{t(option.help)}</div>
                      </button>
                    );
                  })}
                </div>
              </div>

              <div className="rmo-strategySection">
                <div className="rmo-strategySectionHead">
                  <h4 className="rmo-strategySectionTitle">2. When does it affect the market?</h4>
                  <div className="rmo-strategySectionHelp">
                    The RMS will only use this disruption inside this date range.
                  </div>
                </div>
                <div className="rmo-formGrid">
                  <div className="rmo-formField">
                    <label htmlFor="market-disruption-start">{t("Start date")}</label>
                    <input
                      id="market-disruption-start"
                      className="rmo-input"
                      type="date"
                      value={marketDisruptionDraft?.start_date ?? ""}
                      onChange={(event) =>
                        updateMarketDisruptionField("start_date", event.target.value)
                      }
                    />
                  </div>
                  <div className="rmo-formField">
                    <label htmlFor="market-disruption-end">{t("End date")}</label>
                    <input
                      id="market-disruption-end"
                      className="rmo-input"
                      type="date"
                      value={marketDisruptionDraft?.end_date ?? ""}
                      onChange={(event) =>
                        updateMarketDisruptionField("end_date", event.target.value)
                      }
                    />
                  </div>
                </div>
              </div>

              <div className="rmo-strategySection">
                <div className="rmo-strategySectionHead">
                  <h4 className="rmo-strategySectionTitle">3. Should prices go up or down?</h4>
                  <div className="rmo-strategySectionHelp">
                    Pick the price direction this disruption should push the optimizer towards.
                  </div>
                </div>
                <div className="rmo-choiceGrid">
                  {MARKET_DISRUPTION_DIRECTION_OPTIONS.map((option) => {
                    const isActive = marketDisruptionDraft?.impact_direction === option.value;
                    return (
                      <button
                        key={option.value}
                        type="button"
                        className={`rmo-choiceCard${isActive ? " isActive" : ""}`}
                        onClick={() => updateMarketDisruptionField("impact_direction", option.value)}
                      >
                        <div className="rmo-choiceCardTitle">{t(option.label)}</div>
                        <div className="rmo-choiceCardText">{t(option.help)}</div>
                      </button>
                    );
                  })}
                </div>
              </div>

              <div className="rmo-strategySection">
                <div className="rmo-strategySectionHead">
                  <h4 className="rmo-strategySectionTitle">4. How strong is the effect?</h4>
                  <div className="rmo-strategySectionHelp">
                    Choose the disruption strength, then say how confident you are in that judgement.
                  </div>
                </div>
                <div className="rmo-choiceGrid">
                  {MARKET_DISRUPTION_LEVEL_OPTIONS.map((option) => {
                    const isActive = marketDisruptionDraft?.impact_level === option.value;
                    return (
                      <button
                        key={option.value}
                        type="button"
                        className={`rmo-choiceCard${isActive ? " isActive" : ""}`}
                        onClick={() => updateMarketDisruptionField("impact_level", option.value)}
                      >
                        <div className="rmo-choiceCardTitle">{t(option.label)}</div>
                        <div className="rmo-choiceCardText">{t(option.help)}</div>
                      </button>
                    );
                  })}
                </div>
                <div className="rmo-formGrid">
                  <div className="rmo-formField">
                    <label htmlFor="market-disruption-confidence">{t("Confidence in this estimate")}</label>
                    <div className="rmo-inputRow">
                      <input
                        id="market-disruption-confidence"
                        className="rmo-input rmo-inputGrow"
                        type="number"
                        inputMode="decimal"
                        min="0"
                        max="100"
                        step="1"
                        placeholder="100"
                        value={marketDisruptionDraft?.confidence ?? ""}
                        onChange={(event) =>
                          updateMarketDisruptionField("confidence", event.target.value)
                        }
                      />
                      <span className="rmo-inputSuffix">%</span>
                    </div>
                    <div className="rmo-formHelp">
                      100 means you are very sure. 50 means the disruption is real, but the size of the
                      effect is still uncertain.
                    </div>
                  </div>
                </div>
              </div>

              <div className="rmo-strategySection">
                <div className="rmo-strategySectionHead">
                  <h4 className="rmo-strategySectionTitle">5. Optional business notes</h4>
                  <div className="rmo-strategySectionHelp">
                    Add context that helps you remember why this disruption exists.
                  </div>
                </div>
                <div className="rmo-formGrid">
                  <div className="rmo-formField">
                    <label htmlFor="market-disruption-source">{t("Source")}</label>
                    <input
                      id="market-disruption-source"
                      className="rmo-input"
                      type="text"
                      placeholder="City hall calendar"
                      value={marketDisruptionDraft?.source ?? ""}
                      onChange={(event) =>
                        updateMarketDisruptionField("source", event.target.value)
                      }
                    />
                    <div className="rmo-formHelp">
                      Optional. Example: city agenda, organizer site, transport notice.
                    </div>
                  </div>

                  <div className="rmo-formField">
                    <label htmlFor="market-disruption-notes">{t("Notes")}</label>
                    <textarea
                      id="market-disruption-notes"
                      className="rmo-textarea"
                      rows={3}
                      placeholder="Short note about why this should affect pricing."
                      value={marketDisruptionDraft?.notes ?? ""}
                      onChange={(event) =>
                        updateMarketDisruptionField("notes", event.target.value)
                      }
                    />
                    <div className="rmo-formHelp">
                      Optional. Use plain language, not a technical formula.
                    </div>
                  </div>
                </div>
              </div>

              <div className="rmo-strategySection">
                <div className="rmo-strategySectionHead">
                  <h4 className="rmo-strategySectionTitle">6. Should this disruption be active now?</h4>
                  <div className="rmo-strategySectionHelp">
                    Inactive disruptions stay saved, but the RMS ignores them until you turn them on.
                  </div>
                </div>
                <div className="rmo-toggleGrid rmo-toggleGrid--single">
                  <button
                    type="button"
                    className={`rmo-toggleCard${marketDisruptionDraft?.is_active ? " isActive" : ""}`}
                    aria-pressed={marketDisruptionDraft?.is_active ? "true" : "false"}
                    onClick={() =>
                      updateMarketDisruptionField("is_active", !marketDisruptionDraft?.is_active)
                    }
                  >
                    <div className="rmo-toggleCardHead">
                      <div className="rmo-toggleCardTitle">
                        {marketDisruptionDraft?.is_active
                          ? "This disruption is active"
                          : "This disruption is inactive"}
                      </div>
                      <span
                        className={`rmo-pill ${
                          marketDisruptionDraft?.is_active
                            ? "rmo-pill--success"
                            : "rmo-pill--soft"
                        }`}
                      >
                        {marketDisruptionDraft?.is_active ? "Active" : "Inactive"}
                      </span>
                    </div>
                    <div className="rmo-choiceCardText">
                      {marketDisruptionDraft?.is_active
                        ? "The RMS can use this disruption during the selected dates."
                        : "Keep it saved without letting it affect live RMS calculations yet."}
                    </div>
                  </button>
                </div>
              </div>
            </div>
            <div className="rmo-modalFoot">
              <button type="button" className="rmo-btn" onClick={closeMarketDisruptionEditor}>
                {t("Cancel")}
              </button>
              <button
                type="button"
                className="rmo-btn rmo-btn--accent"
                onClick={saveMarketDisruption}
                disabled={saving}
              >
                {saving ? t("Saving...") : t("Save disruption")}
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {editor ? (
        <div className="rmo-modalBackdrop" onClick={closeEditor}>
          <div className="rmo-modal" onClick={(event) => event.stopPropagation()}>
            <div className="rmo-modalHead">
              <div>
                <h3 className="rmo-headTitle" style={{ marginBottom: 0 }}>
                  {isStrategyEditor
                    ? `${t(editor.item ? "Edit strategy" : "Create strategy")} · ${t(editor.resource.title)}`
                    : `${t(editor.item ? "Advanced edit" : "Create row")} · ${t(editor.resource.title)}`}
                </h3>
                <div className="rmo-headSubtitle">
                  {isStrategyEditor
                    ? t(
                        "Tell the optimizer where this rule applies, what it should prioritize and which limits it must respect."
                      )
                    : t("Advanced configuration editor for this section.")}
                </div>
              </div>
              <button type="button" className="rmo-btn" onClick={closeEditor}>
                {t("Close")}
              </button>
            </div>
            {editorError ? <div className="rmo-errorBox">{editorError}</div> : null}
            <div className="rmo-modalBody">
              {isStrategyEditor ? (
                <div className="rmo-strategyEditor">
                  <div className="rmo-strategySummary">
                    <div className="rmo-strategySummaryEyebrow">{t("What this rule is doing")}</div>
                    <div className="rmo-strategySummaryTitle">
                      {t(strategyModeMeta.label)} {t("for")}{" "}
                      {strategyDraft?.scope_type === "room_category" && selectedRoomCategoryName
                        ? selectedRoomCategoryName
                        : t(scopeTypeMeta.label).toLowerCase()}
                    </div>
                    <div className="rmo-strategySummaryText">
                      {t(strategyModeMeta.help)}{" "}
                      {strategyDraft?.scope_type === "room_category"
                        ? t("This rule will only affect the selected room category.")
                        : t(
                            "This rule will affect every room category unless a more specific room-category rule exists."
                          )}
                    </div>
                    <div className="rmo-strategySummaryPills">
                      <span className="rmo-pill rmo-pill--soft">{t(scopeTypeMeta.label)}</span>
                      <span className="rmo-pill rmo-pill--soft">{t(strategyModeMeta.label)}</span>
                      <span className="rmo-pill rmo-pill--soft">
                        {t("{count}-day daily view", {
                          count: strategyDraft?.detailed_horizon_days || "90",
                        })}
                      </span>
                      <span className="rmo-pill rmo-pill--soft">
                        {t(strategyDraft?.is_active ? "Active now" : "Saved as inactive")}
                      </span>
                    </div>
                  </div>

                  <div className="rmo-banner rmo-banner--tight">
                    <div className="rmo-bannerStack">
                      <div className="rmo-bannerText">
                        <strong>{t("How the RMS uses strategy rules:")}</strong>{" "}
                        {t(
                          "It first looks for one active rule for the room category. If it does not find one, it falls back to the active whole-hotel rule."
                        )}
                      </div>
                      <div className="rmo-bannerText">
                        {t(
                          "Start with one whole-hotel rule, then add category rules only where you really want different behavior. Keep only one active rule per room category."
                        )}
                      </div>
                    </div>
                  </div>

                  <section className="rmo-strategySection">
                    <div className="rmo-strategySectionHead">
                      <h4 className="rmo-strategySectionTitle">
                        {t("1. Where should this rule apply?")}
                      </h4>
                      <div className="rmo-strategySectionHelp">
                        {t(
                          "Start by choosing whether this rule is for the whole hotel or for one room category only."
                        )}
                      </div>
                    </div>
                    <div className="rmo-choiceGrid">
                      {scopeTypeOptions.map((option) => {
                        const optionMeta = getOptionMeta(
                          SCOPE_TYPE_META,
                          option,
                          String(option || "")
                        );
                        const isActive = strategyDraft?.scope_type === option;
                        return (
                          <button
                            key={option}
                            type="button"
                            className={`rmo-choiceCard${isActive ? " isActive" : ""}`}
                            onClick={() =>
                              setStrategyDraft((previous) => ({
                                ...(previous || normalizeStrategyDraft(strategyResource?.template || {})),
                                scope_type: option,
                                scope_id: option === "hotel" ? "" : previous?.scope_id || "",
                                min_adr_target:
                                  option === "hotel" ? "" : previous?.min_adr_target || "",
                                floor_price: option === "hotel" ? "" : previous?.floor_price || "",
                              }))
                            }
                          >
                            <div className="rmo-choiceCardTitle">{t(optionMeta.label)}</div>
                            <div className="rmo-choiceCardText">{t(optionMeta.help)}</div>
                          </button>
                        );
                      })}
                    </div>
                    {strategyDraft?.scope_type === "room_category" ? (
                      <div className="rmo-formField">
                        <label htmlFor="strategy-scope-id">{t("Room category")}</label>
                        <select
                          id="strategy-scope-id"
                          className="rmo-select"
                          value={strategyDraft?.scope_id ?? ""}
                          onChange={(event) => updateStrategyField("scope_id", event.target.value)}
                        >
                          <option value="">{t("Choose a room category")}</option>
                          {roomCategoryOptions.map((category) => (
                            <option key={category.id} value={category.id}>
                              {category.name}
                            </option>
                          ))}
                        </select>
                        <div className="rmo-formHelp">
                          {t(
                            "Use this when one room category needs a different strategy than the rest of the hotel."
                          )}
                        </div>
                      </div>
                    ) : null}
                  </section>

                  <section className="rmo-strategySection">
                    <div className="rmo-strategySectionHead">
                      <h4 className="rmo-strategySectionTitle">
                        {t("2. What should the optimizer prioritize?")}
                      </h4>
                      <div className="rmo-strategySectionHelp">
                        {t(
                          "Pick the commercial goal that best describes how you want this rule to behave."
                        )}
                      </div>
                    </div>
                    <div className="rmo-choiceGrid">
                      {strategyModeOptions.map((option) => {
                        const optionMeta = getOptionMeta(
                          STRATEGY_MODE_META,
                          option,
                          String(option || "")
                        );
                        const isActive = strategyDraft?.strategy_mode === option;
                        return (
                          <button
                            key={option}
                            type="button"
                            className={`rmo-choiceCard${isActive ? " isActive" : ""}`}
                            onClick={() => updateStrategyField("strategy_mode", option)}
                          >
                            <div className="rmo-choiceCardTitle">{t(optionMeta.label)}</div>
                            <div className="rmo-choiceCardText">{t(optionMeta.help)}</div>
                          </button>
                        );
                      })}
                    </div>
                  </section>

                  <section className="rmo-strategySection">
                    <div className="rmo-strategySectionHead">
                      <h4 className="rmo-strategySectionTitle">{t("3. Price guardrails")}</h4>
                      <div className="rmo-strategySectionHelp">
                        {t(
                          "These limits keep recommendations inside your commercial comfort zone. Leave a field empty if you do not want to force that rule."
                        )}
                      </div>
                    </div>
                    <div className="rmo-formGrid">
                      <div className="rmo-formField">
                        <label htmlFor="strategy-target-occupancy">{t("Target occupancy")}</label>
                        <div className="rmo-inputRow">
                          <input
                            id="strategy-target-occupancy"
                            className="rmo-input rmo-inputGrow"
                            type="number"
                            inputMode="decimal"
                            min="0"
                            max="100"
                            step="0.1"
                            placeholder="78"
                            value={strategyDraft?.target_occupancy_pct ?? ""}
                            onChange={(event) =>
                              updateStrategyField("target_occupancy_pct", event.target.value)
                            }
                          />
                          <span className="rmo-inputSuffix">%</span>
                        </div>
                        <div className="rmo-formHelp">
                          {t(
                            "Optional. Example: 78 means the RMS will try to finish around 78% occupancy."
                          )}
                        </div>
                      </div>

                      <div className="rmo-formField">
                        <label htmlFor="strategy-min-adr">{t("Minimum average daily rate")}</label>
                        <input
                          id="strategy-min-adr"
                          className="rmo-input"
                          type="number"
                          inputMode="decimal"
                          min="0"
                          step="0.01"
                          placeholder="120"
                          value={strategyDraft?.min_adr_target ?? ""}
                          disabled={!isRoomCategoryStrategy}
                          onChange={(event) => updateStrategyField("min_adr_target", event.target.value)}
                        />
                        <div className="rmo-formHelp">
                          {isRoomCategoryStrategy
                            ? t(
                                "Optional hard floor for this room category. No recommended day should go below this amount."
                              )
                            : t(
                                "This field is only available in room-category rules, so it does not push every category in the hotel to the same minimum."
                              )}
                        </div>
                      </div>

                      <div className="rmo-formField">
                        <label htmlFor="strategy-price-index">{t("Desired price position vs market")}</label>
                        <div className="rmo-inputRow">
                          <input
                            id="strategy-price-index"
                            className="rmo-input rmo-inputGrow"
                            type="number"
                            inputMode="decimal"
                            min="0"
                            step="0.1"
                            placeholder="100"
                            value={strategyDraft?.desired_price_index ?? ""}
                            onChange={(event) =>
                              updateStrategyField("desired_price_index", event.target.value)
                            }
                          />
                          <span className="rmo-inputSuffix">{t("index")}</span>
                        </div>
                        <div className="rmo-formHelp">
                          {t(
                            "Optional. 100 means match the market. 105 means aim about 5% above. 95 means aim about 5% below."
                          )}
                        </div>
                      </div>

                      <div className="rmo-formField">
                        <label htmlFor="strategy-floor-price">{t("Lowest allowed price")}</label>
                        <input
                          id="strategy-floor-price"
                          className="rmo-input"
                          type="number"
                          inputMode="decimal"
                          min="0"
                          step="0.01"
                          placeholder="95"
                          value={strategyDraft?.floor_price ?? ""}
                          disabled={!isRoomCategoryStrategy}
                          onChange={(event) => updateStrategyField("floor_price", event.target.value)}
                        />
                        <div className="rmo-formHelp">
                          {isRoomCategoryStrategy
                            ? t(
                                "Optional absolute safety floor. No single recommended day should go below this amount."
                              )
                            : t(
                                "This field is only available in room-category rules, so one hotel rule cannot set the same absolute minimum for all categories."
                              )}
                        </div>
                      </div>

                      <div className="rmo-formField">
                        <label htmlFor="strategy-ceiling-price">{t("Highest allowed price")}</label>
                        <input
                          id="strategy-ceiling-price"
                          className="rmo-input"
                          type="number"
                          inputMode="decimal"
                          min="0"
                          step="0.01"
                          placeholder="320"
                          value={strategyDraft?.ceiling_price ?? ""}
                          onChange={(event) => updateStrategyField("ceiling_price", event.target.value)}
                        />
                        <div className="rmo-formHelp">
                          {t(
                            "Optional hard ceiling. The RMS should not recommend a higher selling price than this."
                          )}
                        </div>
                      </div>
                    </div>
                  </section>

                  <section className="rmo-strategySection">
                    <div className="rmo-strategySectionHead">
                      <h4 className="rmo-strategySectionTitle">
                        {t("4. Minimum stay rules")}
                      </h4>
                      <div className="rmo-strategySectionHelp">
                        {t(
                          "Decide whether the RMS may tighten or relax minimum stay and how far it may go."
                        )}
                      </div>
                    </div>
                    <div className="rmo-toggleGrid">
                      <button
                        type="button"
                        className={`rmo-toggleCard${
                          strategyDraft?.allow_min_stay_relaxation ? " isActive" : ""
                        }`}
                        aria-pressed={strategyDraft?.allow_min_stay_relaxation ? "true" : "false"}
                        onClick={() =>
                          updateStrategyField(
                            "allow_min_stay_relaxation",
                            !strategyDraft?.allow_min_stay_relaxation
                          )
                        }
                      >
                        <div className="rmo-toggleCardHead">
                          <div className="rmo-toggleCardTitle">{t("Allow shorter minimum stays")}</div>
                          <span
                            className={`rmo-pill ${
                              strategyDraft?.allow_min_stay_relaxation
                                ? "rmo-pill--success"
                                : "rmo-pill--soft"
                            }`}
                          >
                            {t(strategyDraft?.allow_min_stay_relaxation ? "Allowed" : "Locked")}
                          </span>
                        </div>
                        <div className="rmo-choiceCardText">
                          {t(
                            "Turn this on if the RMS may reduce minimum stay to help fill softer dates."
                          )}
                        </div>
                      </button>

                      <button
                        type="button"
                        className={`rmo-toggleCard${
                          strategyDraft?.allow_min_stay_hardening ? " isActive" : ""
                        }`}
                        aria-pressed={strategyDraft?.allow_min_stay_hardening ? "true" : "false"}
                        onClick={() =>
                          updateStrategyField(
                            "allow_min_stay_hardening",
                            !strategyDraft?.allow_min_stay_hardening
                          )
                        }
                      >
                        <div className="rmo-toggleCardHead">
                          <div className="rmo-toggleCardTitle">{t("Allow longer minimum stays")}</div>
                          <span
                            className={`rmo-pill ${
                              strategyDraft?.allow_min_stay_hardening
                                ? "rmo-pill--success"
                                : "rmo-pill--soft"
                            }`}
                          >
                            {t(strategyDraft?.allow_min_stay_hardening ? "Allowed" : "Locked")}
                          </span>
                        </div>
                        <div className="rmo-choiceCardText">
                          {t(
                            "Turn this on if the RMS may increase minimum stay on stronger dates."
                          )}
                        </div>
                      </button>
                    </div>
                    <div className="rmo-formGrid">
                      <div className="rmo-formField">
                        <label htmlFor="strategy-min-stay-min">{t("Shortest minimum stay allowed")}</label>
                        <div className="rmo-inputRow">
                          <input
                            id="strategy-min-stay-min"
                            className="rmo-input rmo-inputGrow"
                            type="number"
                            inputMode="numeric"
                            min="1"
                            step="1"
                            placeholder="1"
                            value={strategyDraft?.min_stay_min_allowed ?? ""}
                            onChange={(event) =>
                              updateStrategyField("min_stay_min_allowed", event.target.value)
                            }
                          />
                          <span className="rmo-inputSuffix">{t("nights")}</span>
                        </div>
                        <div className="rmo-formHelp">
                          {t(
                            "Lowest minimum stay the RMS may use. Example: 1 means one-night stays are allowed if the rule says so."
                          )}
                        </div>
                      </div>

                      <div className="rmo-formField">
                        <label htmlFor="strategy-min-stay-max">{t("Longest minimum stay allowed")}</label>
                        <div className="rmo-inputRow">
                          <input
                            id="strategy-min-stay-max"
                            className="rmo-input rmo-inputGrow"
                            type="number"
                            inputMode="numeric"
                            min="1"
                            step="1"
                            placeholder="7"
                            value={strategyDraft?.min_stay_max_allowed ?? ""}
                            onChange={(event) =>
                              updateStrategyField("min_stay_max_allowed", event.target.value)
                            }
                          />
                          <span className="rmo-inputSuffix">{t("nights")}</span>
                        </div>
                        <div className="rmo-formHelp">
                          {t(
                            "Highest minimum stay the RMS may use. This must be equal to or above the shortest minimum stay."
                          )}
                        </div>
                      </div>
                    </div>
                  </section>

                  <section className="rmo-strategySection">
                    <div className="rmo-strategySectionHead">
                      <h4 className="rmo-strategySectionTitle">
                        {t("5. How far ahead should the RMS guide prices?")}
                      </h4>
                      <div className="rmo-strategySectionHelp">
                        {t(
                          "First choose how many days get one exact recommendation per day. After that, the RMS groups future dates into longer periods and gives one guidance level for each period."
                        )}
                      </div>
                    </div>
                    <div className="rmo-formGrid">
                      <div className="rmo-formField">
                        <label htmlFor="strategy-detailed-horizon">{t("Daily recommendation horizon")}</label>
                        <div className="rmo-inputRow">
                          <input
                            id="strategy-detailed-horizon"
                            className="rmo-input rmo-inputGrow"
                            type="number"
                            inputMode="numeric"
                            min="1"
                            step="1"
                            placeholder="90"
                            value={strategyDraft?.detailed_horizon_days ?? ""}
                            onChange={(event) =>
                              updateStrategyField("detailed_horizon_days", event.target.value)
                            }
                          />
                          <span className="rmo-inputSuffix">{t("days")}</span>
                        </div>
                        <div className="rmo-formHelp">
                          {t(
                            "How many days ahead the RMS should recommend one exact price per day."
                          )}
                        </div>
                      </div>

                      <div className="rmo-formField">
                        <label htmlFor="strategy-long-range-block-days">{t("Shortest long-range period")}</label>
                        <div className="rmo-inputRow">
                          <input
                            id="strategy-long-range-block-days"
                            className="rmo-input rmo-inputGrow"
                            type="number"
                            inputMode="numeric"
                            min="1"
                            step="1"
                            placeholder="7"
                            value={strategyDraft?.long_range_min_block_days ?? ""}
                            onChange={(event) =>
                              updateStrategyField("long_range_min_block_days", event.target.value)
                            }
                          />
                          <span className="rmo-inputSuffix">{t("days")}</span>
                        </div>
                        <div className="rmo-formHelp">
                          {t(
                            "This is the shortest future period the RMS may group together after the daily horizon ends. Example: 7 means it can build weekly periods, but not tiny 2- or 3-day periods."
                          )}
                        </div>
                      </div>
                    </div>

                    <div className="rmo-choiceGrid">
                      {LONG_RANGE_MODE_OPTIONS.map((option) => {
                        const isActive = strategyDraft?.long_range_mode === option.value;
                        return (
                          <button
                            key={option.value}
                            type="button"
                            className={`rmo-choiceCard${isActive ? " isActive" : ""}`}
                            onClick={() =>
                              setStrategyDraft((previous) => ({
                                ...(previous || normalizeStrategyDraft(strategyResource?.template || {})),
                                long_range_mode: option.value,
                                long_range_block_type:
                                  option.value === "season_blocks"
                                    ? "seasonality_period"
                                    : "calendar_range",
                              }))
                            }
                          >
                            <div className="rmo-choiceCardTitle">{t(option.label)}</div>
                            <div className="rmo-choiceCardText">{t(option.help)}</div>
                          </button>
                        );
                      })}
                    </div>

                    <div className="rmo-banner rmo-banner--tight">
                      <div className="rmo-bannerText">
                        {t("Long-range blocks are currently set to {label}.", {
                          label: t(longRangeBlockTypeMeta.label),
                        })}{" "}
                        {t(longRangeModeMeta.help)}
                      </div>
                    </div>
                  </section>

                  <section className="rmo-strategySection">
                    <div className="rmo-strategySectionHead">
                      <h4 className="rmo-strategySectionTitle">
                        {t("6. Should this strategy be used now?")}
                      </h4>
                      <div className="rmo-strategySectionHelp">
                        {t(
                          "You can save a rule without activating it yet if you are still preparing your setup."
                        )}
                      </div>
                    </div>
                    <div className="rmo-toggleGrid rmo-toggleGrid--single">
                      <button
                        type="button"
                        className={`rmo-toggleCard${strategyDraft?.is_active ? " isActive" : ""}`}
                        aria-pressed={strategyDraft?.is_active ? "true" : "false"}
                        onClick={() => updateStrategyField("is_active", !strategyDraft?.is_active)}
                      >
                        <div className="rmo-toggleCardHead">
                          <div className="rmo-toggleCardTitle">{t("Use this strategy now")}</div>
                          <span
                            className={`rmo-pill ${
                              strategyDraft?.is_active ? "rmo-pill--success" : "rmo-pill--warn"
                            }`}
                          >
                            {t(strategyDraft?.is_active ? "Active" : "Inactive")}
                          </span>
                        </div>
                        <div className="rmo-choiceCardText">
                          {t(
                            "Active strategies are used by the RMS. Inactive ones are saved for later and do not affect recommendations."
                          )}
                        </div>
                      </button>
                    </div>
                  </section>
                </div>
              ) : (
                <div className="rmo-formField">
                  <label htmlFor="rms-json-editor">{t("Configuration data")}</label>
                  <div className="rmo-formHelp" style={{ marginBottom: 10 }}>
                    This editor is intended for advanced configuration changes.
                  </div>
                  <textarea
                    id="rms-json-editor"
                    className="rmo-textarea"
                    style={{ minHeight: 420 }}
                    value={editorText}
                    onChange={(event) => setEditorText(event.target.value)}
                  />
                </div>
              )}
            </div>
            <div className="rmo-modalFoot">
              <button type="button" className="rmo-btn" onClick={closeEditor} disabled={saving}>
                {t("Cancel")}
              </button>
              <button type="button" className="rmo-btn rmo-btn--accent" onClick={saveEditor} disabled={saving}>
                {saving
                  ? t("Saving...")
                  : isStrategyEditor
                    ? editor.item
                      ? t("Save strategy")
                      : t("Create strategy")
                    : editor.item
                      ? t("Save changes")
                      : t("Create row")}
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}

function stripAssociations(item) {
  const nextItem = { ...item };
  delete nextItem.id;
  Object.keys(nextItem).forEach((key) => {
    if (key.endsWith("_at")) return;
    if (typeof nextItem[key] === "object" && nextItem[key] !== null && !Array.isArray(nextItem[key])) {
      delete nextItem[key];
    }
  });
  return nextItem;
}
